<template>
  <div class="pt-shell">
    <!-- TOP BAR -->
    <div class="pt-topbar">
      <div class="pt-topbar-inner">
        <div class="pt-brand-sm"><i class="mdi mdi-hospital-building"></i> Patient Portal</div>
        <div class="d-flex align-items-center gap-3">
          <!-- Profile switcher -->
          <div class="pt-profile-picker" @click="profileMenu = !profileMenu" v-if="profiles.length > 1 || activeProfile.relationship !== 'self'">
            <div class="pt-profile-current">
              <div class="pt-avatar" :class="profileColor(activeProfile)">{{ profileInitial(activeProfile) }}</div>
              <div class="pt-profile-info">
                <div class="pt-profile-name">{{ activeProfile.first_name }} {{ activeProfile.last_name }}</div>
                <div class="pt-profile-rel">{{ formatRel(activeProfile.relationship) }}</div>
              </div>
              <i class="mdi mdi-menu-down"></i>
            </div>
            <div v-if="profileMenu" class="pt-profile-menu">
              <div v-for="p in profiles" :key="p.patient_id" class="pt-profile-row" :class="{ 'pt-profile-active': p.patient_id == activeProfile.patient_id }" @click.stop="switchProfile(p)">
                <div class="pt-avatar" :class="profileColor(p)">{{ profileInitial(p) }}</div>
                <div>
                  <div class="pt-profile-name">{{ p.first_name }} {{ p.last_name }}</div>
                  <div class="pt-profile-rel">{{ formatRel(p.relationship) }} · {{ p.age }}y</div>
                </div>
                <i v-if="p.patient_id == activeProfile.patient_id" class="mdi mdi-check pt-profile-check"></i>
              </div>
              <div class="pt-profile-divider"></div>
              <div class="pt-profile-row pt-profile-add" @click.stop="openAddFamily">
                <i class="mdi mdi-account-plus"></i>
                <span>Add family member</span>
              </div>
            </div>
          </div>

          <div v-else class="pt-user">
            <i class="mdi mdi-account-circle"></i>
            {{ patient.name || '...' }}<span class="pt-muted-sm ms-2">{{ patient.patient_number }}</span>
          </div>
          <button class="pt-icon-btn" @click="logout" title="Sign out"><i class="mdi mdi-logout"></i></button>
        </div>
      </div>

      <!-- Proxy banner -->
      <div v-if="activeProfile.relationship !== 'self'" class="pt-proxy-banner">
        <i class="mdi mdi-account-switch"></i>
        Viewing <strong>{{ activeProfile.first_name }}'s</strong> records ({{ formatRel(activeProfile.relationship) }}). Actions taken here are logged.
        <button class="pt-btn pt-btn-link pt-btn-sm" @click="switchToSelf">← Switch to my profile</button>
      </div>
      <div class="pt-tabbar">
        <button class="pt-tab" :class="{ active: tab==='home' }" @click="tab='home'"><i class="mdi mdi-home"></i> Home</button>
        <button class="pt-tab" :class="{ active: tab==='messages' }" @click="goTab('messages')">
          <i class="mdi mdi-message-text"></i> Messages
          <span v-if="unreadMessages > 0" class="pt-badge">{{ unreadMessages }}</span>
        </button>
        <button class="pt-tab" :class="{ active: tab==='symptom' }" @click="goTab('symptom')"><i class="mdi mdi-robot-happy"></i> Symptom Check</button>
        <button class="pt-tab" :class="{ active: tab==='diary' }" @click="goTab('diary')"><i class="mdi mdi-notebook-heart"></i> Health Diary</button>
        <button class="pt-tab" :class="{ active: tab==='emergency' }" @click="goTab('emergency')"><i class="mdi mdi-medical-bag"></i> Emergency Card</button>
        <button class="pt-tab" :class="{ active: tab==='meds' }" @click="goTab('meds')"><i class="mdi mdi-pill-multiple"></i> My Meds</button>
        <button class="pt-tab" :class="{ active: tab==='wearables' }" @click="goTab('wearables')"><i class="mdi mdi-watch-variant"></i> Wearables</button>
        <button class="pt-tab" :class="{ active: tab==='docs' }" @click="goTab('docs')"><i class="mdi mdi-folder-multiple"></i> Documents</button>
        <button v-if="preg.IsPregnant" class="pt-tab" :class="{ active: tab==='pregnancy' }" @click="goTab('pregnancy')"><i class="mdi mdi-human-pregnant"></i> Pregnancy</button>
        <button v-if="growth.IsPaediatric" class="pt-tab" :class="{ active: tab==='growth' }" @click="goTab('growth')"><i class="mdi mdi-chart-bell-curve"></i> Growth</button>
        <button class="pt-tab" :class="{ active: tab==='fp' }" @click="goTab('fp')"><i class="mdi mdi-pill"></i> Family Planning</button>
        <button class="pt-tab" :class="{ active: tab==='wellbeing' }" @click="goTab('wellbeing')"><i class="mdi mdi-heart-pulse"></i> Wellbeing</button>
        <button class="pt-tab" :class="{ active: tab==='booklet' }" @click="goTab('booklet')"><i class="mdi mdi-book-open-page-variant"></i> MCH Booklet</button>
        <button class="pt-tab" :class="{ active: tab==='ask' }" @click="goTab('ask')"><i class="mdi mdi-chat-question"></i> Ask my record</button>
        <button class="pt-tab" :class="{ active: tab==='appointments' }" @click="goTab('appointments')"><i class="mdi mdi-calendar"></i> Appointments</button>
        <button class="pt-tab" :class="{ active: tab==='results' }" @click="goTab('results')"><i class="mdi mdi-flask"></i> Results</button>
        <button class="pt-tab" :class="{ active: tab==='prescriptions' }" @click="goTab('prescriptions')"><i class="mdi mdi-pill"></i> Prescriptions</button>
        <button class="pt-tab" :class="{ active: tab==='immunizations' }" @click="goTab('immunizations')"><i class="mdi mdi-needle"></i> Immunizations</button>
        <button class="pt-tab" :class="{ active: tab==='telehealth' }" @click="goTab('telehealth')"><i class="mdi mdi-video"></i> Telehealth</button>
        <button class="pt-tab" :class="{ active: tab==='referrals' }" @click="goTab('referrals')"><i class="mdi mdi-share-variant"></i> Referrals</button>
        <button class="pt-tab" :class="{ active: tab==='bills' }" @click="goTab('bills')"><i class="mdi mdi-cash"></i> Bills</button>
        <button class="pt-tab" :class="{ active: tab==='visits' }" @click="goTab('visits')"><i class="mdi mdi-clipboard-text"></i> Visits</button>
        <button class="pt-tab" :class="{ active: tab==='consents' }" @click="goTab('consents')"><i class="mdi mdi-file-sign"></i> Consents</button>
        <button class="pt-tab" :class="{ active: tab==='education' }" @click="goTab('education')"><i class="mdi mdi-school"></i> Health info</button>
        <button class="pt-tab" :class="{ active: tab==='profile' }" @click="goTab('profile')"><i class="mdi mdi-cog"></i> Profile</button>
      </div>
    </div>

    <div class="pt-body">
      <div class="pt-container">
        <!-- ===== HOME ===== -->
        <div v-if="tab==='home'">
          <h2 class="pt-h2">Hello, {{ firstName }}.</h2>
          <p class="pt-muted">Here's a quick view of your care.</p>

          <!-- Adolescent content stream (auto-shown for under-25s with adolescent flag) -->
          <div v-if="adolescent.IsAdolescent && adolescent.Cards?.length" class="pt-teen-strip">
            <h3 class="pt-h3"><i class="mdi mdi-account-school"></i> For you ({{ adolescent.Age }} years old)</h3>
            <div class="pt-teen-cards">
              <div v-for="(c, i) in adolescent.Cards" :key="i" class="pt-teen-card" :style="{ borderLeftColor: c.color }">
                <i :class="'mdi ' + c.icon" :style="{ color: c.color }"></i>
                <div>
                  <strong>{{ c.title }}</strong>
                  <p class="pt-muted-sm">{{ c.body }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="pt-grid">
            <div class="pt-card-sm" @click="goTab('appointments')">
              <div class="pt-num">{{ home.UpcomingAppointments?.length || 0 }}</div>
              <div class="pt-lbl">Upcoming appointments</div>
              <i class="mdi mdi-calendar pt-icon"></i>
            </div>
            <div class="pt-card-sm" @click="goTab('results')">
              <div class="pt-num">{{ home.RecentResults?.length || 0 }}</div>
              <div class="pt-lbl">Recent results</div>
              <i class="mdi mdi-flask pt-icon"></i>
            </div>
            <div class="pt-card-sm" @click="goTab('prescriptions')">
              <div class="pt-num">{{ home.ActivePrescriptions?.length || 0 }}</div>
              <div class="pt-lbl">Recent prescriptions</div>
              <i class="mdi mdi-pill pt-icon"></i>
            </div>
            <div class="pt-card-sm" @click="goTab('telehealth')">
              <div class="pt-num">{{ home.UpcomingTelehealth?.length || 0 }}</div>
              <div class="pt-lbl">Telehealth sessions</div>
              <i class="mdi mdi-video pt-icon"></i>
            </div>
            <div class="pt-card-sm" @click="goTab('immunizations')">
              <div class="pt-num">{{ home.ImmunizationsGiven || 0 }}</div>
              <div class="pt-lbl">Immunizations on record</div>
              <i class="mdi mdi-needle pt-icon"></i>
            </div>
            <div class="pt-card-sm" @click="goTab('bills')">
              <div class="pt-num">KES {{ formatMoney(home.TotalBalance) }}</div>
              <div class="pt-lbl">Outstanding balance</div>
              <i class="mdi mdi-cash pt-icon"></i>
            </div>
          </div>

          <div v-if="home.UpcomingAppointments?.length" class="pt-section">
            <h3 class="pt-h3">Next visits</h3>
            <div v-for="a in home.UpcomingAppointments" :key="a.appointment_id" class="pt-row">
              <div>
                <strong>{{ formatDate(a.appointment_date) }}</strong> {{ a.appointment_time || '' }}
                <div class="pt-muted-sm">{{ a.department || '' }} {{ a.doctor_name ? '· Dr. ' + a.doctor_name : '' }}</div>
              </div>
              <span class="pt-pill" :class="apptPill(a.status)">{{ a.status }}</span>
            </div>
          </div>

          <div v-if="home.UpcomingTelehealth?.length" class="pt-section">
            <h3 class="pt-h3">Telehealth</h3>
            <div v-for="t in home.UpcomingTelehealth" :key="t.session_id" class="pt-row">
              <div>
                <strong>{{ formatDt(t.scheduled_start) }}</strong> · {{ t.specialty || t.mode }}
                <div class="pt-muted-sm">Join code: <code>{{ t.join_code }}</code></div>
              </div>
              <span class="pt-pill pt-pill-info">{{ t.status }}</span>
            </div>
          </div>
        </div>

        <!-- ===== MESSAGES ===== -->
        <div v-if="tab==='messages'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 class="pt-h2">Messages</h2>
              <p class="pt-muted mb-0">Talk to your doctor, nurse, reception, billing or pharmacy. Replies typically within 24 hours.</p>
            </div>
            <button class="pt-btn pt-btn-primary" @click="newMsgModal=true"><i class="mdi mdi-pencil"></i> New message</button>
          </div>

          <!-- thread list (left) + thread detail (right) -->
          <div class="pt-msg-shell" v-if="!activeThread">
            <div v-for="t in threads" :key="t.thread_id" class="pt-thread-row" :class="{ 'pt-thread-unread': t.unread_for_patient > 0 }" @click="openThread(t)">
              <div class="pt-thread-target">
                <i class="mdi" :class="roleIcon(t.target_role)"></i>
                <span>{{ formatRole(t.target_role) }}</span>
              </div>
              <div class="pt-thread-mid">
                <div class="pt-thread-subj">{{ t.subject }}</div>
                <div class="pt-muted-sm">{{ formatDt(t.last_msg_at) }} · {{ t.last_msg_by === 'patient' ? 'You' : (t.last_msg_by === 'staff' ? 'Hospital' : 'System') }}</div>
              </div>
              <div class="pt-thread-end">
                <span class="pt-pill" :class="threadStatusPill(t.status)">{{ t.status }}</span>
                <span v-if="t.unread_for_patient > 0" class="pt-badge ms-1">{{ t.unread_for_patient }}</span>
              </div>
            </div>
            <div v-if="!threads.length" class="pt-empty">No conversations yet. Click "New message" to start one.</div>
          </div>

          <!-- thread detail view -->
          <div class="pt-thread-detail" v-if="activeThread">
            <div class="pt-thread-header">
              <button class="pt-btn pt-btn-link" @click="activeThread=null; loadThreads()"><i class="mdi mdi-arrow-left"></i> Back</button>
              <div>
                <strong>{{ activeThread.subject }}</strong>
                <div class="pt-muted-sm">
                  <i class="mdi" :class="roleIcon(activeThread.target_role)"></i> {{ formatRole(activeThread.target_role) }}
                  · <span class="pt-pill" :class="threadStatusPill(activeThread.status)">{{ activeThread.status }}</span>
                  <span v-if="activeThread.priority !== 'normal'" class="pt-pill ms-1" :class="priorityPill(activeThread.priority)">{{ activeThread.priority }}</span>
                </div>
              </div>
              <button v-if="activeThread.status !== 'closed'" class="pt-btn pt-btn-link" @click="closeThread()"><i class="mdi mdi-close-circle"></i> Close</button>
            </div>

            <div class="pt-msg-list" ref="msgList">
              <div v-for="m in activeMessages" :key="m.message_id" class="pt-msg" :class="'pt-msg-' + m.sender_type">
                <div class="pt-msg-meta"><strong>{{ m.sender_type === 'patient' ? 'You' : (m.sender_name || 'Hospital') }}</strong> · {{ formatDt(m.sent_at) }}</div>
                <div class="pt-msg-body">{{ m.body }}</div>
              </div>
              <div v-if="!activeMessages.length" class="pt-empty">No messages.</div>
            </div>

            <div v-if="activeThread.status !== 'closed'" class="pt-msg-input">
              <textarea v-model="replyBody" class="pt-input" rows="3" placeholder="Type your message…" @keyup.enter.ctrl="sendReply"></textarea>
              <button class="pt-btn pt-btn-primary mt-2" :disabled="!replyBody.trim() || sending" @click="sendReply">{{ sending ? 'Sending…' : 'Send (Ctrl+Enter)' }}</button>
            </div>
            <div v-else class="pt-empty">This conversation is closed. Start a new message to continue.</div>
          </div>
        </div>

        <!-- ===== SYMPTOM CHECKER ===== -->
        <div v-if="tab==='symptom'">
          <div v-if="!sym.active && !sym.assessment">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h2 class="pt-h2"><i class="mdi mdi-robot-happy"></i> AI Symptom Check</h2>
                <p class="pt-muted mb-0">Describe what you're feeling. The assistant will ask a few questions and recommend whether you need urgent care, an appointment, or self-care.</p>
              </div>
              <button class="pt-btn pt-btn-primary" @click="symStart"><i class="mdi mdi-message-plus"></i> New check</button>
            </div>

            <div class="pt-sym-disclaimer">
              <i class="mdi mdi-alert-circle"></i>
              <div>
                <strong>This is guidance only.</strong> For chest pain, breathing difficulty, severe bleeding, suicidal thoughts, or anything life-threatening — call <strong>1199</strong> (MOH) / hospital emergency line immediately. Do not wait for the AI.
              </div>
            </div>

            <h4 class="pt-h3 mt-4">Past checks</h4>
            <div v-for="a in sym.history" :key="a.assessment_id" class="pt-sym-history-row" @click="symOpen(a)">
              <div>
                <strong>{{ a.chief_complaint || 'Symptom check' }}</strong>
                <div class="pt-muted-sm">{{ formatDt(a.started_at) }}</div>
              </div>
              <div class="text-end">
                <span v-if="a.urgency" class="pt-pill" :class="urgencyPill(a.urgency)">{{ formatUrgency(a.urgency) }}</span>
                <span v-else class="pt-muted-sm">in progress</span>
              </div>
            </div>
            <div v-if="!sym.history.length" class="pt-empty">No previous checks. Click "New check" to start.</div>
          </div>

          <!-- ACTIVE CHAT -->
          <div v-if="sym.active" class="pt-sym-chat">
            <div class="pt-thread-header">
              <button class="pt-btn pt-btn-link" @click="symBackToList()"><i class="mdi mdi-arrow-left"></i> Back</button>
              <div><strong><i class="mdi mdi-robot-happy"></i> Symptom assistant</strong><div class="pt-muted-sm">{{ sym.assessmentId ? 'Conversation #' + sym.assessmentId : 'New conversation' }}</div></div>
              <span></span>
            </div>

            <div class="pt-msg-list" ref="symMsgList">
              <div v-for="(m, idx) in sym.messages" :key="idx" class="pt-msg" :class="m.role === 'user' ? 'pt-msg-patient' : 'pt-msg-staff'">
                <div class="pt-msg-meta">{{ m.role === 'user' ? 'You' : 'AI Assistant' }}</div>
                <div class="pt-msg-body">{{ m.content }}</div>
              </div>
              <div v-if="sym.thinking" class="pt-msg pt-msg-staff">
                <div class="pt-msg-meta">AI Assistant</div>
                <div class="pt-msg-body"><span class="pt-typing"><span></span><span></span><span></span></span></div>
              </div>

              <!-- Final assessment card -->
              <div v-if="sym.assessment" class="pt-sym-result" :class="urgencyClass(sym.assessment.urgency)">
                <div class="pt-sym-result-head">
                  <i class="mdi" :class="urgencyIcon(sym.assessment.urgency)"></i>
                  <div>
                    <h3 class="m-0">{{ formatUrgency(sym.assessment.urgency) }}</h3>
                    <div class="pt-muted-sm">{{ sym.assessment.chief_complaint }}</div>
                  </div>
                </div>
                <div class="pt-sym-summary">{{ sym.assessment.summary }}</div>

                <div v-if="sym.assessment.likely_causes && sym.assessment.likely_causes.length" class="pt-sym-block">
                  <h5>Likely causes</h5>
                  <ul><li v-for="(c, i) in sym.assessment.likely_causes" :key="i">{{ c }}</li></ul>
                </div>

                <div v-if="sym.assessment.red_flags && sym.assessment.red_flags.length" class="pt-sym-block pt-sym-redflags">
                  <h5><i class="mdi mdi-flag"></i> Watch for these — seek care immediately if any happen</h5>
                  <ul><li v-for="(c, i) in sym.assessment.red_flags" :key="i">{{ c }}</li></ul>
                </div>

                <div v-if="sym.assessment.self_care_advice" class="pt-sym-block">
                  <h5>Self-care advice</h5>
                  <p>{{ sym.assessment.self_care_advice }}</p>
                </div>

                <div class="pt-sym-actions">
                  <button v-if="sym.assessment.urgency !== 'self_care'" class="pt-btn pt-btn-primary" @click="symBookAppt"><i class="mdi mdi-calendar-plus"></i> Book appointment</button>
                  <button class="pt-btn pt-btn-secondary" @click="symSendToDoctor"><i class="mdi mdi-message-text"></i> Send to my doctor</button>
                  <button v-if="sym.assessment.urgency === 'emergency'" class="pt-btn pt-btn-danger" onclick="window.location.href='tel:1199'"><i class="mdi mdi-phone"></i> Call emergency 1199</button>
                </div>
              </div>
            </div>

            <div v-if="!sym.assessment" class="pt-msg-input">
              <textarea v-model="sym.input" class="pt-input" rows="2" placeholder="Describe your symptoms…" @keyup.enter.ctrl="symSend"></textarea>
              <button class="pt-btn pt-btn-primary mt-2" :disabled="!sym.input.trim() || sym.thinking" @click="symSend">{{ sym.thinking ? 'Thinking…' : 'Send' }}</button>
            </div>
            <div v-else class="pt-msg-input">
              <button class="pt-btn pt-btn-link" @click="symBackToList()">← Back to history</button>
            </div>
          </div>
        </div>

        <!-- ===== ASK MY RECORD ===== -->
        <div v-if="tab==='ask'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 class="pt-h2"><i class="mdi mdi-chat-question"></i> Ask about your record</h2>
              <p class="pt-muted mb-0">Ask a question in plain English about your visits, results, medications, or appointments. AI answers only from your own record.</p>
            </div>
            <button class="pt-btn pt-btn-link" @click="askNewSession" v-if="askSession">
              <i class="mdi mdi-restart"></i> New conversation
            </button>
          </div>

          <!-- Suggested prompts (shown only on empty state) -->
          <div v-if="!askMessages.length" class="pt-ask-suggestions">
            <h4 class="pt-h3">Try asking:</h4>
            <div class="pt-ask-chips">
              <button v-for="(s, i) in suggestedAsks" :key="i" class="pt-ask-chip" @click="askInput = s; sendAsk()">
                <i class="mdi mdi-lightbulb-outline"></i> {{ s }}
              </button>
            </div>
          </div>

          <!-- Chat -->
          <div class="pt-ask-chat" v-if="askMessages.length">
            <div ref="askMsgList" class="pt-ask-list">
              <div v-for="(m, i) in askMessages" :key="i" class="pt-msg" :class="m.role === 'user' ? 'pt-msg-patient' : 'pt-msg-staff'">
                <div class="pt-msg-meta">{{ m.role === 'user' ? 'You' : 'AI assistant' }} <span v-if="m.ms" class="pt-muted-sm">· {{ m.ms }}ms</span></div>
                <div class="pt-msg-body">{{ m.content }}</div>
              </div>
              <div v-if="askThinking" class="pt-msg pt-msg-staff">
                <div class="pt-msg-meta">AI assistant</div>
                <div class="pt-msg-body"><span class="pt-typing"><span></span><span></span><span></span></span> reading your record…</div>
              </div>
            </div>
          </div>

          <div class="pt-ask-input">
            <textarea v-model="askInput" class="pt-input" rows="2" placeholder="e.g. What was my last Hb? When is my next appointment? What medications am I on?" @keyup.enter.ctrl="sendAsk"></textarea>
            <button class="pt-btn pt-btn-primary mt-2" :disabled="!askInput.trim() || askThinking" @click="sendAsk">
              <i class="mdi mdi-send"></i> {{ askThinking ? 'Thinking…' : 'Ask' }}
            </button>
          </div>

          <p class="pt-muted-sm mt-2">
            <i class="mdi mdi-shield-lock"></i>
            AI only sees your own record. It can't give medical advice — for interpretation, ask your clinician via Messages.
          </p>
        </div>

        <!-- ===== GROWTH CHARTS (paeds) ===== -->
        <div v-if="tab==='growth' && growth.IsPaediatric">
          <h2 class="pt-h2"><i class="mdi mdi-chart-bell-curve"></i> Growth Charts</h2>
          <p class="pt-muted">{{ growth.Patient.name }} · {{ growth.Patient.sex }} · {{ growth.Patient.age_label }} old · DOB {{ formatDate(growth.Patient.dob) }}</p>

          <!-- Latest summary -->
          <div class="pt-growth-stats">
            <div class="pt-growth-stat" :class="growthStatusClass(growth.Latest.weight_status)">
              <div class="pt-stat-num">{{ growth.Latest.weight ? growth.Latest.weight + ' kg' : '—' }}</div>
              <div class="pt-stat-lbl">Weight · {{ growth.Latest.weight_status || '—' }}</div>
            </div>
            <div class="pt-growth-stat" :class="growthStatusClass(growth.Latest.height_status)">
              <div class="pt-stat-num">{{ growth.Latest.height ? growth.Latest.height + ' cm' : '—' }}</div>
              <div class="pt-stat-lbl">Height · {{ growth.Latest.height_status || '—' }}</div>
            </div>
            <div class="pt-growth-stat">
              <div class="pt-stat-num">{{ growth.Latest.bmi || '—' }}</div>
              <div class="pt-stat-lbl">BMI</div>
            </div>
          </div>

          <!-- Charts -->
          <GrowthChart title="Weight-for-age" unit="kg" :measurements="growth.Measurements.weight" :reference="growth.Reference.weight" :age-months="growth.Patient.age_months" color="#2563eb" />
          <GrowthChart title="Height-for-age" unit="cm" :measurements="growth.Measurements.height" :reference="growth.Reference.height" :age-months="growth.Patient.age_months" color="#16a34a" />
          <GrowthChart v-if="growth.Patient.age_months >= 60" title="BMI-for-age" unit="kg/m²" :measurements="growth.Measurements.bmi" :reference="growth.Reference.bmi" :age-months="growth.Patient.age_months" color="#7c3aed" />

          <div class="pt-muted-sm mt-2">
            <i class="mdi mdi-information-outline"></i>
            Curves show the WHO reference 3rd, 50th and 97th percentiles. Falling outside this range may need clinical review — your paediatrician will assess at the next visit.
          </div>

          <!-- HEI (HIV-exposed infant) tracking -->
          <div v-if="hei.IsHei" class="pt-hei-card mt-3">
            <h3 class="pt-h3"><i class="mdi mdi-shield-account"></i> Baby's HIV testing (HEI follow-up)</h3>
            <p class="pt-muted-sm">Because mum is HIV+, your baby is tested 4 times on a schedule to confirm they are HIV-free.</p>
            <div class="pt-hei-row">
              <div><strong>Feeding plan:</strong> {{ hei.Hei.feeding_option ? hei.Hei.feeding_option.replace('_',' ') : '—' }}</div>
              <div><strong>Risk:</strong>
                <span class="pt-pill" :class="hei.Hei.risk_classification === 'high_risk' ? 'pt-pill-warn' : 'pt-pill-success'">{{ hei.Hei.risk_classification.replace('_',' ') }}</span>
              </div>
              <div><strong>ARV regimen:</strong> {{ hei.Hei.arv_regimen || '—' }}</div>
              <div><strong>Final status:</strong>
                <span class="pt-pill" :class="hei.Hei.final_status === 'final_negative' ? 'pt-pill-success' : (hei.Hei.final_status === 'positive' ? 'pt-pill-bad' : 'pt-pill-info')">{{ hei.Hei.final_status.replace('_',' ') }}</span>
              </div>
            </div>
            <h4 class="pt-h4 mt-2">Test schedule</h4>
            <div v-for="t in hei.Tests" :key="t.dbs_id" class="pt-pnc-row" :class="t.result === 'positive' ? 'pt-pnc-row-bad' : (t.is_overdue ? 'pt-pnc-row-warn' : '')">
              <div class="pt-pnc-icon">
                <i v-if="t.result === 'negative'" class="mdi mdi-check-circle pt-good"></i>
                <i v-else-if="t.result === 'positive'" class="mdi mdi-alert-circle pt-bad"></i>
                <i v-else-if="t.is_overdue" class="mdi mdi-clock-alert pt-bad"></i>
                <i v-else-if="t.is_due_soon" class="mdi mdi-clock-outline pt-warn"></i>
                <i v-else class="mdi mdi-circle-outline pt-muted"></i>
              </div>
              <div class="pt-pnc-body">
                <strong>{{ t.test_label }}</strong>
                <div class="pt-muted-sm">
                  <span v-if="t.result_date">Result {{ formatDate(t.result_date) }}: <strong>{{ t.result_label }}</strong></span>
                  <span v-else>Scheduled {{ formatDate(t.scheduled_date) }} — {{ t.result_label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== FAMILY PLANNING ===== -->
        <div v-if="tab==='fp'">
          <h2 class="pt-h2"><i class="mdi mdi-pill"></i> Family Planning — choose what fits you</h2>
          <p class="pt-muted">No two methods suit every woman. This is a personal choice — your provider will help you decide. Spacing pregnancies by at least 2 years is the safest for mother and baby.</p>

          <div v-if="fp.IsBreastfeeding" class="pt-fp-context">
            <i class="mdi mdi-baby-face-outline"></i> You delivered <strong>{{ fp.MonthsSinceDelivery }} month(s) ago</strong> and are within the breastfeeding window. We've marked methods that are <strong>safe during breastfeeding</strong>.
          </div>
          <div v-else-if="fp.ObstetricHistory" class="pt-fp-context">
            Your obstetric summary: <strong>{{ fp.ObstetricHistory }}</strong>. All methods shown — discuss your choice with your provider.
          </div>

          <div class="pt-fp-filter">
            <button class="pt-fp-chip" :class="{ active: fpFilter === 'recommended' }" @click="fpFilter='recommended'">⭐ Best for me</button>
            <button class="pt-fp-chip" :class="{ active: fpFilter === 'all' }" @click="fpFilter='all'">All methods</button>
            <button class="pt-fp-chip" :class="{ active: fpFilter === 'larc' }" @click="fpFilter='larc'">Long-acting (LARC)</button>
            <button class="pt-fp-chip" :class="{ active: fpFilter === 'permanent' }" @click="fpFilter='permanent'">Permanent</button>
          </div>

          <div class="pt-fp-grid">
            <div v-for="m in filteredFpMethods" :key="m.code" class="pt-fp-card" :class="m.highly_recommended ? 'pt-fp-recommended' : (m.not_for_you_now ? 'pt-fp-blocked' : '')">
              <div class="pt-fp-head">
                <i :class="'mdi ' + m.icon"></i>
                <div>
                  <h4 class="pt-fp-name">{{ m.name }}</h4>
                  <span class="pt-pill pt-pill-info">{{ m.category }}</span>
                  <span v-if="m.highly_recommended" class="pt-pill pt-pill-success">⭐ Recommended for you</span>
                  <span v-if="m.not_for_you_now" class="pt-pill pt-pill-bad">Not while breastfeeding</span>
                </div>
              </div>
              <div class="pt-fp-stats">
                <div><strong>Effectiveness</strong><br>{{ m.effectiveness }}</div>
                <div><strong>Lasts</strong><br>{{ m.duration }}</div>
                <div><strong>Breastfeeding</strong><br>{{ m.breastfeeding_safe ? 'Safe ✓' : 'Wait' }}</div>
              </div>
              <div class="pt-fp-text"><strong>✓ Pros:</strong> {{ m.pros }}</div>
              <div class="pt-fp-text"><strong>! Cons:</strong> {{ m.cons }}</div>
              <div class="pt-fp-best"><strong>Best for:</strong> {{ m.best_for }}</div>
            </div>
          </div>

          <div class="pt-muted-sm mt-3">
            <i class="mdi mdi-information-outline"></i> Effectiveness shown is for typical use. Want to start a method? Book an appointment under Appointments → New booking → reason "Family Planning".
          </div>
        </div>

        <!-- ===== WELLBEING (EPDS + IPV self-screen) ===== -->
        <div v-if="tab==='wellbeing'">
          <h2 class="pt-h2"><i class="mdi mdi-heart-pulse"></i> Your Wellbeing</h2>
          <p class="pt-muted">A private space to check on yourself. Nobody sees these answers except you and your clinical team.</p>

          <!-- EPDS card -->
          <div class="pt-wb-card">
            <div class="pt-wb-head">
              <i class="mdi mdi-brain"></i>
              <div>
                <h3 class="pt-h3 m-0">Mood check (EPDS)</h3>
                <p class="pt-muted-sm m-0">10 quick questions about how you've felt in the last 7 days. Takes 2 minutes.</p>
              </div>
              <button v-if="!epdsForm.active" class="pt-btn pt-btn-primary" @click="startEpds"><i class="mdi mdi-play"></i> Start</button>
            </div>

            <div v-if="epdsForm.active">
              <p class="pt-muted-sm">In the last 7 days, I have…</p>
              <div v-for="(q, i) in epdsQuestions" :key="i" class="pt-wb-q">
                <strong>{{ i+1 }}. {{ q.text }}</strong>
                <div class="pt-wb-options">
                  <label v-for="(opt, j) in q.options" :key="j" class="pt-wb-opt" :class="{ selected: epdsForm.answers[i+1] === j }">
                    <input type="radio" :value="j" :name="'q'+i" v-model.number="epdsForm.answers[i+1]" />
                    <span>{{ opt }}</span>
                  </label>
                </div>
              </div>
              <div class="pt-wb-actions">
                <button class="pt-btn pt-btn-secondary" @click="epdsForm.active=false">Cancel</button>
                <button class="pt-btn pt-btn-primary" :disabled="!epdsComplete || epdsSubmitting" @click="submitEpds">
                  <i class="mdi mdi-check"></i> {{ epdsSubmitting ? 'Submitting…' : 'Submit (' + epdsAnsweredCount + '/10)' }}
                </button>
              </div>
            </div>

            <div v-if="epdsResult" class="pt-wb-result" :class="'pt-wb-result-' + (epdsResult.classification || 'low')">
              <h4 class="pt-h3">Your result</h4>
              <div class="pt-wb-score">Score: <strong>{{ epdsResult.total_score }}/30</strong></div>
              <p>{{ epdsResult.message }}</p>
              <p v-if="epdsResult.alert_opened" class="pt-muted-sm">A clinician has been notified privately. You'll hear from them soon.</p>
              <button class="pt-btn pt-btn-link mt-2" @click="epdsResult=null">Close</button>
            </div>

            <div v-if="epdsHistory.length && !epdsForm.active && !epdsResult" class="pt-wb-history">
              <h4 class="pt-h4">Your past checks</h4>
              <div v-for="h in epdsHistory" :key="h.ess_id" class="pt-wb-history-row" :class="'pt-wb-row-' + h.classification">
                <span>{{ formatDate(h.screened_at) }}</span>
                <span><strong>{{ h.total_score }}</strong>/30 · {{ h.classification.replace(/_/g,' ') }}</span>
                <span v-if="h.q10_self_harm==1" class="pt-pill pt-pill-bad">Q10 flagged</span>
              </div>
            </div>
          </div>

          <!-- IPV self-screen -->
          <div class="pt-wb-card pt-wb-ipv">
            <div class="pt-wb-head">
              <i class="mdi mdi-shield-account"></i>
              <div>
                <h3 class="pt-h3 m-0">Are things okay at home?</h3>
                <p class="pt-muted-sm m-0">4 confidential questions about your relationship. Anyone can be hurt by someone they love. We're here for you.</p>
              </div>
              <button v-if="!ipvForm.active" class="pt-btn pt-btn-primary" @click="startIpv">Start</button>
            </div>

            <div v-if="ipvForm.active">
              <p class="pt-muted-sm">How often does your partner physically <strong>hurt</strong> you? (1 = never · 5 = frequently)</p>
              <div class="pt-wb-scale"><input type="range" v-model.number="ipvForm.h1_hurt" min="1" max="5" /> <strong>{{ ipvForm.h1_hurt }}</strong></div>
              <p class="pt-muted-sm">How often does your partner <strong>insult</strong> or talk down to you?</p>
              <div class="pt-wb-scale"><input type="range" v-model.number="ipvForm.h2_insult" min="1" max="5" /> <strong>{{ ipvForm.h2_insult }}</strong></div>
              <p class="pt-muted-sm">How often does your partner <strong>threaten</strong> you with harm?</p>
              <div class="pt-wb-scale"><input type="range" v-model.number="ipvForm.h3_threaten" min="1" max="5" /> <strong>{{ ipvForm.h3_threaten }}</strong></div>
              <p class="pt-muted-sm">How often does your partner <strong>scream or curse</strong> at you?</p>
              <div class="pt-wb-scale"><input type="range" v-model.number="ipvForm.h4_scream" min="1" max="5" /> <strong>{{ ipvForm.h4_scream }}</strong></div>

              <label class="pt-wb-check"><input type="checkbox" v-model="ipvForm.children_at_risk" :true-value="1" :false-value="0" /> I am worried about my children's safety</label>
              <label class="pt-wb-check"><input type="checkbox" v-model="ipvForm.immediate_danger" :true-value="1" :false-value="0" /> I feel in danger right now</label>

              <div class="pt-wb-actions">
                <button class="pt-btn pt-btn-secondary" @click="ipvForm.active=false">Cancel</button>
                <button class="pt-btn pt-btn-primary" :disabled="ipvSubmitting" @click="submitIpv">{{ ipvSubmitting ? 'Saving…' : 'Submit confidentially' }}</button>
              </div>
            </div>

            <div v-if="ipvResult" class="pt-wb-result" :class="'pt-wb-result-' + (ipvResult.severity || 'no_disclosure')">
              <h4 class="pt-h3">Thank you for telling us</h4>
              <p>{{ ipvResult.message }}</p>
              <div v-if="ipvResult.resources && ipvResult.resources.length" class="pt-wb-resources">
                <h4 class="pt-h4">Help is one tap away</h4>
                <div v-for="(r, i) in ipvResult.resources" :key="i" class="pt-wb-resource">
                  <a :href="'tel:' + r.phone"><i class="mdi mdi-phone"></i> {{ r.phone }}</a>
                  <div><strong>{{ r.label }}</strong> <span class="pt-muted-sm">{{ r.type }}</span></div>
                </div>
              </div>
              <button class="pt-btn pt-btn-link mt-2" @click="ipvResult=null">Close</button>
            </div>

            <div class="pt-wb-safety">
              <i class="mdi mdi-exit-run"></i> <strong>Quick Exit</strong> (bottom-right of every screen) hides this app and clears history. Practice it now so it's ready when you need it.
            </div>
          </div>
        </div>

        <!-- ===== MCH BOOKLET (printable) ===== -->
        <div v-if="tab==='booklet'" class="pt-booklet">
          <div class="pt-booklet-controls">
            <h2 class="pt-h2"><i class="mdi mdi-book-open-page-variant"></i> Your MCH Booklet</h2>
            <button class="pt-btn pt-btn-primary" @click="printBooklet"><i class="mdi mdi-printer"></i> Print / Save as PDF</button>
          </div>
          <p class="pt-muted">Digital twin of the Kenya MoH Mother & Child Health booklet. Take it with you to every visit.</p>

          <div v-if="booklet" class="pt-booklet-page">
            <header class="pt-booklet-header">
              <div>
                <h1 class="pt-booklet-title">Mother & Child Health Booklet</h1>
                <div class="pt-muted-sm">{{ booklet.Facility?.hospital_name || 'Facility' }} · Generated {{ formatDateTime(booklet.GeneratedAt) }}</div>
              </div>
              <div class="pt-booklet-id">
                <div><strong>{{ booklet.Patient?.first_name }} {{ booklet.Patient?.last_name }}</strong></div>
                <div class="pt-muted-sm">{{ booklet.Patient?.patient_no }} · DOB {{ formatDate(booklet.Patient?.dob) }}</div>
                <div v-if="booklet.Patient?.birth_notification_no" class="pt-muted-sm">NoB: {{ booklet.Patient.birth_notification_no }}</div>
              </div>
            </header>

            <!-- MOTHER SECTION -->
            <section v-if="!booklet.IsBaby">
              <h3 class="pt-booklet-h3">1. Antenatal care</h3>
              <table class="pt-booklet-table">
                <thead><tr><th>#</th><th>Date</th><th>Wk</th><th>BP</th><th>Wt</th><th>Hb</th><th>FHR</th><th>HIV</th><th>Complications</th></tr></thead>
                <tbody>
                  <tr v-for="v in booklet.AncVisits" :key="v.visit_number">
                    <td>{{ v.visit_number }}</td>
                    <td>{{ formatDate(v.visit_date) }}</td>
                    <td>{{ v.gestational_weeks }}</td>
                    <td>{{ v.bp_systolic ? v.bp_systolic + '/' + v.bp_diastolic : '—' }}</td>
                    <td>{{ v.weight_kg || '—' }}</td>
                    <td>{{ v.hb_level || '—' }}</td>
                    <td>{{ v.fetal_heart_rate || '—' }}</td>
                    <td>{{ v.hiv_status || '—' }}</td>
                    <td>{{ v.complications || '' }}</td>
                  </tr>
                  <tr v-if="!booklet.AncVisits?.length"><td colspan="9" class="pt-empty">No ANC visits recorded.</td></tr>
                </tbody>
              </table>

              <h3 class="pt-booklet-h3">2. Delivery</h3>
              <div v-if="booklet.Delivery">
                <div class="pt-booklet-grid">
                  <div><strong>Date / time:</strong> {{ formatDateTime(booklet.Delivery.delivered_at) }}</div>
                  <div><strong>Mode:</strong> {{ booklet.Delivery.mode }}</div>
                  <div><strong>Baby outcome:</strong> {{ booklet.Delivery.baby_outcome }}</div>
                  <div><strong>Baby sex:</strong> {{ booklet.Delivery.baby_sex }}</div>
                  <div><strong>Birth weight:</strong> {{ booklet.Delivery.baby_weight_g }} g</div>
                  <div><strong>APGAR 1 / 5:</strong> {{ booklet.Delivery.apgar_1 }}/{{ booklet.Delivery.apgar_5 }}</div>
                  <div><strong>PPH:</strong> {{ booklet.Delivery.pph_occurred == 1 ? 'YES' : 'no' }}</div>
                  <div><strong>AMTSL completed:</strong> {{ booklet.Delivery.amtsl_completed == 1 ? '✓' : '—' }}</div>
                </div>
              </div>
              <div v-else class="pt-empty">No delivery on file.</div>

              <h3 class="pt-booklet-h3">3. Postnatal visits</h3>
              <table class="pt-booklet-table">
                <thead><tr><th>Timing</th><th>Scheduled</th><th>Done</th><th>BP</th><th>Baby wt</th><th>EPDS</th><th>Danger signs</th></tr></thead>
                <tbody>
                  <tr v-for="v in booklet.PncVisits" :key="v.visit_timing">
                    <td><strong>{{ v.visit_timing }}</strong></td>
                    <td>{{ formatDate(v.scheduled_date) }}</td>
                    <td>{{ formatDate(v.visit_date) || '—' }}</td>
                    <td>{{ v.bp_systolic ? v.bp_systolic + '/' + v.bp_diastolic : '—' }}</td>
                    <td>{{ v.baby_weight_g || '—' }}</td>
                    <td>{{ v.epds_score !== null ? v.epds_score : '—' }}</td>
                    <td>{{ (v.danger_signs_found || '') === 'none' ? '—' : v.danger_signs_found }}</td>
                  </tr>
                  <tr v-if="!booklet.PncVisits?.length"><td colspan="7" class="pt-empty">No PNC visits.</td></tr>
                </tbody>
              </table>
            </section>

            <!-- BABY SECTION -->
            <section v-if="booklet.IsBaby">
              <h3 class="pt-booklet-h3">1. Birth details</h3>
              <div class="pt-booklet-grid">
                <div><strong>DOB:</strong> {{ formatDate(booklet.Patient?.dob) }}</div>
                <div><strong>Sex:</strong> {{ booklet.Patient?.gender }}</div>
                <div><strong>NoB:</strong> {{ booklet.Patient?.birth_notification_no || '—' }}</div>
                <div><strong>Birth facility:</strong> {{ booklet.Patient?.birth_facility_name || '—' }}</div>
                <div v-if="booklet.Patient?.is_hei_baby == 1"><strong>HIV-exposed infant</strong> (HEI follow-up)</div>
              </div>

              <h3 class="pt-booklet-h3">2. Growth</h3>
              <table class="pt-booklet-table">
                <thead><tr><th>Date</th><th>Wt (kg)</th><th>Ht (cm)</th><th>HC (cm)</th><th>WAZ</th><th>HAZ</th><th>Status</th></tr></thead>
                <tbody>
                  <tr v-for="(g, i) in booklet.BabyGrowth" :key="i">
                    <td>{{ formatDate(g.measured_at) }}</td>
                    <td>{{ g.weight_kg || '—' }}</td>
                    <td>{{ (g.length_cm || g.height_cm) || '—' }}</td>
                    <td>{{ g.head_circumference_cm || '—' }}</td>
                    <td>{{ g.waz || '—' }}</td>
                    <td>{{ g.haz || '—' }}</td>
                    <td>{{ g.nutritional_status }}</td>
                  </tr>
                  <tr v-if="!booklet.BabyGrowth?.length"><td colspan="7" class="pt-empty">No growth measurements.</td></tr>
                </tbody>
              </table>

              <h3 class="pt-booklet-h3">3. Immunisation card (KEPI)</h3>
              <table class="pt-booklet-table">
                <thead><tr><th>Vaccine</th><th>Scheduled</th><th>Given</th><th>Status</th></tr></thead>
                <tbody>
                  <tr v-for="(d, i) in booklet.BabyImmunizations" :key="i">
                    <td><strong>{{ d.vaccine_code }}</strong> {{ d.vaccine_name }}</td>
                    <td>{{ formatDate(d.scheduled_date) }}</td>
                    <td>{{ formatDate(d.given_date) || '—' }}</td>
                    <td>{{ d.status }}</td>
                  </tr>
                  <tr v-if="!booklet.BabyImmunizations?.length"><td colspan="4" class="pt-empty">No immunisations recorded.</td></tr>
                </tbody>
              </table>

              <h3 class="pt-booklet-h3" v-if="booklet.BabyHei">4. HEI (HIV-exposed infant) follow-up</h3>
              <div v-if="booklet.BabyHei" class="pt-booklet-grid">
                <div><strong>Risk:</strong> {{ booklet.BabyHei.risk_classification }}</div>
                <div><strong>ARV:</strong> {{ booklet.BabyHei.arv_regimen }}</div>
                <div><strong>Feeding:</strong> {{ booklet.BabyHei.feeding_option }}</div>
                <div><strong>Final status:</strong> {{ booklet.BabyHei.final_status }}</div>
              </div>
            </section>

            <footer class="pt-booklet-footer">
              Generated by HMS-MAIN · {{ formatDateTime(booklet.GeneratedAt) }} ·
              This summary mirrors your paper MCH booklet. Take it with you to every clinical contact.
            </footer>
          </div>
        </div>

        <!-- ===== PREGNANCY TIMELINE ===== -->
        <div v-if="tab==='pregnancy' && preg.IsPregnant">
          <!-- Hero -->
          <div class="pt-preg-hero">
            <div class="pt-preg-circle">
              <svg viewBox="0 0 120 120" class="pt-preg-svg">
                <circle cx="60" cy="60" r="52" stroke="#fce7f3" stroke-width="10" fill="none" />
                <circle cx="60" cy="60" r="52" :stroke="weekColor" stroke-width="10" fill="none"
                        :stroke-dasharray="326" :stroke-dashoffset="326 - (326 * (preg.Weeks/40))"
                        stroke-linecap="round" transform="rotate(-90 60 60)" />
                <text x="60" y="58" text-anchor="middle" class="pt-preg-week-num">{{ preg.Weeks }}</text>
                <text x="60" y="78" text-anchor="middle" class="pt-preg-week-lbl">weeks</text>
              </svg>
            </div>
            <div class="pt-preg-info">
              <h2 class="pt-h2">You're {{ preg.Weeks }} weeks{{ preg.Days ? ' ' + preg.Days + ' days' : '' }} pregnant</h2>
              <p>Trimester {{ preg.Trimester }} · EDD <strong>{{ formatDate(preg.Latest.edd) }}</strong>
                <span v-if="preg.DaysToEdd >= 0"> ({{ preg.DaysToEdd }} days to go)</span>
                <span v-else class="pt-bad"> ({{ Math.abs(preg.DaysToEdd) }} days past due)</span>
              </p>
              <p class="pt-muted">G{{ preg.Latest.gravida }}P{{ preg.Latest.para }} · LMP {{ formatDate(preg.Latest.lmp_date) }}</p>
            </div>
          </div>

          <!-- This week -->
          <div class="pt-preg-card pt-preg-current" v-if="preg.Current">
            <h3 class="pt-h3"><i class="mdi mdi-baby-face-outline"></i> This week — week {{ preg.Weeks }}</h3>
            <div class="pt-preg-size">📏 Baby is the size of a <strong>{{ preg.Current.size }}</strong></div>
            <div class="pt-preg-grid">
              <div><strong>👶 Baby:</strong> {{ preg.Current.baby }}</div>
              <div><strong>🤰 Mum:</strong> {{ preg.Current.mum }}</div>
              <div class="pt-preg-do"><strong>✓ This visit:</strong> {{ preg.Current.do }}</div>
            </div>
          </div>

          <!-- Risk flags -->
          <div v-if="preg.RiskFactors.length" class="pt-preg-card pt-preg-risks">
            <h3 class="pt-h3"><i class="mdi mdi-alert"></i> Risk factors</h3>
            <ul class="pt-list">
              <li v-for="(r, i) in preg.RiskFactors" :key="i">{{ r }}</li>
            </ul>
          </div>

          <!-- Always-on danger signs -->
          <div class="pt-preg-card pt-preg-danger">
            <h3 class="pt-h3"><i class="mdi mdi-alert-octagon"></i> Call us immediately if you have:</h3>
            <ul class="pt-list">
              <li>Severe headache, blurred vision, or upper-tummy pain</li>
              <li>Sudden swelling of face, hands or feet</li>
              <li>Heavy bleeding or sudden gush of fluid from the vagina</li>
              <li>No baby movement for several hours after 24 weeks</li>
              <li>Severe abdominal pain or contractions before 37 weeks</li>
              <li>High fever (&gt;38°C) or chills</li>
            </ul>
            <a href="tel:1199" class="pt-btn pt-btn-danger mt-2"><i class="mdi mdi-phone"></i> Call emergency 1199</a>
          </div>

          <!-- Coming up -->
          <div class="pt-preg-card" v-if="preg.Upcoming.length">
            <h3 class="pt-h3"><i class="mdi mdi-calendar-clock"></i> Coming up</h3>
            <div v-for="(m, i) in preg.Upcoming" :key="i" class="pt-preg-up">
              <strong>Weeks {{ m.from }}–{{ m.to }}</strong> · {{ m.size }}
              <div class="pt-muted-sm">{{ m.do }}</div>
            </div>
          </div>

          <!-- ANC visits history -->
          <div class="pt-preg-card">
            <h3 class="pt-h3"><i class="mdi mdi-calendar"></i> ANC visits</h3>
            <table class="pt-table">
              <thead><tr><th>#</th><th>Date</th><th>Week</th><th>BP</th><th>Wt</th><th>Hb</th><th>FHR</th><th>Notes</th></tr></thead>
              <tbody>
                <tr v-for="v in preg.AncVisits" :key="v.anc_visit_id">
                  <td><strong>{{ v.visit_number }}</strong></td>
                  <td>{{ formatDate(v.visit_date) }}</td>
                  <td>{{ v.gestational_weeks }}w</td>
                  <td>{{ v.bp_systolic ? v.bp_systolic + '/' + v.bp_diastolic : '—' }}</td>
                  <td>{{ v.weight_kg ? v.weight_kg + ' kg' : '—' }}</td>
                  <td>{{ v.hb_level || '—' }}</td>
                  <td>{{ v.fetal_heart_rate ? v.fetal_heart_rate + ' bpm' : '—' }}</td>
                  <td class="pt-muted-sm">{{ v.complications || '' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="preg.Latest.next_visit_date" class="pt-muted-sm mt-2">
              📅 Next ANC visit: <strong>{{ formatDate(preg.Latest.next_visit_date) }}</strong>
            </div>
          </div>
        </div>

        <!-- PMTCT cascade card (shows whenever the patient is enrolled in PMTCT) -->
        <div v-if="tab==='pregnancy' && pmtct.IsPmtctClient" class="pt-preg-card pt-pmtct-card">
          <h3 class="pt-h3"><i class="mdi mdi-shield-account"></i> Your HIV / ART status (PMTCT)</h3>
          <p class="pt-muted-sm">Your private status update. Stay on your daily ART and your baby is protected.</p>
          <div class="pt-pmtct-row">
            <div><strong>HIV status:</strong> {{ pmtct.Client.hiv_status }}</div>
            <div><strong>ART:</strong>
              <span v-if="pmtct.Client.art_started==1" class="pt-pill pt-pill-success">{{ pmtct.Client.art_regimen || 'on treatment' }}</span>
              <span v-else class="pt-pill pt-pill-warn">Not yet started — please come in</span>
            </div>
            <div v-if="pmtct.Client.art_start_date"><strong>Started:</strong> {{ formatDate(pmtct.Client.art_start_date) }}</div>
          </div>
          <div v-if="pmtct.LatestVl" class="pt-pmtct-row">
            <div><strong>Last viral load:</strong>
              <span v-if="pmtct.LatestVl.result_status === 'suppressed' || pmtct.LatestVl.result_status === 'LDL'" class="pt-pill pt-pill-success">{{ pmtct.LatestVl.vl_copies || '<50' }} c/ml ✓ suppressed</span>
              <span v-else-if="pmtct.LatestVl.result_status === 'high_level'" class="pt-pill pt-pill-warn">{{ pmtct.LatestVl.vl_copies }} c/ml — please come in</span>
              <span v-else>{{ pmtct.LatestVl.vl_copies }} c/ml ({{ pmtct.LatestVl.result_status.replace('_',' ') }})</span>
            </div>
            <div class="pt-muted-sm">{{ formatDate(pmtct.LatestVl.result_date) }}</div>
          </div>
          <div v-if="pmtct.NextVl" class="pt-pmtct-row">
            <div><strong>Next VL due:</strong> {{ formatDate(pmtct.NextVl.sample_due_date) }} ({{ pmtct.NextVl.timing.replace('_',' ') }})</div>
          </div>
          <div class="pt-pmtct-row pt-muted-sm">
            Cotrimoxazole: {{ pmtct.Client.cotrimoxazole_prophylaxis==1 ? '✓' : 'not started' }} ·
            Partner tested: {{ pmtct.Client.partner_tested==1 ? '✓ (' + (pmtct.Client.partner_status||'unknown') + ')' : 'no' }}
          </div>
          <div class="pt-pmtct-status"><i class="mdi mdi-progress-check"></i> {{ pmtct.Client.cascade_label }}</div>
        </div>

        <!-- PNC SCHEDULE — shown whenever there's a delivery on file (postpartum mode) -->
        <div v-if="tab==='pregnancy' && pnc.HasPnc" class="pt-preg-card">
          <h3 class="pt-h3"><i class="mdi mdi-baby-face"></i> Your postnatal visits</h3>
          <p class="pt-muted-sm">After delivery on {{ formatDate(pnc.Delivery.delivered_at) }} ({{ pnc.Delivery.mode }}). 5 check-ups protect you and baby — please come for each one.</p>
          <div v-for="v in pnc.Visits" :key="v.pnc_id" class="pt-pnc-row" :class="pncRowClass(v)">
            <div class="pt-pnc-icon">
              <i v-if="v.visit_status === 'completed'" class="mdi mdi-check-circle pt-good"></i>
              <i v-else-if="v.is_overdue" class="mdi mdi-alert-circle pt-bad"></i>
              <i v-else-if="v.is_due_today" class="mdi mdi-clock-alert pt-warn"></i>
              <i v-else-if="v.is_due_soon" class="mdi mdi-clock-outline pt-muted"></i>
              <i v-else class="mdi mdi-circle-outline pt-muted"></i>
            </div>
            <div class="pt-pnc-body">
              <strong>{{ v.timing_label }} check</strong>
              <div class="pt-muted-sm">
                <span v-if="v.visit_status === 'completed'">Done {{ formatDate(v.visit_date) }}</span>
                <span v-else-if="v.is_due_today">Due TODAY — {{ formatDate(v.scheduled_date) }}</span>
                <span v-else-if="v.is_overdue">Overdue — was due {{ formatDate(v.scheduled_date) }}</span>
                <span v-else>Scheduled {{ formatDate(v.scheduled_date) }}</span>
              </div>
              <div v-if="v.visit_status === 'completed'" class="pt-muted-sm">
                <span v-if="v.bp_systolic">BP {{ v.bp_systolic }}/{{ v.bp_diastolic }}</span>
                <span v-if="v.baby_weight_g"> · baby {{ v.baby_weight_g }}g</span>
                <span v-if="v.visit_timing === '6wk' && v.epds_score !== null"> · EPDS {{ v.epds_score }}{{ v.epds_q10_self_harm == 1 ? ' (Q10+)' : '' }}</span>
                <span v-if="v.fp_started_today == 1"> · FP started: {{ v.fp_method_chosen }}</span>
              </div>
              <div v-if="v.danger_signs_found && v.danger_signs_found !== 'none' && v.danger_signs_found !== ''" class="pt-muted-sm pt-bad">⚠ {{ v.danger_signs_found.replace(/,/g, ' · ') }}</div>
            </div>
          </div>
        </div>

        <!-- Empty state if not pregnant + no postpartum -->
        <div v-if="tab==='pregnancy' && !preg.IsPregnant && !pnc.HasPnc" class="pt-empty">
          No active pregnancy or postpartum record on file. Once you have an ANC visit recorded, this tab will populate.
        </div>

        <!-- ===== DOCUMENT VAULT ===== -->
        <div v-if="tab==='docs'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 class="pt-h2"><i class="mdi mdi-folder-multiple"></i> Document Vault</h2>
              <p class="pt-muted mb-0">Securely store ID, insurance card, foreign records, scans. Your care team can access them when needed.</p>
            </div>
            <button class="pt-btn pt-btn-primary" @click="openDocUpload"><i class="mdi mdi-cloud-upload"></i> Upload</button>
          </div>

          <!-- Stats -->
          <div class="pt-doc-stats" v-if="docStats.total > 0">
            <div class="pt-doc-stat"><strong>{{ docStats.total }}</strong><div class="pt-muted-sm">Documents</div></div>
            <div class="pt-doc-stat"><strong>{{ formatBytes(docStats.total_bytes) }}</strong><div class="pt-muted-sm">Storage used</div></div>
            <div class="pt-doc-stat"><strong>{{ docStats.ids || 0 }}</strong><div class="pt-muted-sm">IDs</div></div>
            <div class="pt-doc-stat"><strong>{{ docStats.insurance || 0 }}</strong><div class="pt-muted-sm">Insurance</div></div>
            <div class="pt-doc-stat"><strong>{{ docStats.records || 0 }}</strong><div class="pt-muted-sm">Records</div></div>
            <div class="pt-doc-stat"><strong>{{ docStats.scans || 0 }}</strong><div class="pt-muted-sm">Scans</div></div>
          </div>

          <!-- Category filter pills -->
          <div class="pt-doc-filters">
            <button class="pt-doc-pill" :class="{ active: docFilter === '' }" @click="docFilter=''; loadDocs()">All</button>
            <button v-for="c in docCategoriesAll" :key="c.value" class="pt-doc-pill" :class="{ active: docFilter === c.value }" @click="docFilter=c.value; loadDocs()">
              <i class="mdi" :class="docCatIcon(c.value)"></i> {{ c.label }}
            </button>
          </div>

          <!-- Document grid -->
          <div class="pt-doc-grid">
            <div v-for="d in docs" :key="d.document_id" class="pt-doc-card">
              <div class="pt-doc-icon" :class="docTypeColor(d.file_extension)">
                <i class="mdi" :class="docTypeIcon(d.file_extension)"></i>
              </div>
              <div class="pt-doc-meta">
                <strong class="pt-doc-title" :title="d.title">{{ d.title }}</strong>
                <div class="pt-muted-sm">
                  <span class="pt-pill pt-pill-muted">{{ docCatLabel(d.category) }}</span>
                  · {{ formatBytes(d.file_size_bytes) }}
                </div>
                <div class="pt-muted-sm">{{ formatDate(d.created_at) }}</div>
                <div v-if="!d.visible_to_clinicians" class="pt-muted-sm pt-bad"><i class="mdi mdi-eye-off"></i> Hidden from clinicians</div>
              </div>
              <div class="pt-doc-actions">
                <button class="pt-btn-link" title="View" @click="viewDoc(d)"><i class="mdi mdi-eye"></i></button>
                <button class="pt-btn-link" title="Edit" @click="editDoc(d)"><i class="mdi mdi-pencil"></i></button>
                <button class="pt-btn-link pt-bad" title="Delete" @click="deleteDoc(d)"><i class="mdi mdi-trash-can"></i></button>
              </div>
            </div>
          </div>
          <div v-if="!docs.length" class="pt-empty">No documents yet. Click <strong>Upload</strong> to add your first one.</div>
        </div>

        <!-- ===== WEARABLES ===== -->
        <div v-if="tab==='wearables'">
          <h2 class="pt-h2"><i class="mdi mdi-watch-variant"></i> Wearables &amp; Devices</h2>
          <p class="pt-muted">Sync your smart watch, fitness tracker, BP cuff, or glucometer. Data flows into your Health Diary so your clinician sees your real-world readings.</p>

          <!-- 7-day stats -->
          <div v-if="wearStats.readings_7d > 0" class="pt-wear-stats">
            <div class="pt-wear-stat"><div class="pt-stat-num">{{ wearStats.readings_7d }}</div><div class="pt-stat-lbl">Readings (7d)</div></div>
            <div class="pt-wear-stat"><div class="pt-stat-num">{{ formatNumber(wearStats.steps_7d) }}</div><div class="pt-stat-lbl">Steps total</div></div>
            <div class="pt-wear-stat"><div class="pt-stat-num">{{ wearStats.avg_hr_7d ? Math.round(wearStats.avg_hr_7d) : '—' }}</div><div class="pt-stat-lbl">Avg heart rate</div></div>
            <div class="pt-wear-stat"><div class="pt-stat-num">{{ wearStats.avg_sleep_7d ? parseFloat(wearStats.avg_sleep_7d).toFixed(1) : '—' }}h</div><div class="pt-stat-lbl">Avg sleep</div></div>
          </div>

          <!-- Connected devices -->
          <h3 class="pt-h3 mt-3">Connected devices</h3>
          <div v-for="d in wearDevices" :key="d.connection_id" class="pt-wear-device">
            <i class="mdi pt-wear-icon" :class="vendorIcon(d.vendor)"></i>
            <div class="flex-grow-1">
              <strong>{{ vendorLabel(d.vendor) }}</strong>
              <div class="pt-muted-sm">{{ d.device_name || vendorLabel(d.vendor) }}</div>
              <div class="pt-muted-sm">
                Connected {{ formatDate(d.connected_at) }} · {{ d.sync_count }} sync{{ d.sync_count == 1 ? '' : 's' }}
                <span v-if="d.last_sync_at"> · last {{ formatDt(d.last_sync_at) }}</span>
              </div>
            </div>
            <span class="pt-pill" :class="d.status === 'active' ? 'pt-pill-success' : 'pt-pill-muted'">{{ d.status }}</span>
            <button class="pt-btn pt-btn-link" @click="disconnectDevice(d)">Disconnect</button>
          </div>
          <div v-if="!wearDevices.length" class="pt-empty">No devices connected yet — pick one below.</div>

          <!-- Connect new -->
          <h3 class="pt-h3 mt-4">Connect a device</h3>
          <div class="pt-wear-grid">
            <div class="pt-wear-card" @click="openWearConnect('apple_health')">
              <i class="mdi mdi-apple"></i><strong>Apple Health</strong>
              <div class="pt-muted-sm">iPhone / Apple Watch</div>
              <span class="pt-pill pt-pill-info">XML import</span>
            </div>
            <div class="pt-wear-card" @click="openWearConnect('fitbit')">
              <i class="mdi mdi-watch"></i><strong>Fitbit</strong>
              <div class="pt-muted-sm">Charge / Versa / Sense</div>
              <span class="pt-pill pt-pill-warn">OAuth (setup required)</span>
            </div>
            <div class="pt-wear-card" @click="openWearConnect('garmin')">
              <i class="mdi mdi-watch-variant"></i><strong>Garmin</strong>
              <div class="pt-muted-sm">Forerunner / Venu / Fenix</div>
              <span class="pt-pill pt-pill-warn">OAuth (setup required)</span>
            </div>
            <div class="pt-wear-card" @click="openWearConnect('samsung_health')">
              <i class="mdi mdi-cellphone"></i><strong>Samsung Health</strong>
              <div class="pt-muted-sm">Galaxy Watch / Phone</div>
              <span class="pt-pill pt-pill-warn">OAuth (setup required)</span>
            </div>
            <div class="pt-wear-card" @click="openWearConnect('oura')">
              <i class="mdi mdi-ring"></i><strong>Oura Ring</strong>
              <div class="pt-muted-sm">Sleep + recovery</div>
              <span class="pt-pill pt-pill-warn">OAuth (setup required)</span>
            </div>
            <div class="pt-wear-card" @click="openWearConnect('withings')">
              <i class="mdi mdi-scale-bathroom"></i><strong>Withings</strong>
              <div class="pt-muted-sm">Scales, BP, watches</div>
              <span class="pt-pill pt-pill-warn">OAuth (setup required)</span>
            </div>
            <div class="pt-wear-card" @click="openWearConnect('google_fit')">
              <i class="mdi mdi-google"></i><strong>Google Fit</strong>
              <div class="pt-muted-sm">Wear OS / Pixel Watch</div>
              <span class="pt-pill pt-pill-warn">OAuth (setup required)</span>
            </div>
            <div class="pt-wear-card pt-wear-demo" @click="simulateWear()">
              <i class="mdi mdi-flask-outline"></i><strong>Demo: Simulate</strong>
              <div class="pt-muted-sm">Generate 7 days of fake data</div>
              <span class="pt-pill pt-pill-success">For testing</span>
            </div>
          </div>

          <p class="pt-muted-sm mt-3"><i class="mdi mdi-shield-lock"></i> All wearable data is encrypted in transit and only visible to you and your clinical team. You can disconnect at any time.</p>
        </div>

        <!-- ===== MY MEDICATIONS ===== -->
        <div v-if="tab==='meds'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 class="pt-h2"><i class="mdi mdi-pill-multiple"></i> My Medications</h2>
              <p class="pt-muted mb-0">Track your active medications, set SMS reminders, and log when you take each dose.</p>
            </div>
          </div>

          <!-- Adherence overview -->
          <div v-if="medAdherence.Total" class="pt-adh-summary">
            <div class="pt-adh-card">
              <div class="pt-adh-pct" :class="adhClass(medAdherence.Total.taken, medAdherence.Total.total)">
                {{ adhPercent(medAdherence.Total.taken, medAdherence.Total.total) }}<span>%</span>
              </div>
              <div class="pt-muted-sm">{{ medAdherence.Days }}-day adherence</div>
              <div class="pt-muted-sm">{{ medAdherence.Total.taken || 0 }} of {{ medAdherence.Total.total || 0 }} doses taken</div>
            </div>
            <div class="pt-adh-card">
              <div class="pt-adh-streak"><i class="mdi mdi-fire"></i> {{ medAdherence.Streak || 0 }}</div>
              <div class="pt-muted-sm">Day streak (≥90% adherence)</div>
            </div>
          </div>

          <!-- Active meds -->
          <div v-for="m in meds" :key="m.item_id" class="pt-med-card">
            <div class="pt-med-head">
              <div>
                <strong>{{ m.drug_name }}</strong>
                <div class="pt-muted-sm">{{ m.dosage }} · {{ m.frequency }} · {{ m.duration }} · {{ m.route || 'PO' }}</div>
              </div>
              <div class="text-end">
                <div v-if="m.adherence_pct !== null" class="pt-med-adh" :class="adhClassPct(m.adherence_pct)">
                  <strong>{{ m.adherence_pct }}%</strong>
                  <div class="pt-muted-sm">{{ m.taken_30d }}/{{ m.doses_30d }} (30d)</div>
                </div>
                <div v-else class="pt-muted-sm">No tracking yet</div>
              </div>
            </div>

            <div class="pt-med-actions">
              <button class="pt-btn pt-btn-success" @click="logDose(m, 'taken')"><i class="mdi mdi-check"></i> Took it</button>
              <button class="pt-btn pt-btn-warning" @click="logDose(m, 'late')"><i class="mdi mdi-clock-alert"></i> Late</button>
              <button class="pt-btn pt-btn-secondary" @click="logSkip(m)"><i class="mdi mdi-close"></i> Skipped</button>
              <button class="pt-btn pt-btn-link ms-auto" @click="openReminderSetup(m)"><i class="mdi mdi-bell"></i> {{ m.reminder_on == 1 ? 'Reminders ON' : 'Set reminders' }}</button>
            </div>

            <div v-if="m.reminder_on == 1 && m.schedule_times" class="pt-med-times">
              <i class="mdi mdi-bell-ring"></i> Reminders at:
              <code v-for="(t, i) in m.schedule_times" :key="i">{{ t }}</code>
            </div>
          </div>

          <div v-if="!meds.length" class="pt-empty">No active medications. Your prescriptions appear here once issued by a clinician.</div>
        </div>

        <!-- ===== EMERGENCY QR CARD ===== -->
        <div v-if="tab==='emergency'">
          <h2 class="pt-h2"><i class="mdi mdi-medical-bag"></i> Emergency QR Card</h2>
          <p class="pt-muted">A QR code paramedics or first responders can scan to instantly see your allergies, conditions, current medications, and next-of-kin — without needing to log in.</p>

          <div v-if="emQR" class="pt-em-grid">
            <div class="pt-em-card">
              <img :src="emQR.qr_image" class="pt-em-qr" alt="Emergency QR" />
              <div class="pt-em-shortcode">
                <div class="pt-muted-sm">Reference code:</div>
                <code>{{ emQR.short_code }}</code>
              </div>
              <div class="pt-em-stats">
                <div><strong>{{ emQR.scan_count }}</strong> scan{{ emQR.scan_count === 1 ? '' : 's' }}</div>
                <div v-if="emQR.last_scanned_at" class="pt-muted-sm">Last: {{ formatDt(emQR.last_scanned_at) }}</div>
              </div>
              <div class="pt-em-actions">
                <button class="pt-btn pt-btn-primary" @click="printEmergencyCard"><i class="mdi mdi-printer"></i> Print wallet card</button>
                <button class="pt-btn pt-btn-secondary" @click="copyEmergencyLink"><i class="mdi mdi-link"></i> Copy link</button>
                <button class="pt-btn pt-btn-link" @click="regenerateEmergency"><i class="mdi mdi-refresh"></i> Regenerate</button>
              </div>
            </div>

            <div class="pt-em-info">
              <h3 class="pt-h3">How to use it</h3>
              <ol>
                <li><strong>Save the QR</strong> on your phone's lock screen wallpaper, in your wallet (printed), or as a sticker on your medical card.</li>
                <li><strong>In an emergency</strong>, paramedics or anyone helping you can scan it with any phone camera.</li>
                <li>The scan opens a <strong>read-only emergency page</strong> showing allergies, current meds, blood group, conditions, and your next-of-kin's number.</li>
                <li><strong>Lost your phone?</strong> Click <em>Regenerate</em> to invalidate the old QR and issue a new one.</li>
              </ol>

              <h3 class="pt-h3 mt-3">What it shows</h3>
              <ul class="pt-em-shows">
                <li>✓ Your name, age, sex, blood group</li>
                <li>✓ Allergies (red banner)</li>
                <li>✓ Active programmes (HIV, TB, MH, etc.)</li>
                <li>✓ Current medications (last 90 days)</li>
                <li>✓ Recent diagnoses</li>
                <li>✓ Next of kin with one-tap call</li>
                <li>✓ Insurance / SHA details</li>
                <li>✗ <em>Not</em> your full medical history</li>
                <li>✗ <em>Not</em> your password / login</li>
              </ul>

              <a class="pt-btn pt-btn-link mt-3" :href="emQR.public_url" target="_blank">
                <i class="mdi mdi-open-in-new"></i> Preview what others will see
              </a>
            </div>
          </div>

          <div v-if="!emQR && !emLoading" class="text-center pt-pad">
            <button class="pt-btn pt-btn-primary" @click="loadEmergencyQR"><i class="mdi mdi-qrcode-plus"></i> Get my QR card</button>
          </div>
          <div v-if="emLoading" class="pt-empty">Generating…</div>
        </div>

        <!-- ===== HEALTH DIARY ===== -->
        <div v-if="tab==='diary'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 class="pt-h2"><i class="mdi mdi-notebook-heart"></i> Health Diary</h2>
              <p class="pt-muted mb-0">Log your readings from home. Your doctor sees the trend at your next visit.</p>
            </div>
            <select v-model="diaryDays" class="pt-input" style="max-width:160px" @change="loadDiary">
              <option :value="7">Last 7 days</option>
              <option :value="30">Last 30 days</option>
              <option :value="90">Last 90 days</option>
            </select>
          </div>

          <!-- Quick log buttons -->
          <div class="pt-diary-quick">
            <button class="pt-diary-btn pt-diary-bp"      @click="openDiaryLog('bp')"><i class="mdi mdi-heart-pulse"></i><div>Blood Pressure</div></button>
            <button class="pt-diary-btn pt-diary-glucose" @click="openDiaryLog('glucose')"><i class="mdi mdi-water"></i><div>Glucose</div></button>
            <button class="pt-diary-btn pt-diary-weight"  @click="openDiaryLog('weight')"><i class="mdi mdi-scale-bathroom"></i><div>Weight</div></button>
            <button class="pt-diary-btn pt-diary-mood"    @click="openDiaryLog('mood')"><i class="mdi mdi-emoticon-outline"></i><div>Mood &amp; Sleep</div></button>
            <button class="pt-diary-btn pt-diary-other"   @click="openDiaryLog('other')"><i class="mdi mdi-plus"></i><div>Other</div></button>
          </div>

          <!-- Trends grid -->
          <div class="pt-trend-grid">
            <div class="pt-trend-card" :class="bpClass(diary.latest)">
              <div class="pt-trend-head"><i class="mdi mdi-heart-pulse"></i> Blood Pressure</div>
              <div class="pt-trend-value">
                <strong v-if="diary.latest.bp_systolic">{{ diary.latest.bp_systolic }}/{{ diary.latest.bp_diastolic }}</strong>
                <span v-else class="pt-muted">no data</span>
                <span class="pt-trend-unit">mmHg</span>
              </div>
              <div class="pt-muted-sm">{{ diary.latest.bp_at ? formatDt(diary.latest.bp_at) : '' }}</div>
              <Spark :data="diary.bp.map(r => parseInt(r.sys))" color="#e53e3e" />
              <Spark :data="diary.bp.map(r => parseInt(r.dia))" color="#1a4480" />
            </div>

            <div class="pt-trend-card" :class="glucoseClass(diary.latest.glucose)">
              <div class="pt-trend-head"><i class="mdi mdi-water"></i> Glucose</div>
              <div class="pt-trend-value">
                <strong v-if="diary.latest.glucose">{{ diary.latest.glucose }}</strong>
                <span v-else class="pt-muted">no data</span>
                <span class="pt-trend-unit">mmol/L</span>
              </div>
              <div class="pt-muted-sm">{{ diary.latest.glucose_at ? formatDt(diary.latest.glucose_at) : '' }}</div>
              <Spark :data="diary.glucose.map(r => parseFloat(r.v))" color="#dd6b20" />
            </div>

            <div class="pt-trend-card">
              <div class="pt-trend-head"><i class="mdi mdi-scale-bathroom"></i> Weight</div>
              <div class="pt-trend-value">
                <strong v-if="diary.latest.weight">{{ parseFloat(diary.latest.weight).toFixed(1) }}</strong>
                <span v-else class="pt-muted">no data</span>
                <span class="pt-trend-unit">kg</span>
                <span v-if="diary.latest.bmi" class="pt-trend-sub">BMI {{ diary.latest.bmi }}</span>
              </div>
              <div class="pt-muted-sm">{{ diary.latest.weight_at ? formatDt(diary.latest.weight_at) : '' }}</div>
              <Spark :data="diary.weight.map(r => parseFloat(r.v))" color="#2563eb" />
            </div>

            <div class="pt-trend-card">
              <div class="pt-trend-head"><i class="mdi mdi-emoticon-outline"></i> Mood</div>
              <div class="pt-trend-value">
                <strong v-if="diary.latest.mood">{{ diary.latest.mood }}/10</strong>
                <span v-else class="pt-muted">no data</span>
              </div>
              <div class="pt-muted-sm">{{ diary.latest.mood_at ? formatDt(diary.latest.mood_at) : '' }}</div>
              <Spark :data="diary.mood.map(r => parseFloat(r.v))" color="#7c3aed" />
            </div>
          </div>

          <!-- History table -->
          <h3 class="pt-h3 mt-4">Recent entries</h3>
          <table class="pt-table">
            <thead><tr><th>When</th><th>Type</th><th>Reading</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              <tr v-for="l in diary.logs" :key="l.log_id">
                <td class="pt-muted-sm">{{ formatDt(l.logged_at) }}</td>
                <td><span class="pt-pill" :class="logTypePill(l.log_type)">{{ formatLogType(l.log_type) }}</span></td>
                <td><strong>{{ formatLogReading(l) }}</strong></td>
                <td class="pt-muted-sm">{{ l.notes || l.symptom_text || '' }}</td>
                <td><button class="pt-btn-link" @click="deleteLog(l)" title="Delete"><i class="mdi mdi-trash-can"></i></button></td>
              </tr>
              <tr v-if="!diary.logs.length"><td colspan="5" class="pt-empty">No entries yet — use the buttons above to log your first reading.</td></tr>
            </tbody>
          </table>
        </div>

        <!-- ===== APPOINTMENTS ===== -->
        <div v-if="tab==='appointments'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h2 class="pt-h2">Appointments</h2>
            <button class="pt-btn pt-btn-primary" @click="bookModal=true"><i class="mdi mdi-plus"></i> Book new</button>
          </div>
          <div v-for="a in appointments" :key="a.appointment_id" class="pt-row pt-row-card">
            <div>
              <strong>{{ formatDate(a.appointment_date) }} {{ a.appointment_time || '' }}</strong>
              <div class="pt-muted-sm">{{ a.appointment_number }} · {{ a.department || '' }} {{ a.doctor_name ? '· Dr. ' + a.doctor_name : '' }}</div>
              <div v-if="a.reason" class="pt-muted-sm">{{ a.reason }}</div>
            </div>
            <div class="text-end">
              <span class="pt-pill" :class="apptPill(a.status)">{{ a.status }}</span>
              <button v-if="a.status === 'scheduled'" class="pt-btn pt-btn-link mt-2" @click="cancelAppt(a)">Cancel</button>
            </div>
          </div>
          <div v-if="!appointments.length" class="pt-empty">No appointments yet.</div>
        </div>

        <!-- ===== RESULTS ===== -->
        <div v-if="tab==='results'">
          <h2 class="pt-h2">Lab results</h2>
          <p class="pt-muted">Only verified results are shown. Use Print for a full lab report.</p>

          <div v-for="grp in groupedResults" :key="grp.request_id" class="pt-results-card">
            <div class="pt-results-head">
              <div>
                <strong>{{ grp.request_number }}</strong>
                <span class="pt-muted-sm ms-2">{{ formatDate(grp.created_at) }}</span>
              </div>
              <button class="pt-btn pt-btn-primary" @click="printLab(grp.request_id)">
                🖨 Print report
              </button>
            </div>
            <table class="pt-table mb-0">
              <thead><tr><th>Test</th><th>Result</th><th>Range</th><th>Flag</th><th>Verified</th></tr></thead>
              <tbody>
                <tr v-for="r in grp.items" :key="r.item_id" :class="(r.flag === 'high' || r.flag === 'low' || r.flag === 'abnormal') ? 'pt-row-warn' : ((r.flag === 'critical_high' || r.flag === 'critical_low') ? 'pt-row-action' : '')">
                  <td><strong>{{ r.test_name }}</strong></td>
                  <td><strong>{{ r.result_value }}</strong> <span class="pt-muted-sm">{{ r.unit }}</span></td>
                  <td class="pt-muted-sm">{{ r.reference_range || '—' }}</td>
                  <td><span v-if="r.flag" class="pt-pill" :class="flagPill(r.flag)">{{ r.flag }}</span></td>
                  <td class="pt-muted-sm">{{ formatDate(r.verified_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="!groupedResults.length" class="pt-empty">No verified results yet.</div>
        </div>

        <!-- ===== RX ===== -->
        <div v-if="tab==='prescriptions'">
          <h2 class="pt-h2">Prescriptions</h2>
          <div v-for="rx in groupedRx" :key="rx.id" class="pt-row pt-row-card flex-column align-items-start">
            <div class="d-flex justify-content-between w-100 align-items-start">
              <div><strong>{{ formatDate(rx.created_at) }}</strong><div class="pt-muted-sm">Rx #{{ rx.id }}</div></div>
              <span class="pt-pill" :class="rxPill(rx.status)">{{ rx.status }}</span>
            </div>
            <div class="pt-rx-items">
              <div v-for="(item, idx) in rx.items" :key="idx" class="pt-rx-item">
                <strong>{{ item.item_description }}</strong>
                <div class="pt-muted-sm">{{ item.dosage }} · {{ item.frequency }} · {{ item.duration }}</div>
              </div>
            </div>
            <div v-if="rx.notes" class="pt-muted-sm mt-2">{{ rx.notes }}</div>
          </div>
          <div v-if="!groupedRx.length" class="pt-empty">No prescriptions yet.</div>
        </div>

        <!-- ===== IMMUNIZATIONS ===== -->
        <div v-if="tab==='immunizations'">
          <h2 class="pt-h2">Immunization card</h2>
          <table class="pt-table">
            <thead><tr><th>Vaccine</th><th>Dose</th><th>Date</th><th>Site/Route</th><th>Batch</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="i in immunizations" :key="i.dose_id">
                <td><strong>{{ i.code }}</strong> <span class="pt-muted-sm">{{ i.name }}</span></td>
                <td>#{{ i.dose_number }}</td>
                <td>{{ formatDate(i.given_date) || '—' }}</td>
                <td class="pt-muted-sm">{{ i.site || '' }} {{ i.route ? '· ' + i.route : '' }}</td>
                <td class="pt-muted-sm">{{ i.batch_number || '—' }}</td>
                <td><span class="pt-pill" :class="immPill(i.status)">{{ i.status }}</span></td>
              </tr>
              <tr v-if="!immunizations.length"><td colspan="6" class="pt-empty">No immunizations on record.</td></tr>
            </tbody>
          </table>
        </div>

        <!-- ===== TELEHEALTH ===== -->
        <div v-if="tab==='telehealth'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 class="pt-h2"><i class="mdi mdi-video"></i> Telehealth</h2>
              <p class="pt-muted mb-0">Talk to a clinician by video, voice, or chat — without coming to the hospital.</p>
            </div>
            <button class="pt-btn pt-btn-primary pt-btn-large" @click="openTelehealthRequest">
              <i class="mdi mdi-video-plus"></i> Request video call
            </button>
          </div>

          <div v-for="t in telehealth" :key="t.session_id" class="pt-row pt-row-card">
            <div>
              <strong>{{ formatDt(t.scheduled_start) }}</strong>
              <div class="pt-muted-sm">{{ t.document_no }} · {{ t.specialty || t.mode }} {{ t.provider_name ? '· ' + t.provider_name : '' }}</div>
              <div v-if="t.chief_complaint" class="pt-muted-sm">"{{ t.chief_complaint }}"</div>
              <div v-if="t.join_code && (t.status === 'scheduled' || t.status === 'in_progress')" class="pt-muted-sm">Join code: <code>{{ t.join_code }}</code></div>
              <div v-if="t.plan_text" class="pt-muted-sm mt-1">Plan: {{ t.plan_text }}</div>
            </div>
            <div class="text-end">
              <span class="pt-pill" :class="thPill(t.status)">{{ formatThStatus(t.status) }}</span>
              <button v-if="t.status === 'requested' || t.status === 'scheduled'" class="pt-btn pt-btn-link pt-btn-sm mt-1" @click="cancelTelehealth(t)">Cancel</button>
            </div>
          </div>
          <div v-if="!telehealth.length" class="pt-empty">No telehealth sessions yet. Click "Request video call" above to start one.</div>
        </div>

        <!-- ===== REFERRALS ===== -->
        <div v-if="tab==='referrals'">
          <h2 class="pt-h2">Referrals</h2>
          <div v-for="r in referrals" :key="r.referral_id" class="pt-row pt-row-card">
            <div>
              <strong>{{ r.document_no }}</strong>
              <div class="pt-muted-sm">{{ formatDate(r.created_at) }} · To {{ r.receiving_facility_name }} {{ r.receiving_specialty ? '· ' + r.receiving_specialty : '' }}</div>
              <div v-if="r.referral_reason" class="pt-muted-sm mt-1">{{ r.referral_reason }}</div>
              <div v-if="r.feedback_to_referrer" class="pt-feedback mt-2">{{ r.feedback_to_referrer }}</div>
            </div>
            <div class="text-end">
              <span class="pt-pill" :class="refPill(r.status)">{{ r.status }}</span>
              <div class="pt-muted-sm mt-1">{{ r.urgency }}</div>
            </div>
          </div>
          <div v-if="!referrals.length" class="pt-empty">No referrals.</div>
        </div>

        <!-- ===== BILLS ===== -->
        <div v-if="tab==='bills'">
          <h2 class="pt-h2">Bills</h2>
          <p class="pt-muted">Pay outstanding invoices instantly via M-PESA.</p>
          <table class="pt-table">
            <thead><tr><th>Invoice</th><th>Date</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th><th></th></tr></thead>
            <tbody>
              <tr v-for="b in bills" :key="b.invoice_id" :class="b.balance > 0 ? 'pt-row-warn' : ''">
                <td><strong>{{ b.invoice_number }}</strong></td>
                <td class="pt-muted-sm">{{ formatDate(b.invoice_date) }}</td>
                <td>KES {{ formatMoney(b.total_amount) }}</td>
                <td>KES {{ formatMoney(b.paid_amount) }}</td>
                <td><strong :class="b.balance > 0 ? 'pt-bad' : 'pt-good'">KES {{ formatMoney(b.balance) }}</strong></td>
                <td><span class="pt-pill" :class="payPill(b.payment_status)">{{ b.payment_status }}</span></td>
                <td>
                  <button v-if="b.balance > 0" class="pt-btn pt-btn-mpesa" @click="openPay(b)">
                    <i class="mdi mdi-cellphone"></i> Pay
                  </button>
                </td>
              </tr>
              <tr v-if="!bills.length"><td colspan="7" class="pt-empty">No bills.</td></tr>
            </tbody>
          </table>
        </div>

        <!-- ===== VISITS ===== -->
        <div v-if="tab==='visits'">
          <h2 class="pt-h2">Visit history</h2>
          <p class="pt-muted">Past consultations and admissions. Print a copy of any visit summary.</p>
          <table class="pt-table">
            <thead><tr><th>Date</th><th>Type</th><th>Visit #</th><th>Department</th><th>Doctor</th><th>Diagnoses</th><th>Status</th><th></th></tr></thead>
            <tbody>
              <tr v-for="v in visits" :key="v.visit_id">
                <td><strong>{{ formatDate(v.visit_date) }}</strong></td>
                <td><span class="pt-pill pt-pill-info">{{ v.visit_type }}</span></td>
                <td>{{ v.visit_number }}</td>
                <td>{{ v.department || '—' }}</td>
                <td>{{ v.doctor_name ? 'Dr. ' + v.doctor_name : '—' }}</td>
                <td class="pt-muted-sm">{{ v.diagnoses || '—' }}</td>
                <td><span class="pt-pill" :class="visitPill(v.visit_status)">{{ v.visit_status }}</span></td>
                <td><button class="pt-btn pt-btn-primary pt-btn-sm" @click="printVisit(v.visit_id)"><i class="mdi mdi-printer"></i> Summary</button></td>
              </tr>
              <tr v-if="!visits.length"><td colspan="8" class="pt-empty">No visits on record.</td></tr>
            </tbody>
          </table>
        </div>

        <!-- ===== CONSENTS ===== -->
        <div v-if="tab==='consents'">
          <h2 class="pt-h2">Consent forms</h2>
          <p class="pt-muted">Forms requiring your digital signature.</p>
          <div v-for="c in consents" :key="c.consent_id" class="pt-row pt-row-card flex-column align-items-start">
            <div class="d-flex justify-content-between w-100 align-items-start">
              <div>
                <strong>{{ c.consent_title }}</strong>
                <div class="pt-muted-sm">{{ c.consent_type }} · {{ formatDate(c.created_at) }}</div>
              </div>
              <span class="pt-pill" :class="consentPill(c.status)">{{ c.status }}</span>
            </div>
            <div class="pt-consent-text">{{ c.consent_text }}</div>
            <div v-if="c.status === 'signed'" class="pt-muted-sm mt-2">Signed {{ formatDt(c.signed_at) }}</div>
            <button v-else class="pt-btn pt-btn-primary mt-2" @click="signConsent(c)"><i class="mdi mdi-pen"></i> Sign now</button>
          </div>
          <div v-if="!consents.length" class="pt-empty">No consent forms pending.</div>
        </div>

        <!-- ===== EDUCATION ===== -->
        <div v-if="tab==='education'">
          <h2 class="pt-h2">Health information</h2>
          <p class="pt-muted">Articles and guidance from the hospital.</p>
          <div v-for="a in education" :key="a.content_id" class="pt-row pt-row-card flex-column align-items-start">
            <strong>{{ a.title }}</strong>
            <div class="pt-muted-sm">{{ a.category }}{{ a.language ? ' · ' + a.language : '' }} · {{ formatDate(a.created_at) }}</div>
            <div class="pt-edu-body mt-2">{{ a.content }}</div>
          </div>
          <div v-if="!education.length" class="pt-empty">No articles available.</div>
        </div>

        <!-- ===== PROFILE ===== -->
        <div v-if="tab==='profile'">
          <h2 class="pt-h2">Profile & preferences</h2>
          <div class="pt-form-card">
            <div class="row g-2">
              <div class="col-12 col-md-6"><label class="pt-label">Name</label><input :value="patient.name" disabled class="pt-input" /></div>
              <div class="col-12 col-md-6"><label class="pt-label">Patient #</label><input :value="patient.patient_number" disabled class="pt-input" /></div>
              <div class="col-12 col-md-6"><label class="pt-label">Phone</label><input :value="patient.phone" disabled class="pt-input" /></div>
              <div class="col-12 col-md-6"><label class="pt-label">Email</label><input v-model="profile.email" class="pt-input" /></div>
              <div class="col-12 col-md-6"><label class="pt-label">Next of kin name</label><input v-model="profile.next_of_kin_name" class="pt-input" /></div>
              <div class="col-12 col-md-6"><label class="pt-label">Next of kin phone</label><input v-model="profile.next_of_kin_phone" class="pt-input" /></div>
            </div>
            <button class="pt-btn pt-btn-primary mt-3" :disabled="profileSaving" @click="saveProfile">{{ profileSaving?'Saving…':'Save profile' }}</button>

            <div class="pt-section mt-4">
              <h3 class="pt-h3">Notifications</h3>
              <p class="pt-muted-sm">Stop receiving SMS reminders, results-ready notifications, and other messages from us.</p>
              <button class="pt-btn pt-btn-danger" @click="optOut('phone')">Opt out of SMS</button>
              <button v-if="patient.email" class="pt-btn pt-btn-danger ms-2" @click="optOut('email')">Opt out of email</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ⚠️ FLOATING CRISIS BUTTON — always available -->
    <button class="pt-crisis-fab" :class="{ 'pt-crisis-fab-mh': crisis.IsMHClient, 'pt-crisis-fab-high': crisis.RiskLevel === 'high' || crisis.RiskLevel === 'imminent' }" @click="openCrisis" title="Mental health help">
      <i class="mdi mdi-lifebuoy"></i>
      <span v-if="crisis.RiskLevel === 'high' || crisis.RiskLevel === 'imminent'" class="pt-crisis-pulse"></span>
    </button>

    <!-- CRISIS MODAL -->
    <div v-if="crisisModal" class="pt-modal-bg pt-crisis-bg" @click.self="closeCrisis">
      <div class="pt-modal pt-crisis-modal">
        <div class="pt-crisis-head">
          <h2><i class="mdi mdi-lifebuoy"></i> You're not alone</h2>
          <p>Whatever you're feeling right now, help is one tap away.</p>
          <button class="pt-icon-btn pt-crisis-close" @click="closeCrisis"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="pt-modal-body">

          <!-- Immediate danger -->
          <div class="pt-crisis-section pt-crisis-danger">
            <h3>🚨 If you are in immediate danger</h3>
            <a href="tel:1199" class="pt-crisis-btn pt-crisis-emergency" @click="logCrisis('called_emergency_1199')">
              <i class="mdi mdi-phone"></i>
              <div><strong>Call emergency 1199</strong><span>Kenya Red Cross — free, 24/7</span></div>
            </a>
            <a v-if="crisis.Facility.phone" :href="'tel:' + crisis.Facility.phone" class="pt-crisis-btn pt-crisis-hospital" @click="logCrisis('called_hospital')">
              <i class="mdi mdi-hospital-building"></i>
              <div><strong>Call {{ crisis.Facility.name }}</strong><span>{{ crisis.Facility.phone }}</span></div>
            </a>
          </div>

          <!-- Talk to someone -->
          <div class="pt-crisis-section">
            <h3>💬 Talk to someone now</h3>
            <a v-for="(h, i) in crisis.Hotlines" :key="i" :href="'tel:' + h.number" class="pt-crisis-btn pt-crisis-line" @click="logCrisis('called_' + (h.number === '0722178177' ? 'befrienders' : (h.number === '0900620200' ? 'niskize' : (h.number === '1199' ? 'emergency_1199' : 'hospital'))))">
              <i class="mdi mdi-phone-message"></i>
              <div><strong>{{ h.name }}</strong><span>{{ h.tagline }} · {{ h.number }}</span></div>
            </a>
          </div>

          <!-- Message care team -->
          <div class="pt-crisis-section">
            <h3>✉️ Send an urgent message to your care team</h3>
            <p class="pt-muted-sm">Reach out to our mental health team. They'll respond as soon as possible.</p>
            <textarea v-model="crisisMessage" class="pt-input" rows="3" placeholder="Tell us what you're feeling… (optional)"></textarea>
            <button class="pt-btn pt-btn-primary mt-2" :disabled="crisisSending" @click="sendCrisisMsg">
              <i class="mdi mdi-message-alert"></i> {{ crisisSending?'Sending…':'Send urgent message' }}
            </button>
          </div>

          <!-- Personal supporter -->
          <div v-if="crisis.Supporter && crisis.Supporter.phone" class="pt-crisis-section pt-crisis-supporter">
            <h3>👥 Your treatment supporter</h3>
            <p>{{ crisis.Supporter.name }}</p>
            <a :href="'tel:' + crisis.Supporter.phone" class="pt-crisis-btn pt-crisis-line" @click="logCrisis('called_hospital')">
              <i class="mdi mdi-account-heart"></i>
              <div><strong>Call {{ crisis.Supporter.name }}</strong><span>{{ crisis.Supporter.phone }}</span></div>
            </a>
          </div>

          <!-- Safety plan -->
          <div v-if="crisis.SafetyPlan" class="pt-crisis-section pt-crisis-plan">
            <h3>📋 Your safety plan</h3>
            <p class="pt-crisis-plan-text">{{ crisis.SafetyPlan }}</p>
            <p v-if="crisis.CurrentMeds" class="pt-muted-sm"><strong>Current medication:</strong> {{ crisis.CurrentMeds }}</p>
          </div>

          <!-- Self-help reminders -->
          <div class="pt-crisis-section">
            <h3>🌿 Grounding & self-help</h3>
            <ul class="pt-crisis-tips">
              <li><strong>5-4-3-2-1 grounding:</strong> Name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, 1 you taste.</li>
              <li><strong>Box breathing:</strong> 4 seconds in · 4 seconds hold · 4 seconds out · 4 seconds hold. Repeat 4 times.</li>
              <li><strong>Cold water:</strong> Splash cold water on your face — activates the dive reflex and calms the nervous system.</li>
              <li><strong>Reach out:</strong> Even a short message to one trusted person can change everything.</li>
            </ul>
          </div>

          <div class="pt-crisis-footer">
            <p>This conversation is private and saved to your record so your care team can follow up. You are valued. You matter.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- DOCUMENT UPLOAD MODAL -->
    <div v-if="docUploadModal" class="pt-modal-bg" @click.self="docUploadModal=false">
      <div class="pt-modal" style="width:560px">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0"><i class="mdi mdi-cloud-upload"></i> {{ docForm.document_id ? 'Edit document' : 'Upload document' }}</h3><button class="pt-icon-btn" @click="docUploadModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">

          <label class="pt-label">Title *</label>
          <input v-model="docForm.title" class="pt-input" placeholder="e.g. National ID, SHA Card, X-ray from Bomet Hospital" />

          <label class="pt-label mt-2">Category</label>
          <select v-model="docForm.category" class="pt-input">
            <option v-for="c in docCategoriesAll" :key="c.value" :value="c.value">{{ c.label }}</option>
          </select>

          <label class="pt-label mt-2">Notes (optional)</label>
          <textarea v-model="docForm.notes" class="pt-input" rows="2" placeholder="Anything your doctor should know about this document?"></textarea>

          <label class="pt-label mt-2">
            <input type="checkbox" v-model="docForm.visible_to_clinicians" :true-value="1" :false-value="0" />
            Allow my care team to view this
          </label>
          <div class="pt-muted-sm">If unchecked, only you can see this document.</div>

          <template v-if="!docForm.document_id">
            <label class="pt-label mt-3">File *</label>
            <input type="file" @change="onDocFile" class="pt-input"
                   accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.tif,.tiff,.doc,.docx,.txt" />
            <div v-if="docForm.file_name" class="pt-muted-sm mt-1">
              📎 {{ docForm.file_name }} <span v-if="docForm.file_size_kb">({{ docForm.file_size_kb }} KB)</span>
            </div>
            <div class="pt-muted-sm">Max 15 MB. Allowed: PDF, JPG, PNG, GIF, WebP, HEIC, TIFF, DOC/DOCX, TXT.</div>
          </template>

        </div>
        <div class="pt-modal-foot">
          <button class="pt-btn pt-btn-link" @click="docUploadModal=false">Cancel</button>
          <button class="pt-btn pt-btn-primary" :disabled="!canSubmitDoc || sending" @click="submitDoc">{{ sending?'Uploading…':(docForm.document_id?'Save':'Upload') }}</button>
        </div>
      </div>
    </div>

    <!-- DOCUMENT VIEWER MODAL -->
    <div v-if="docViewModal" class="pt-modal-bg" @click.self="docViewModal=false">
      <div class="pt-modal pt-doc-viewer">
        <div class="pt-modal-head">
          <h3 class="pt-h3 m-0">{{ docView.title }}</h3>
          <div>
            <a v-if="docView.dataUrl" :href="docView.dataUrl" :download="docView.file_name" class="pt-btn pt-btn-link"><i class="mdi mdi-download"></i> Download</a>
            <button class="pt-icon-btn" @click="docViewModal=false"><i class="mdi mdi-close"></i></button>
          </div>
        </div>
        <div class="pt-modal-body pt-doc-view-body">
          <div v-if="docView.loading" class="pt-empty">Loading…</div>
          <img v-else-if="docView.isImage" :src="docView.dataUrl" alt="" class="pt-doc-img" />
          <iframe v-else-if="docView.isPdf" :src="docView.dataUrl" class="pt-doc-iframe"></iframe>
          <div v-else class="pt-empty">
            <i class="mdi mdi-file" style="font-size:64px"></i>
            <p>{{ docView.file_name }}</p>
            <p class="pt-muted-sm">{{ docView.file_type }} · {{ formatBytes(docView.file_size_bytes) }}</p>
            <a :href="docView.dataUrl" :download="docView.file_name" class="pt-btn pt-btn-primary mt-2"><i class="mdi mdi-download"></i> Download to view</a>
          </div>
          <div v-if="docView.notes" class="pt-doc-notes">
            <strong>Notes:</strong> {{ docView.notes }}
          </div>
        </div>
      </div>
    </div>

    <!-- WEARABLE CONNECT MODAL -->
    <div v-if="wearModal" class="pt-modal-bg" @click.self="wearModal=false">
      <div class="pt-modal" style="width:560px">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0">Connect {{ vendorLabel(wearVendor) }}</h3><button class="pt-icon-btn" @click="wearModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">

          <template v-if="wearVendor === 'apple_health'">
            <h4>Apple Health export</h4>
            <ol class="pt-wear-steps">
              <li>Open the <strong>Health</strong> app on your iPhone</li>
              <li>Tap your <strong>profile picture</strong> (top right)</li>
              <li>Scroll down → <strong>Export All Health Data</strong></li>
              <li>Wait for the ZIP to be created (1-3 minutes)</li>
              <li>Open the ZIP, then upload <code>export.xml</code> below</li>
            </ol>
            <label class="pt-label mt-2">Upload export.xml</label>
            <input type="file" accept=".xml,application/xml" @change="onAppleFile" class="pt-input" />
            <div v-if="wearXmlSize" class="pt-muted-sm mt-1">{{ wearXmlSize }} loaded — last 90 days will be imported.</div>
          </template>

          <template v-else>
            <p class="pt-muted">{{ vendorLabel(wearVendor) }} OAuth requires the hospital admin to register an app with the vendor and store the credentials.</p>
            <p class="pt-muted">For now, you can:</p>
            <ul class="pt-muted">
              <li>Manually export readings from the {{ vendorLabel(wearVendor) }} app and upload as JSON</li>
              <li>Use the <em>Demo simulate</em> button to populate test data</li>
            </ul>
            <label class="pt-label mt-2">Device label (optional)</label>
            <input v-model="wearDeviceName" class="pt-input" :placeholder="defaultDeviceName(wearVendor)" />
            <p class="pt-muted-sm mt-2"><i class="mdi mdi-information-outline"></i> Once OAuth is configured at the hospital level, this will become a one-tap "Authorize" flow.</p>
          </template>

        </div>
        <div class="pt-modal-foot">
          <button class="pt-btn pt-btn-link" @click="wearModal=false">Cancel</button>
          <button v-if="wearVendor === 'apple_health'" class="pt-btn pt-btn-primary" :disabled="!wearXml || sending" @click="uploadApple">{{ sending?'Importing…':'Import' }}</button>
          <button v-else class="pt-btn pt-btn-primary" :disabled="sending" @click="connectGeneric">Mark as connected</button>
        </div>
      </div>
    </div>

    <!-- MED REMINDER SETUP MODAL -->
    <div v-if="reminderModal" class="pt-modal-bg" @click.self="reminderModal=false">
      <div class="pt-modal" style="width:480px">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0"><i class="mdi mdi-bell"></i> Reminders for {{ remForm.drug_name }}</h3><button class="pt-icon-btn" @click="reminderModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">
          <p class="pt-muted-sm">{{ remForm.dosage }} · {{ remForm.frequency }}</p>

          <label class="pt-label mt-2"><input type="checkbox" v-model="remForm.is_enabled" :true-value="1" :false-value="0" /> Send me SMS reminders</label>

          <label class="pt-label mt-3">Reminder times</label>
          <div class="pt-rem-times">
            <div v-for="(t, i) in remForm.schedule_times" :key="i" class="pt-rem-time-row">
              <input v-model="remForm.schedule_times[i]" type="time" class="pt-input" />
              <button class="pt-btn pt-btn-link" @click="remForm.schedule_times.splice(i, 1)" v-if="remForm.schedule_times.length > 1"><i class="mdi mdi-trash-can"></i></button>
            </div>
            <button class="pt-btn pt-btn-secondary pt-btn-sm" @click="remForm.schedule_times.push('20:00')"><i class="mdi mdi-plus"></i> Add another time</button>
          </div>
          <div class="pt-muted-sm mt-1">Tip: TLD is once daily (e.g. 08:00). BD meds need 2 times. TDS needs 3.</div>

          <label class="pt-label mt-3">Send via</label>
          <select v-model="remForm.notify_via" class="pt-input">
            <option value="sms">SMS</option>
            <option value="email">Email</option>
            <option value="both">SMS + Email</option>
          </select>
        </div>
        <div class="pt-modal-foot">
          <button class="pt-btn pt-btn-link" @click="reminderModal=false">Cancel</button>
          <button class="pt-btn pt-btn-primary" :disabled="sending" @click="saveReminder">{{ sending?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- TELEHEALTH REQUEST MODAL -->
    <div v-if="thModal" class="pt-modal-bg" @click.self="thModal=false">
      <div class="pt-modal" style="width:520px">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0"><i class="mdi mdi-video-plus"></i> Request video consultation</h3><button class="pt-icon-btn" @click="thModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">

          <div v-if="thStep === 'form'">
            <div class="pt-th-disclaimer">
              <i class="mdi mdi-information-outline"></i>
              <span>For genuine emergencies (chest pain, severe bleeding, suicidal thoughts) call <strong>1199</strong> instead.</span>
            </div>

            <label class="pt-label mt-2">What do you need help with?</label>
            <textarea v-model="thForm.chief_complaint" class="pt-input" rows="3" placeholder="Describe your concern in your own words…"></textarea>

            <div class="row g-2 mt-2">
              <div class="col-6">
                <label class="pt-label">Specialty (optional)</label>
                <select v-model="thForm.specialty" class="pt-input">
                  <option value="general">General — any clinician</option>
                  <option value="Internal Medicine">Internal Medicine</option>
                  <option value="Paediatrics">Paediatrics</option>
                  <option value="OB/GYN">OB/GYN</option>
                  <option value="Mental Health">Mental Health</option>
                  <option value="Dermatology">Dermatology</option>
                  <option value="ENT">ENT</option>
                  <option value="ART (HIV)">ART (HIV)</option>
                  <option value="TB clinic">TB clinic</option>
                </select>
              </div>
              <div class="col-6">
                <label class="pt-label">Mode</label>
                <select v-model="thForm.mode" class="pt-input">
                  <option value="video">Video call</option>
                  <option value="audio">Voice call only</option>
                  <option value="chat">Chat (text)</option>
                </select>
              </div>
            </div>

            <label class="pt-label mt-2">When?</label>
            <div class="pt-th-when">
              <label :class="{ active: thForm.preferred_time === 'now' }">
                <input type="radio" value="now" v-model="thForm.preferred_time" />
                <strong>As soon as possible</strong>
                <span>~10-30 min wait</span>
              </label>
              <label :class="{ active: thForm.preferred_time === 'today' }">
                <input type="radio" value="today" v-model="thForm.preferred_time" />
                <strong>Sometime today</strong>
                <span>Late afternoon</span>
              </label>
              <label :class="{ active: thForm.preferred_time === 'specific' }">
                <input type="radio" value="specific" v-model="thForm.preferred_time" />
                <strong>Specific time</strong>
                <span>Pick below</span>
              </label>
            </div>
            <input v-if="thForm.preferred_time === 'specific'" v-model="thForm.specific_time" type="datetime-local" class="pt-input mt-2" />

            <label class="pt-label mt-2">Urgency</label>
            <select v-model="thForm.urgency" class="pt-input">
              <option value="routine">Routine</option>
              <option value="urgent">Urgent — same day</option>
            </select>
          </div>

          <div v-if="thStep === 'submitting'" class="pt-pay-status">
            <div class="pt-spinner"></div>
            <h4>Sending request…</h4>
          </div>

          <div v-if="thStep === 'success'" class="pt-pay-status pt-pay-success">
            <i class="mdi mdi-check-circle"></i>
            <h4>Request received</h4>
            <p>{{ thResult.message }}</p>
            <p>Reference: <strong>{{ thResult.document_no }}</strong></p>
            <p class="pt-muted-sm">A clinician will accept shortly. Check the Telehealth tab for status updates.</p>
          </div>
        </div>
        <div class="pt-modal-foot">
          <button v-if="thStep === 'form'" class="pt-btn pt-btn-link" @click="thModal=false">Cancel</button>
          <button v-if="thStep === 'form'" class="pt-btn pt-btn-primary" :disabled="!thForm.chief_complaint || sending" @click="submitTelehealth">{{ sending?'Sending…':'Send request' }}</button>
          <button v-if="thStep === 'success'" class="pt-btn pt-btn-primary" @click="thModal=false">Done</button>
        </div>
      </div>
    </div>

    <!-- ADD FAMILY MEMBER MODAL -->
    <div v-if="famModal" class="pt-modal-bg" @click.self="famModal=false">
      <div class="pt-modal" style="width:520px">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0"><i class="mdi mdi-account-plus"></i> Add family member</h3><button class="pt-icon-btn" @click="famModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">

          <div class="pt-fam-tabs">
            <button class="pt-fam-tab" :class="{ active: famForm.mode==='create_minor' }" @click="famForm.mode='create_minor'">
              <i class="mdi mdi-baby-face-outline"></i> My child (under 18)
            </button>
            <button class="pt-fam-tab" :class="{ active: famForm.mode==='link_existing' }" @click="famForm.mode='link_existing'">
              <i class="mdi mdi-account-search"></i> Existing patient
            </button>
          </div>

          <!-- CREATE MINOR -->
          <template v-if="famForm.mode==='create_minor'">
            <p class="pt-muted-sm">Auto-approved for parents/guardians of minors. Access expires when child turns 18.</p>
            <div class="row g-2 mt-2">
              <div class="col-6"><label class="pt-label">First name *</label><input v-model="famForm.first_name" class="pt-input" /></div>
              <div class="col-6"><label class="pt-label">Last name</label><input v-model="famForm.last_name" class="pt-input" :placeholder="patient.name && patient.name.split(' ').pop()" /></div>
              <div class="col-6"><label class="pt-label">Date of birth *</label><input v-model="famForm.dob" type="date" class="pt-input" :max="maxChildDob" /></div>
              <div class="col-6"><label class="pt-label">Gender</label>
                <select v-model="famForm.gender" class="pt-input">
                  <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                </select>
              </div>
              <div class="col-12"><label class="pt-label">Relationship</label>
                <select v-model="famForm.relationship" class="pt-input">
                  <option value="parent_of_minor">I am the parent</option>
                  <option value="guardian">I am the legal guardian</option>
                </select>
              </div>
            </div>
          </template>

          <!-- LINK EXISTING -->
          <template v-if="famForm.mode==='link_existing'">
            <p class="pt-muted-sm">Request access to an adult family member's record. The hospital will verify within 24 hours.</p>
            <label class="pt-label mt-2">Patient #</label>
            <input v-model="famForm.patient_no" class="pt-input" placeholder="PAT11012605000XXX" />
            <label class="pt-label mt-2">Their phone (for verification)</label>
            <input v-model="famForm.verify_phone" class="pt-input" placeholder="0722XXXXXX" />
            <label class="pt-label mt-2">Relationship</label>
            <select v-model="famForm.relationship" class="pt-input">
              <option value="spouse">Spouse</option>
              <option value="adult_child">Adult child</option>
              <option value="parent_of_minor">Parent</option>
              <option value="sibling">Sibling</option>
              <option value="caregiver">Caregiver</option>
              <option value="dependant">Dependant</option>
              <option value="other">Other</option>
            </select>
          </template>

          <label class="pt-label mt-3">Permissions</label>
          <div class="pt-perms">
            <label class="pt-perm-row" v-for="p in availablePermissions" :key="p.value">
              <input type="checkbox" :value="p.value" v-model="famForm.permList" />
              <span><strong>{{ p.label }}</strong> — {{ p.desc }}</span>
            </label>
          </div>

        </div>
        <div class="pt-modal-foot">
          <button class="pt-btn pt-btn-link" @click="famModal=false">Cancel</button>
          <button class="pt-btn pt-btn-primary" :disabled="famSaving" @click="addFamily">{{ famSaving?'Saving…':'Add' }}</button>
        </div>
      </div>
    </div>

    <!-- DIARY LOG MODAL -->
    <div v-if="diaryModal" class="pt-modal-bg" @click.self="diaryModal=false">
      <div class="pt-modal">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0">{{ diaryTitle }}</h3><button class="pt-icon-btn" @click="diaryModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">

          <label class="pt-label">When (defaults to now)</label>
          <input v-model="diaryForm.logged_at" type="datetime-local" class="pt-input" />

          <template v-if="diaryType==='bp'">
            <div class="row g-2 mt-2">
              <div class="col-4"><label class="pt-label">Systolic</label><input v-model.number="diaryForm.bp_systolic" type="number" class="pt-input" placeholder="120" /></div>
              <div class="col-4"><label class="pt-label">Diastolic</label><input v-model.number="diaryForm.bp_diastolic" type="number" class="pt-input" placeholder="80" /></div>
              <div class="col-4"><label class="pt-label">Pulse</label><input v-model.number="diaryForm.pulse" type="number" class="pt-input" placeholder="72" /></div>
            </div>
          </template>

          <template v-if="diaryType==='glucose'">
            <label class="pt-label mt-2">Blood glucose (mmol/L)</label>
            <input v-model.number="diaryForm.blood_glucose" type="number" step="0.1" class="pt-input" placeholder="5.5" />
            <label class="pt-label mt-2">Context</label>
            <select v-model="diaryForm.glucose_context" class="pt-input">
              <option value="fasting">Fasting</option>
              <option value="before_meal">Before meal</option>
              <option value="after_meal">After meal (postprandial)</option>
              <option value="bedtime">Bedtime</option>
              <option value="random">Random</option>
            </select>
          </template>

          <template v-if="diaryType==='weight'">
            <div class="row g-2 mt-2">
              <div class="col-6"><label class="pt-label">Weight (kg)</label><input v-model.number="diaryForm.weight_kg" type="number" step="0.1" class="pt-input" /></div>
              <div class="col-6"><label class="pt-label">Height (cm)</label><input v-model.number="diaryForm.height_cm" type="number" step="0.1" class="pt-input" /></div>
            </div>
            <div class="pt-muted-sm">BMI auto-calculated if both filled.</div>
          </template>

          <template v-if="diaryType==='mood'">
            <label class="pt-label mt-2">Mood today (1 = lowest, 10 = best)</label>
            <input v-model.number="diaryForm.mood_score" type="range" min="1" max="10" class="pt-input pt-range" />
            <div class="pt-mood-display">{{ diaryForm.mood_score }}/10 — {{ moodEmoji(diaryForm.mood_score) }}</div>
            <div class="row g-2 mt-2">
              <div class="col-6"><label class="pt-label">Sleep hours</label><input v-model.number="diaryForm.sleep_hours" type="number" step="0.5" class="pt-input" /></div>
              <div class="col-6"><label class="pt-label">Sleep quality</label>
                <select v-model="diaryForm.sleep_quality" class="pt-input"><option value="">—</option><option value="excellent">Excellent</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option><option value="none">None</option></select>
              </div>
            </div>
          </template>

          <template v-if="diaryType==='other'">
            <div class="row g-2 mt-2">
              <div class="col-6"><label class="pt-label">Temp (°C)</label><input v-model.number="diaryForm.temperature_c" type="number" step="0.1" class="pt-input" /></div>
              <div class="col-6"><label class="pt-label">SpO2 (%)</label><input v-model.number="diaryForm.oxygen_sat" type="number" class="pt-input" /></div>
              <div class="col-6"><label class="pt-label">Steps</label><input v-model.number="diaryForm.steps" type="number" class="pt-input" /></div>
              <div class="col-6"><label class="pt-label">Pain (0-10)</label><input v-model.number="diaryForm.pain_score" type="number" min="0" max="10" class="pt-input" /></div>
              <div class="col-12"><label class="pt-label">Symptom</label><textarea v-model="diaryForm.symptom_text" class="pt-input" rows="2"></textarea></div>
            </div>
          </template>

          <label class="pt-label mt-2">Notes (optional)</label>
          <textarea v-model="diaryForm.notes" class="pt-input" rows="2" placeholder="Anything to flag for your doctor?"></textarea>
          <div class="mt-2"><label><input type="checkbox" v-model="diaryForm.flagged_for_review" :true-value="1" :false-value="0" /> Flag this entry for my doctor</label></div>

        </div>
        <div class="pt-modal-foot">
          <button class="pt-btn pt-btn-link" @click="diaryModal=false">Cancel</button>
          <button class="pt-btn pt-btn-primary" :disabled="sending" @click="saveLog">{{ sending?'Saving…':'Save entry' }}</button>
        </div>
      </div>
    </div>

    <!-- M-PESA PAY MODAL -->
    <div v-if="payModal" class="pt-modal-bg" @click.self="closePay">
      <div class="pt-modal pt-mpesa-modal">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0"><i class="mdi mdi-cellphone"></i> Pay with M-PESA</h3><button class="pt-icon-btn" @click="closePay"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">

          <div v-if="payStep === 'form'">
            <div class="pt-pay-summary">
              <div><strong>Invoice:</strong> {{ payInvoice.invoice_number }}</div>
              <div><strong>Total:</strong> KES {{ formatMoney(payInvoice.total_amount) }}</div>
              <div><strong>Paid:</strong> KES {{ formatMoney(payInvoice.paid_amount) }}</div>
              <div class="pt-pay-balance"><strong>Outstanding:</strong> <span class="pt-bad">KES {{ formatMoney(payInvoice.balance) }}</span></div>
            </div>
            <label class="pt-label mt-3">Amount (KES)</label>
            <input v-model.number="payAmount" type="number" :max="payInvoice.balance" min="1" class="pt-input" />
            <div class="pt-muted-sm">You can pay any amount up to the balance.</div>
            <label class="pt-label mt-3">M-PESA phone</label>
            <input v-model="payPhone" class="pt-input" placeholder="0722111021" />
            <div class="pt-muted-sm">An STK prompt will be sent to this number.</div>
          </div>

          <div v-if="payStep === 'pushing'" class="pt-pay-status">
            <div class="pt-spinner"></div>
            <h4>Sending STK push…</h4>
            <p class="pt-muted-sm">Connecting to Safaricom</p>
          </div>

          <div v-if="payStep === 'awaiting'" class="pt-pay-status">
            <div class="pt-pay-phone">
              <i class="mdi mdi-cellphone-message"></i>
            </div>
            <h4>Check your phone</h4>
            <p>Enter your M-PESA PIN to pay <strong>KES {{ formatMoney(payAmount) }}</strong>.</p>
            <p class="pt-muted-sm">{{ payPhone }} · waiting for payment…</p>
            <div class="pt-pay-timer">{{ payCountdown }}s</div>
          </div>

          <div v-if="payStep === 'success'" class="pt-pay-status pt-pay-success">
            <i class="mdi mdi-check-circle"></i>
            <h4>Payment successful</h4>
            <p>KES {{ formatMoney(payAmount) }} received</p>
            <div class="pt-pay-receipt">Receipt: <strong>{{ payReceipt }}</strong></div>
          </div>

          <div v-if="payStep === 'failed'" class="pt-pay-status pt-pay-failed">
            <i class="mdi mdi-close-circle"></i>
            <h4>Payment failed</h4>
            <p>{{ payError || 'The transaction did not complete.' }}</p>
          </div>

        </div>
        <div class="pt-modal-foot">
          <button v-if="payStep === 'form'" class="pt-btn pt-btn-link" @click="closePay">Cancel</button>
          <button v-if="payStep === 'form'" class="pt-btn pt-btn-mpesa" :disabled="!payAmount || !payPhone || sending" @click="initiatePay">
            <i class="mdi mdi-cellphone"></i> Send STK push
          </button>
          <button v-if="payStep === 'success' || payStep === 'failed'" class="pt-btn pt-btn-primary" @click="closePay">Close</button>
          <button v-if="payStep === 'failed'" class="pt-btn pt-btn-link" @click="payStep='form'">Try again</button>
        </div>
      </div>
    </div>

    <!-- NEW MESSAGE MODAL -->
    <div v-if="newMsgModal" class="pt-modal-bg" @click.self="newMsgModal=false">
      <div class="pt-modal">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0">New message</h3><button class="pt-icon-btn" @click="newMsgModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">
          <label class="pt-label">Send to</label>
          <select v-model="newMsgForm.target_role" class="pt-input">
            <option value="general">General — Hospital reception</option>
            <option value="doctor">Doctor — clinical question</option>
            <option value="nurse">Nurse</option>
            <option value="pharmacy">Pharmacy — refill / drug question</option>
            <option value="billing">Billing — invoices, SHA</option>
            <option value="reception">Reception — appointment changes</option>
            <option value="lab">Laboratory — results query</option>
            <option value="art_clinic">ART (CCC) clinic</option>
            <option value="tb_clinic">TB clinic</option>
            <option value="maternity">Maternity</option>
            <option value="mental_health">Mental Health support</option>
          </select>
          <label class="pt-label mt-2">Priority</label>
          <select v-model="newMsgForm.priority" class="pt-input">
            <option value="low">Low — non-urgent</option>
            <option value="normal">Normal</option>
            <option value="high">High — please respond today</option>
            <option value="urgent">Urgent — for emergencies call instead</option>
          </select>
          <label class="pt-label mt-2">Subject</label>
          <input v-model="newMsgForm.subject" class="pt-input" placeholder="Short subject" />
          <label class="pt-label mt-2">Message</label>
          <textarea v-model="newMsgForm.body" class="pt-input" rows="5" placeholder="Type your question or request…"></textarea>
          <div class="pt-muted-sm mt-1"><i class="mdi mdi-shield-lock"></i> Messages are private between you and our team. For emergencies, call instead.</div>
        </div>
        <div class="pt-modal-foot">
          <button class="pt-btn pt-btn-link" @click="newMsgModal=false">Cancel</button>
          <button class="pt-btn pt-btn-primary" :disabled="!newMsgForm.subject || !newMsgForm.body || sending" @click="sendNewMsg">{{ sending ? 'Sending…' : 'Send' }}</button>
        </div>
      </div>
    </div>

    <!-- BOOK MODAL -->
    <div v-if="bookModal" class="pt-modal-bg" @click.self="bookModal=false">
      <div class="pt-modal">
        <div class="pt-modal-head"><h3 class="pt-h3 m-0">Book appointment</h3><button class="pt-icon-btn" @click="bookModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="pt-modal-body">
          <label class="pt-label">Date *</label>
          <input v-model="newAppt.appointment_date" type="date" :min="todayStr" class="pt-input" />
          <label class="pt-label mt-2">Time</label>
          <input v-model="newAppt.appointment_time" type="time" class="pt-input" />
          <label class="pt-label mt-2">Department</label>
          <input v-model="newAppt.department" class="pt-input" placeholder="e.g. Outpatient, Antenatal" />
          <label class="pt-label mt-2">Reason</label>
          <textarea v-model="newAppt.reason" class="pt-input" rows="2"></textarea>
        </div>
        <div class="pt-modal-foot">
          <button class="pt-btn pt-btn-link" @click="bookModal=false">Cancel</button>
          <button class="pt-btn pt-btn-primary" :disabled="!newAppt.appointment_date || booking" @click="submitBook">{{ booking?'Booking…':'Confirm' }}</button>
        </div>
      </div>
    </div>

    <!-- FLOATING QUICK-EXIT (always visible — IPV safety feature) -->
    <button class="pt-quickexit" @click="quickExit" title="Quickly hide this app and clear history">
      <i class="mdi mdi-exit-run"></i>
      <span>Quick Exit</span>
    </button>
  </div>
</template>

<script>
import axios from "axios";
import { hms } from "@/api";

const api = (params) => {
  const token = localStorage.getItem('patientPortalToken') || '';
  const activePid = localStorage.getItem('activeProfileId');
  const merged = activePid ? { active_patient_id: activePid, ...params } : params;
  return axios.post(hms, merged, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
};

const Spark = {
  name: 'Spark',
  props: { data: { type: Array, default: () => [] }, color: { default: '#2563eb' } },
  computed: {
    points() {
      const d = (this.data || []).filter(v => v != null && !isNaN(v)).map(v => +v);
      if (d.length === 0) return '';
      const max = Math.max(...d), min = Math.min(...d);
      const range = max - min || 1;
      const w = 200, h = 30;
      return d.map((v, i) => {
        const x = d.length === 1 ? w/2 : (i * w / (d.length - 1));
        const y = h - ((v - min) / range) * (h - 4) - 2;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ');
    }
  },
  template: `
    <svg v-if="data && data.length" width="100%" height="34" viewBox="0 0 200 30" preserveAspectRatio="none" class="pt-spark">
      <polyline :points="points" fill="none" :stroke="color" stroke-width="1.5" stroke-linejoin="round" />
    </svg>
  `
};

const GrowthChart = {
  name: 'GrowthChart',
  props: { title: String, unit: String, measurements: { type: Array, default: () => [] }, reference: { type: Array, default: () => [] }, ageMonths: Number, color: { default: '#2563eb' } },
  computed: {
    bounds() {
      // x = age in months; y = unit value
      const ref = this.reference || [];
      const meas = this.measurements || [];
      if (!ref.length) return null;
      const ages = ref.map(r => r[0]);
      const xMax = Math.max(...ages, this.ageMonths + 6);
      const xMin = 0;
      const allY = ref.flatMap(r => [r[1], r[3]]).concat(meas.map(m => +m.value || 0));
      const yMin = Math.min(...allY) * 0.92;
      const yMax = Math.max(...allY) * 1.08;
      return { xMin, xMax, yMin, yMax, w: 500, h: 240, pad: { l: 36, r: 12, t: 12, b: 28 } };
    },
    refPath() {
      const b = this.bounds; if (!b) return { p3: '', p50: '', p97: '' };
      const x = a => b.pad.l + (a - b.xMin) / (b.xMax - b.xMin) * (b.w - b.pad.l - b.pad.r);
      const y = v => b.pad.t + (1 - (v - b.yMin) / (b.yMax - b.yMin)) * (b.h - b.pad.t - b.pad.b);
      const path = idx => this.reference.map((r, i) => `${i===0?'M':'L'}${x(r[0]).toFixed(1)},${y(r[idx]).toFixed(1)}`).join(' ');
      return { p3: path(1), p50: path(2), p97: path(3) };
    },
    refBand() {
      const b = this.bounds; if (!b) return '';
      const x = a => b.pad.l + (a - b.xMin) / (b.xMax - b.xMin) * (b.w - b.pad.l - b.pad.r);
      const y = v => b.pad.t + (1 - (v - b.yMin) / (b.yMax - b.yMin)) * (b.h - b.pad.t - b.pad.b);
      const top = this.reference.map(r => `${x(r[0]).toFixed(1)},${y(r[3]).toFixed(1)}`).join(' ');
      const bot = [...this.reference].reverse().map(r => `${x(r[0]).toFixed(1)},${y(r[1]).toFixed(1)}`).join(' ');
      return `M${top.replace(/ /g, ' L')} L${bot.replace(/ /g, ' L')} Z`;
    },
    points() {
      const b = this.bounds; if (!b) return [];
      const x = a => b.pad.l + (a - b.xMin) / (b.xMax - b.xMin) * (b.w - b.pad.l - b.pad.r);
      const y = v => b.pad.t + (1 - (v - b.yMin) / (b.yMax - b.yMin)) * (b.h - b.pad.t - b.pad.b);
      return this.measurements.map(m => ({ x: x(+m.age), y: y(+m.value), age: m.age, value: m.value, at: m.at }));
    },
    measPath() { return this.points.map((p, i) => `${i===0?'M':'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '); },
    xLabels() {
      const b = this.bounds; if (!b) return [];
      const step = b.xMax > 60 ? 24 : 12;
      const labels = [];
      for (let a = 0; a <= b.xMax; a += step) {
        const x = b.pad.l + (a - b.xMin) / (b.xMax - b.xMin) * (b.w - b.pad.l - b.pad.r);
        labels.push({ x: x.toFixed(1), label: a >= 24 ? (a/12).toFixed(0) + 'y' : a + 'm' });
      }
      return labels;
    },
    yLabels() {
      const b = this.bounds; if (!b) return [];
      const labels = [];
      const range = b.yMax - b.yMin;
      const step = range > 100 ? 20 : range > 50 ? 10 : range > 20 ? 5 : range > 10 ? 2 : 1;
      for (let v = Math.ceil(b.yMin / step) * step; v <= b.yMax; v += step) {
        const y = b.pad.t + (1 - (v - b.yMin) / (b.yMax - b.yMin)) * (b.h - b.pad.t - b.pad.b);
        labels.push({ y: y.toFixed(1), label: Number.isInteger(v) ? v : v.toFixed(1) });
      }
      return labels;
    }
  },
  template: `
    <div class="pt-growth-card">
      <h3 class="pt-h3">{{ title }} <span class="pt-muted-sm">({{ unit }})</span></h3>
      <svg v-if="bounds" :viewBox="'0 0 ' + bounds.w + ' ' + bounds.h" width="100%" height="240" class="pt-growth-svg">
        <path :d="refBand" fill="#fef3c7" fill-opacity="0.55" />
        <path :d="refPath.p3"  stroke="#fbbf24" stroke-width="1" stroke-dasharray="3 3" fill="none" />
        <path :d="refPath.p50" stroke="#94a3b8" stroke-width="1.5" fill="none" />
        <path :d="refPath.p97" stroke="#fbbf24" stroke-width="1" stroke-dasharray="3 3" fill="none" />
        <path :d="measPath" :stroke="color" stroke-width="2.5" fill="none" stroke-linejoin="round" />
        <circle v-for="(p,i) in points" :key="i" :cx="p.x" :cy="p.y" r="4" :fill="color" stroke="#fff" stroke-width="1.5">
          <title>{{ p.value }} {{ unit }} at {{ Math.round(p.age) }} months</title>
        </circle>
        <g class="pt-growth-axis">
          <text v-for="(l,i) in xLabels" :key="'x'+i" :x="l.x" :y="bounds.h - 8" text-anchor="middle">{{ l.label }}</text>
          <text v-for="(l,i) in yLabels" :key="'y'+i" :x="bounds.pad.l - 6" :y="l.y + 4" text-anchor="end">{{ l.label }}</text>
          <line :x1="bounds.pad.l" :y1="bounds.h - bounds.pad.b" :x2="bounds.w - bounds.pad.r" :y2="bounds.h - bounds.pad.b" stroke="#cbd5e1" stroke-width="0.8" />
          <line :x1="bounds.pad.l" :y1="bounds.pad.t" :x2="bounds.pad.l" :y2="bounds.h - bounds.pad.b" stroke="#cbd5e1" stroke-width="0.8" />
        </g>
      </svg>
      <div class="pt-growth-legend">
        <span class="pt-leg-dot" :style="{ background: color }"></span> Your child
        <span class="pt-leg-line pt-leg-50"></span> WHO median (50th)
        <span class="pt-leg-band"></span> 3rd–97th percentile range
      </div>
    </div>
  `
};

export default {
  name: "PatientSelfService",
  components: { Spark, GrowthChart },
  data() {
    return {
      tab: 'home', booking: false, profileSaving: false, bookModal: false,
      home: {}, patient: {},
      appointments: [], results: [], prescriptions: [], immunizations: [], telehealth: [], referrals: [], bills: [],
      visits: [], consents: [], education: [],
      threads: [], activeThread: null, activeMessages: [], replyBody: '', sending: false,
      newMsgModal: false, newMsgForm: { target_role: 'general', priority: 'normal', subject: '', body: '' },
      messagePoll: null,
      payModal: false, payStep: 'form', payInvoice: {}, payAmount: 0, payPhone: '',
      payCheckoutId: '', payReceipt: '', payError: '', payCountdown: 60, payPoll: null, payCountdownTimer: null,
      sym: { history: [], active: false, assessmentId: null, messages: [], input: '', thinking: false, assessment: null },
      diary: { latest: {}, counts: {}, bp: [], glucose: [], weight: [], mood: [], logs: [] },
      diaryDays: 30, diaryModal: false, diaryType: 'bp', diaryForm: {},
      emQR: null, emLoading: false,
      thModal: false, thStep: 'form', thResult: {}, thForm: { chief_complaint: '', specialty: 'general', mode: 'video', urgency: 'routine', preferred_time: 'now', specific_time: '' },
      meds: [], medAdherence: { Daily: [], Total: {}, Streak: 0, Days: 30 },
      reminderModal: false, remForm: { drug_name: '', dosage: '', frequency: '', is_enabled: 1, schedule_times: ['08:00'], notify_via: 'sms' }, remItemId: null,
      wearDevices: [], wearStats: {},
      wearModal: false, wearVendor: '', wearDeviceName: '', wearXml: '', wearXmlSize: '',
      preg: { IsPregnant: false, Weeks: 0, Days: 0, DaysToEdd: 0, Trimester: 1, Current: null, Upcoming: [], AncVisits: [], RiskFactors: [], Latest: {}, Delivery: null, Facility: {} },
      growth: { IsPaediatric: false, Patient: { name:'', sex:'', age_months: 0, age_label: '' }, Latest: {}, Measurements: { weight:[], height:[], bmi:[] }, Reference: { weight:[], height:[], bmi:[] } },
      // Maternal-baby additions
      pnc: { HasPnc: false, Visits: [], Delivery: null },
      pmtct: { IsPmtctClient: false, Client: null, LatestVl: null, NextVl: null },
      hei: { IsHei: false, Hei: null, Tests: [] },
      fp: { IsBreastfeeding: false, MonthsSinceDelivery: null, ObstetricHistory: null, Methods: [] },
      fpFilter: 'recommended',
      // EPDS self-screen
      epdsForm: { active: false, answers: {} },
      epdsResult: null, epdsHistory: [], epdsSubmitting: false,
      // IPV self-disclosure
      ipvForm: { active: false, h1_hurt: 1, h2_insult: 1, h3_threaten: 1, h4_scream: 1, children_at_risk: 0, immediate_danger: 0 },
      ipvResult: null, ipvSubmitting: false,
      // MCH booklet
      booklet: null,
      // Adolescent stream
      adolescent: { IsAdolescent: false, Age: null, Cards: [] },
      crisis: { IsMHClient: false, RiskLevel: 'none', SafetyPlan: null, Supporter: null, Hotlines: [], Facility: {} },
      crisisModal: false, crisisMessage: '', crisisSending: false,
      askSession: '', askMessages: [], askInput: '', askThinking: false,
      docs: [], docStats: {}, docFilter: '',
      docUploadModal: false, docForm: { title: '', category: 'id_document', notes: '', visible_to_clinicians: 1, file_name: '', file_type: '', file_size_kb: 0, file_base64: '' },
      docViewModal: false, docView: { loading: false, title: '', file_name: '', file_type: '', dataUrl: '', isImage: false, isPdf: false, notes: '', file_size_bytes: 0 },
      profiles: [], activeProfile: { patient_id: null, first_name: '', last_name: '', relationship: 'self' }, profileMenu: false,
      famModal: false, famSaving: false,
      famForm: { mode: 'create_minor', first_name: '', last_name: '', dob: '', gender: 'male', relationship: 'parent_of_minor', patient_no: '', verify_phone: '', permList: ['view','book_appointments','view_results','manage_immunizations'] },
      profile: { email: '', next_of_kin_name: '', next_of_kin_phone: '' },
      newAppt: { appointment_date: '', appointment_time: '', department: '', reason: '' },
      todayStr: new Date().toISOString().substring(0,10)
    };
  },
  computed: {
    firstName() { return (this.patient.name || '').split(' ')[0] || 'there'; },
    groupedRx() {
      const map = {};
      for (const row of this.prescriptions) {
        const id = row.prescription_id;
        if (!map[id]) map[id] = { id, created_at: row.created_at, status: row.status, notes: row.notes, items: [] };
        if (row.item_description) map[id].items.push(row);
      }
      return Object.values(map);
    },
    unreadMessages() { return this.threads.reduce((s, t) => s + (parseInt(t.unread_for_patient) || 0), 0); },
    diaryTitle() { return ({ bp:'Log Blood Pressure', glucose:'Log Blood Glucose', weight:'Log Weight', mood:'Log Mood & Sleep', other:'Log Other Reading' })[this.diaryType] || 'Log Reading'; },
    weekColor() { const w = this.preg.Weeks || 0; if (w < 14) return '#a78bfa'; if (w < 28) return '#ec4899'; if (w < 37) return '#f59e0b'; return '#dc2626'; },
    filteredFpMethods() {
      const all = (this.fp && this.fp.Methods) || [];
      if (this.fpFilter === 'recommended') return all.filter(m => m.recommended_for_you || (!this.fp.IsBreastfeeding && !m.not_for_you_now));
      if (this.fpFilter === 'larc') return all.filter(m => m.category === 'LARC');
      if (this.fpFilter === 'permanent') return all.filter(m => m.category === 'Permanent');
      return all;
    },
    epdsQuestions() {
      return [
        { text: 'Been able to laugh and see the funny side of things', options: ['As much as I always could (0)','Not quite so much now (1)','Definitely not so much now (2)','Not at all (3)'] },
        { text: 'Looked forward with enjoyment to things',             options: ['As much as I ever did (0)','Rather less than I used to (1)','Definitely less than I used to (2)','Hardly at all (3)'] },
        { text: 'Blamed myself unnecessarily when things went wrong',  options: ['No, never (0)','Not very often (1)','Yes, some of the time (2)','Yes, most of the time (3)'] },
        { text: 'Been anxious or worried for no good reason',          options: ['No, not at all (0)','Hardly ever (1)','Yes, sometimes (2)','Yes, very often (3)'] },
        { text: 'Felt scared or panicky for no very good reason',      options: ['No, not at all (0)','No, not much (1)','Yes, sometimes (2)','Yes, quite a lot (3)'] },
        { text: 'Things have been getting on top of me',               options: ['No, I have been coping as well as ever (0)','No, most of the time I have coped quite well (1)','Yes, sometimes I haven\'t been coping (2)','Yes, most of the time I haven\'t been able to cope (3)'] },
        { text: 'Been so unhappy that I have had difficulty sleeping', options: ['No, not at all (0)','Not very often (1)','Yes, sometimes (2)','Yes, most of the time (3)'] },
        { text: 'Felt sad or miserable',                               options: ['No, not at all (0)','Not very often (1)','Yes, quite often (2)','Yes, most of the time (3)'] },
        { text: 'Been so unhappy that I have been crying',             options: ['No, never (0)','Only occasionally (1)','Yes, quite often (2)','Yes, most of the time (3)'] },
        { text: 'The thought of harming myself has occurred to me',    options: ['Never (0)','Hardly ever (1)','Sometimes (2)','Yes, quite often (3)'] }
      ];
    },
    epdsAnsweredCount() { return Object.keys(this.epdsForm.answers).length; },
    epdsComplete() { return this.epdsAnsweredCount === 10; },
    suggestedAsks() {
      const baseQs = [
        'What was my last Hb result?',
        'When is my next appointment?',
        'What medications am I currently on?',
        'What did the doctor say at my last visit?',
        'Have I had my MR (Measles-Rubella) vaccine?',
        'What was my last BP reading?'
      ];
      if (this.preg.IsPregnant) {
        return [
          'How far along am I?',
          'When is my next ANC visit?',
          'What was my last Hb at ANC?',
          'What’s my EDD?',
          'What tests are due this trimester?'
        ];
      }
      if (this.growth.IsPaediatric) {
        return [
          'Is my child’s weight on track?',
          'Which vaccines has my child received?',
          'When is the next immunization due?',
          'What was the last weight measurement?'
        ];
      }
      return baseQs;
    },
    canSubmitDoc() {
      if (this.docForm.document_id) return !!this.docForm.title;
      return !!this.docForm.title && !!this.docForm.file_base64;
    },
    docCategoriesAll() {
      return [
        { value: 'id_document',          label: 'ID document' },
        { value: 'insurance_card',       label: 'Insurance / SHA card' },
        { value: 'foreign_record',       label: 'Foreign record' },
        { value: 'medical_report',       label: 'Medical report' },
        { value: 'referral_letter',      label: 'Referral letter' },
        { value: 'prescription_external',label: 'External Rx' },
        { value: 'lab_external',         label: 'External lab result' },
        { value: 'radiology_external',   label: 'External X-ray / scan' },
        { value: 'vaccination_card',     label: 'Vaccination card' },
        { value: 'sick_note',            label: 'Sick note' },
        { value: 'death_certificate',    label: 'Death certificate' },
        { value: 'consent_form',         label: 'Consent form' },
        { value: 'photograph',           label: 'Photograph' },
        { value: 'other',                label: 'Other' }
      ];
    },
    maxChildDob() { return new Date().toISOString().substring(0,10); },
    availablePermissions() {
      return [
        { value: 'view', label: 'View records', desc: 'Visits, results, prescriptions' },
        { value: 'book_appointments', label: 'Book appointments', desc: 'Schedule new visits' },
        { value: 'cancel_appointments', label: 'Cancel appointments', desc: 'Cancel scheduled visits' },
        { value: 'message', label: 'Message clinicians', desc: 'Send/receive secure messages' },
        { value: 'pay_bills', label: 'Pay bills', desc: 'M-PESA payments' },
        { value: 'view_results', label: 'Lab/Rad results', desc: 'See verified results' },
        { value: 'view_prescriptions', label: 'Prescriptions', desc: 'See current and past Rx' },
        { value: 'manage_immunizations', label: 'Immunizations', desc: 'Track child vaccines' },
        { value: 'request_telehealth', label: 'Telehealth', desc: 'Request video consultation' }
      ];
    },
    groupedResults() {
      const map = {};
      for (const row of this.results) {
        const id = row.request_id;
        if (!map[id]) map[id] = { request_id: id, request_number: row.request_number, created_at: row.created_at, items: [] };
        map[id].items.push(row);
      }
      return Object.values(map).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
  },
  mounted() {
    if (!localStorage.getItem('patientPortalToken')) { this.$router.push('/portal/login'); return; }
    const u = localStorage.getItem('patientPortalUser');
    if (u) this.patient = JSON.parse(u);
    this.loadProfiles();
    this.loadHome();
    this.loadThreads();
    this.loadPregnancy();
    this.loadGrowth();
    this.loadAdolescent();
    this.loadCrisis();
    this.messagePoll = setInterval(() => this.loadThreads(true), 30000);
  },
  beforeUnmount() { if (this.messagePoll) clearInterval(this.messagePoll); },
  methods: {
    async _call(fn) {
      try { const r = await api({ function: fn }); if (r.data && r.data.success) return r.data.data; if (r.status === 401 || r.data?.message?.includes('Not authenticated')) this.logout(); }
      catch (e) { if (e.response?.status === 401) this.logout(); }
      return null;
    },
    async loadHome() { const d = await this._call('patientPortalDashboard'); if (d) { this.home = d; this.patient = d.Patient; this.profile.email = d.Patient.email || ''; } },
    async loadAppts() { const d = await this._call('patientPortalAppointments'); if (d) this.appointments = d.Appointments || []; },
    async loadResults() { const d = await this._call('patientPortalResults'); if (d) this.results = d.Results || []; },
    async loadRx() { const d = await this._call('patientPortalPrescriptions'); if (d) this.prescriptions = d.Prescriptions || []; },
    async loadImms() { const d = await this._call('patientPortalImmunizations'); if (d) this.immunizations = d.Immunizations || []; },
    async loadTH() { const d = await this._call('patientPortalTelehealth'); if (d) this.telehealth = d.Sessions || []; },
    async loadRefs() { const d = await this._call('patientPortalReferrals'); if (d) this.referrals = d.Referrals || []; },
    async loadBills() { const d = await this._call('patientPortalBills'); if (d) this.bills = d.Invoices || []; },
    async loadVisits() { const d = await this._call('patientPortalVisits'); if (d) this.visits = d.Visits || []; },
    async loadConsents() { const d = await this._call('patientPortalConsents'); if (d) this.consents = d.Consents || []; },
    async loadEducation() { const d = await this._call('patientPortalEducation'); if (d) this.education = d.Articles || []; },
    async signConsent(c) {
      if (!confirm('Sign "' + c.consent_title + '"?')) return;
      const r = await api({ function: 'patientPortalConsentSign', consent_id: c.consent_id });
      if (r.data && r.data.success) await this.loadConsents();
    },
    // ===== DOCUMENT VAULT =====
    async loadDocs() {
      const r = await api({ function: 'patientPortalDocsList', category: this.docFilter });
      if (r.data && r.data.success) {
        this.docs = r.data.data.Documents || [];
        this.docStats = r.data.data.Stats || {};
      }
    },
    openDocUpload() {
      this.docForm = { title: '', category: 'id_document', notes: '', visible_to_clinicians: 1, file_name: '', file_type: '', file_size_kb: 0, file_base64: '' };
      this.docUploadModal = true;
    },
    editDoc(d) {
      this.docForm = { document_id: d.document_id, title: d.title, category: d.category, notes: d.notes || '', visible_to_clinicians: d.visible_to_clinicians };
      this.docUploadModal = true;
    },
    onDocFile(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 15 * 1024 * 1024) { alert('File too large (max 15 MB).'); return; }
      const reader = new FileReader();
      reader.onload = ev => {
        this.docForm.file_name = file.name;
        this.docForm.file_type = file.type;
        this.docForm.file_size_kb = Math.round(file.size / 1024);
        this.docForm.file_base64 = (ev.target.result + '').split(',')[1];   // strip data: prefix
      };
      reader.readAsDataURL(file);
    },
    async submitDoc() {
      this.sending = true;
      try {
        const fn = this.docForm.document_id ? 'patientPortalDocUpdate' : 'patientPortalDocUpload';
        const r = await api({ function: fn, ...this.docForm });
        if (r.data && r.data.success) {
          this.docUploadModal = false;
          await this.loadDocs();
        } else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); alert('Network or server error.'); }
      finally { this.sending = false; }
    },
    async deleteDoc(d) {
      if (!confirm('Delete "' + d.title + '"?')) return;
      const r = await api({ function: 'patientPortalDocDelete', document_id: d.document_id });
      if (r.data && r.data.success) await this.loadDocs();
    },
    async viewDoc(d) {
      this.docView = { loading: true, title: d.title, file_name: d.file_name, file_type: d.file_type, file_size_bytes: d.file_size_bytes, notes: d.notes, dataUrl: '', isImage: false, isPdf: false };
      this.docViewModal = true;
      const r = await api({ function: 'patientPortalDocDownload', document_id: d.document_id });
      if (r.data && r.data.success) {
        const det = r.data.data;
        const mime = det.file_type || (det.file_extension === 'pdf' ? 'application/pdf' : 'application/octet-stream');
        this.docView = {
          loading: false,
          title: det.title,
          file_name: det.file_name,
          file_type: mime,
          file_size_bytes: det.file_size_bytes,
          notes: det.notes,
          dataUrl: 'data:' + mime + ';base64,' + det.data_base64,
          isImage: /^image\//.test(mime),
          isPdf: mime === 'application/pdf'
        };
      }
    },
    formatBytes(b) { b = parseInt(b) || 0; if (b < 1024) return b + ' B'; if (b < 1024*1024) return (b/1024).toFixed(1) + ' KB'; return (b/(1024*1024)).toFixed(1) + ' MB'; },
    docCatLabel(c) { const f = this.docCategoriesAll.find(x => x.value === c); return f ? f.label : c; },
    docCatIcon(c) { return ({ id_document:'mdi-card-account-details', insurance_card:'mdi-shield-account', foreign_record:'mdi-file-document', medical_report:'mdi-clipboard-text', referral_letter:'mdi-share-variant', prescription_external:'mdi-pill', lab_external:'mdi-flask', radiology_external:'mdi-radioactive', vaccination_card:'mdi-needle', sick_note:'mdi-medical-bag', death_certificate:'mdi-grave-stone', consent_form:'mdi-file-sign', photograph:'mdi-image', other:'mdi-file' })[c] || 'mdi-file'; },
    docTypeIcon(ext) {
      ext = (ext || '').toLowerCase();
      if (['png','jpg','jpeg','gif','webp','heic','tif','tiff'].includes(ext)) return 'mdi-image';
      if (ext === 'pdf') return 'mdi-file-pdf-box';
      if (['doc','docx'].includes(ext)) return 'mdi-file-word-box';
      if (ext === 'txt') return 'mdi-file-document';
      return 'mdi-file';
    },
    docTypeColor(ext) {
      ext = (ext || '').toLowerCase();
      if (['png','jpg','jpeg','gif','webp','heic','tif','tiff'].includes(ext)) return 'pt-doc-img-color';
      if (ext === 'pdf') return 'pt-doc-pdf-color';
      if (['doc','docx'].includes(ext)) return 'pt-doc-doc-color';
      return 'pt-doc-other-color';
    },

    // ===== WEARABLES =====
    async loadWear() {
      const r = await api({ function: 'patientPortalWearables' });
      if (r.data && r.data.success) {
        this.wearDevices = r.data.data.Devices || [];
        this.wearStats = r.data.data.Stats || {};
      }
    },
    openWearConnect(vendor) {
      this.wearVendor = vendor;
      this.wearDeviceName = '';
      this.wearXml = '';
      this.wearXmlSize = '';
      this.wearModal = true;
    },
    onAppleFile(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.wearXmlSize = (file.size / (1024 * 1024)).toFixed(1) + ' MB';
      const reader = new FileReader();
      reader.onload = ev => { this.wearXml = ev.target.result; };
      reader.readAsText(file);
    },
    async uploadApple() {
      if (!this.wearXml) return;
      this.sending = true;
      try {
        const r = await api({ function: 'patientPortalWearableImportAppleHealth', xml: this.wearXml });
        if (r.data && r.data.success) {
          this.wearModal = false;
          await this.loadWear();
          alert(r.data.message);
        } else alert(r.data?.message || 'Import failed.');
      } catch (e) { alert('Network error.'); }
      finally { this.sending = false; }
    },
    async connectGeneric() {
      this.sending = true;
      try {
        const r = await api({ function: 'patientPortalWearableConnect', vendor: this.wearVendor, device_name: this.wearDeviceName || this.defaultDeviceName(this.wearVendor) });
        if (r.data && r.data.success) { this.wearModal = false; await this.loadWear(); alert('Device added — OAuth flow will be enabled when admin configures credentials.'); }
      } catch (e) { console.error(e); }
      finally { this.sending = false; }
    },
    async disconnectDevice(d) {
      if (!confirm('Disconnect ' + this.vendorLabel(d.vendor) + '?')) return;
      const r = await api({ function: 'patientPortalWearableDisconnect', connection_id: d.connection_id });
      if (r.data && r.data.success) await this.loadWear();
    },
    async simulateWear() {
      if (!confirm('Generate 7 days of fake wearable data (steps, heart rate, BP, sleep, SpO2)?')) return;
      const r = await api({ function: 'patientPortalWearableSimulate' });
      if (r.data && r.data.success) {
        await this.loadWear();
        alert('Demo data synced. Open Health Diary to see trends.');
      }
    },
    vendorLabel(v) { return ({ apple_health:'Apple Health', fitbit:'Fitbit', garmin:'Garmin', oura:'Oura Ring', samsung_health:'Samsung Health', withings:'Withings', google_fit:'Google Fit', generic:'Generic device', manual_csv:'Manual upload' })[v] || v; },
    vendorIcon(v) { return ({ apple_health:'mdi-apple', fitbit:'mdi-watch', garmin:'mdi-watch-variant', oura:'mdi-ring', samsung_health:'mdi-cellphone', withings:'mdi-scale-bathroom', google_fit:'mdi-google', generic:'mdi-watch-outline', manual_csv:'mdi-file-upload' })[v] || 'mdi-watch'; },
    defaultDeviceName(v) { return this.vendorLabel(v); },
    formatNumber(n) { return Number(n||0).toLocaleString(); },

    // ===== MEDICATION REMINDERS =====
    async loadMeds() {
      const m = await api({ function: 'patientPortalActiveMedications' });
      if (m.data && m.data.success) this.meds = m.data.data.Medications || [];
      const a = await api({ function: 'patientPortalAdherenceTrend', days: 30 });
      if (a.data && a.data.success) this.medAdherence = a.data.data;
    },
    async logDose(med, action) {
      const r = await api({ function: 'patientPortalDoseLog', prescription_item_id: med.item_id, action });
      if (r.data && r.data.success) await this.loadMeds();
    },
    async logSkip(med) {
      const reason = prompt('Why did you skip this dose? (optional)');
      if (reason === null) return;
      const r = await api({ function: 'patientPortalDoseLog', prescription_item_id: med.item_id, action: 'skipped', skip_reason: reason });
      if (r.data && r.data.success) await this.loadMeds();
    },
    openReminderSetup(m) {
      const times = Array.isArray(m.schedule_times) ? [...m.schedule_times] : ['08:00'];
      const freq = (m.frequency || '').toUpperCase();
      // sensible defaults based on frequency
      if (!Array.isArray(m.schedule_times)) {
        if (freq.includes('BD') || freq.includes('BID')) {
          times.splice(0); times.push('08:00','20:00');
        } else if (freq.includes('TDS') || freq.includes('TID')) {
          times.splice(0); times.push('08:00','14:00','20:00');
        } else if (freq.includes('QID') || freq.includes('Q6H')) {
          times.splice(0); times.push('06:00','12:00','18:00','22:00');
        }
      }
      this.remItemId = m.item_id;
      this.remForm = {
        drug_name: m.drug_name, dosage: m.dosage, frequency: m.frequency,
        is_enabled: m.reminder_on == 1 ? 1 : 1,                           // default ON when opening
        schedule_times: times.length ? times : ['08:00'],
        notify_via: m.notify_via || 'sms'
      };
      this.reminderModal = true;
    },
    async saveReminder() {
      this.sending = true;
      try {
        const r = await api({
          function: 'patientPortalReminderSetup',
          prescription_item_id: this.remItemId,
          is_enabled: this.remForm.is_enabled,
          schedule_times: JSON.stringify(this.remForm.schedule_times),
          notify_via: this.remForm.notify_via
        });
        if (r.data && r.data.success) { this.reminderModal = false; await this.loadMeds(); }
      } catch (e) { console.error(e); }
      finally { this.sending = false; }
    },
    adhPercent(taken, total) { if (!total) return '–'; return Math.round(100 * (parseInt(taken) || 0) / parseInt(total)); },
    adhClass(taken, total) {
      const pct = this.adhPercent(taken, total);
      if (pct === '–') return '';
      if (pct >= 90) return 'pt-adh-good';
      if (pct >= 75) return 'pt-adh-fair';
      return 'pt-adh-poor';
    },
    adhClassPct(pct) { if (pct >= 90) return 'pt-adh-good'; if (pct >= 75) return 'pt-adh-fair'; return 'pt-adh-poor'; },

    // ===== TELEHEALTH ON-DEMAND =====
    openTelehealthRequest() {
      this.thStep = 'form';
      this.thForm = { chief_complaint: '', specialty: 'general', mode: 'video', urgency: 'routine', preferred_time: 'now', specific_time: '' };
      this.thModal = true;
    },
    async submitTelehealth() {
      this.sending = true; this.thStep = 'submitting';
      try {
        const payload = {
          function: 'patientPortalTelehealthRequest',
          chief_complaint: this.thForm.chief_complaint,
          specialty: this.thForm.specialty,
          mode: this.thForm.mode,
          urgency: this.thForm.urgency,
          preferred_time: this.thForm.preferred_time === 'specific' ? this.thForm.specific_time : this.thForm.preferred_time
        };
        const r = await api(payload);
        if (r.data && r.data.success) {
          this.thResult = { document_no: r.data.data.document_no, message: r.data.message };
          this.thStep = 'success';
          await this.loadTH();
        } else {
          alert(r.data?.message || 'Failed.');
          this.thStep = 'form';
        }
      } catch (e) { console.error(e); this.thStep = 'form'; }
      finally { this.sending = false; }
    },
    async cancelTelehealth(t) {
      const reason = prompt('Reason for cancelling?');
      if (reason === null) return;
      const r = await api({ function: 'patientPortalTelehealthCancel', session_id: t.session_id, reason });
      if (r.data && r.data.success) await this.loadTH();
    },
    formatThStatus(s) { return ({ requested:'⏳ Awaiting clinician', scheduled:'📅 Scheduled', waiting_room:'🟡 Waiting room', in_progress:'🟢 In progress', completed:'✓ Completed', no_show:'No-show', cancelled:'Cancelled', declined:'Declined' })[s] || s; },

    // ===== EMERGENCY QR =====
    async loadEmergencyQR() {
      this.emLoading = true;
      try {
        const r = await api({ function: 'patientPortalEmergencyQR' });
        if (r.data && r.data.success) this.emQR = r.data.data;
      } catch (e) { console.error(e); }
      finally { this.emLoading = false; }
    },
    async regenerateEmergency() {
      if (!confirm('Generate a new QR? Your old QR (printed copies, saved screenshots) will stop working immediately.')) return;
      const r = await api({ function: 'patientPortalEmergencyRegenerate' });
      if (r.data && r.data.success) { this.emQR = null; await this.loadEmergencyQR(); }
    },
    copyEmergencyLink() {
      if (!this.emQR) return;
      navigator.clipboard.writeText(this.emQR.public_url).then(() => alert('Link copied'));
    },
    printEmergencyCard() {
      if (!this.emQR) return;
      window.open(this.emQR.public_url, '_blank');
    },

    // ===== FAMILY / PROXY =====
    async loadProfiles() {
      // Don't send active_patient_id when fetching profile list (we want from logged-in identity)
      const stored = localStorage.getItem('activeProfileId');
      localStorage.removeItem('activeProfileId');
      try {
        const r = await api({ function: 'patientPortalProfiles' });
        if (r.data && r.data.success) {
          this.profiles = r.data.data.Profiles || [];
          // restore active selection if it's still valid
          let active = this.profiles.find(p => p.relationship === 'self');
          if (stored && this.profiles.some(p => String(p.patient_id) === String(stored))) {
            active = this.profiles.find(p => String(p.patient_id) === String(stored));
            localStorage.setItem('activeProfileId', stored);
          }
          this.activeProfile = active || this.activeProfile;
        }
      } catch (e) { console.error(e); }
    },
    closeProfileMenu() { this.profileMenu = false; },
    async switchProfile(p) {
      this.profileMenu = false;
      if (p.patient_id == this.activeProfile.patient_id) return;
      if (p.relationship === 'self') { this.switchToSelf(); return; }
      const r = await api({ function: 'patientPortalSwitchProfile', target_patient_id: p.patient_id });
      if (r.data && r.data.success) {
        localStorage.setItem('activeProfileId', p.patient_id);
        this.activeProfile = p;
        // Reload everything for new profile
        this.tab = 'home'; this.loadHome(); this.loadPregnancy(); this.loadGrowth(); this.loadAdolescent();
      } else alert(r.data?.message || 'Failed to switch.');
    },
    switchToSelf() {
      localStorage.removeItem('activeProfileId');
      this.activeProfile = this.profiles.find(p => p.relationship === 'self') || this.activeProfile;
      this.tab = 'home'; this.loadHome(); this.loadPregnancy(); this.loadGrowth(); this.loadAdolescent();
    },
    profileInitial(p) { return ((p.first_name || '?').charAt(0) + (p.last_name || '').charAt(0)).toUpperCase(); },
    profileColor(p) {
      const colors = ['pt-avatar-blue', 'pt-avatar-purple', 'pt-avatar-green', 'pt-avatar-orange', 'pt-avatar-red', 'pt-avatar-teal'];
      return colors[((p.patient_id || 0) % colors.length)];
    },
    formatRel(r) { return ({ self:'You', parent_of_minor:'Your child', spouse:'Spouse', adult_child:'Adult child', sibling:'Sibling', guardian:'Ward', caregiver:'Care recipient', dependant:'Dependant', other:'Other' })[r] || r; },
    openAddFamily() {
      this.profileMenu = false;
      this.famForm = { mode: 'create_minor', first_name: '', last_name: '', dob: '', gender: 'male', relationship: 'parent_of_minor', patient_no: '', verify_phone: '', permList: ['view','book_appointments','view_results','manage_immunizations'] };
      this.famModal = true;
    },
    async addFamily() {
      this.famSaving = true;
      try {
        // Send proxy add request — DO NOT propagate active_patient_id (always from logged-in identity)
        const stored = localStorage.getItem('activeProfileId');
        localStorage.removeItem('activeProfileId');
        const r = await api({
          function: 'patientPortalProxyAdd',
          mode: this.famForm.mode,
          first_name: this.famForm.first_name,
          last_name: this.famForm.last_name,
          dob: this.famForm.dob,
          gender: this.famForm.gender,
          patient_no: this.famForm.patient_no,
          verify_phone: this.famForm.verify_phone,
          relationship: this.famForm.relationship,
          permissions: this.famForm.permList.join(',')
        });
        if (stored) localStorage.setItem('activeProfileId', stored);
        if (r.data && r.data.success) {
          this.famModal = false;
          await this.loadProfiles();
          alert(r.data.message || 'Family member added.');
        } else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.famSaving = false; }
    },

    // ===== HEALTH DIARY =====
    async loadDiary() {
      const t = await api({ function: 'patientPortalHealthTrends', days: this.diaryDays });
      if (t.data && t.data.success) {
        this.diary.latest = t.data.data.Latest || {};
        this.diary.counts = t.data.data.Counts || {};
        this.diary.bp = t.data.data.BP || [];
        this.diary.glucose = t.data.data.Glucose || [];
        this.diary.weight = t.data.data.Weight || [];
        this.diary.mood = t.data.data.Mood || [];
      }
      const l = await api({ function: 'patientPortalHealthLogs', days: this.diaryDays });
      if (l.data && l.data.success) this.diary.logs = l.data.data.Logs || [];
    },
    openDiaryLog(type) {
      this.diaryType = type;
      this.diaryForm = {
        log_type: type === 'other' ? 'custom' : type,
        logged_at: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
        mood_score: 5,
        glucose_context: 'fasting',
        flagged_for_review: 0
      };
      this.diaryModal = true;
    },
    async saveLog() {
      this.sending = true;
      try {
        const r = await api({ function: 'patientPortalHealthLogAdd', ...this.diaryForm });
        if (r.data && r.data.success) {
          this.diaryModal = false;
          await this.loadDiary();
        } else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.sending = false; }
    },
    async deleteLog(l) {
      if (!confirm('Delete this entry?')) return;
      const r = await api({ function: 'patientPortalHealthLogDelete', log_id: l.log_id });
      if (r.data && r.data.success) await this.loadDiary();
    },
    bpClass(latest) {
      const s = parseInt(latest.bp_systolic), d = parseInt(latest.bp_diastolic);
      if (!s) return '';
      if (s >= 180 || d >= 110) return 'pt-trend-danger';
      if (s >= 140 || d >= 90)  return 'pt-trend-warn';
      return 'pt-trend-good';
    },
    glucoseClass(g) {
      const v = parseFloat(g);
      if (!v) return '';
      if (v < 4 || v > 11)   return 'pt-trend-danger';
      if (v > 7)             return 'pt-trend-warn';
      return 'pt-trend-good';
    },
    moodEmoji(s) { return s >= 9 ? '😄 great' : s >= 7 ? '🙂 good' : s >= 5 ? '😐 ok' : s >= 3 ? '😟 down' : '😢 very low'; },
    formatLogType(t) { return ({ bp:'BP', glucose:'Glucose', weight:'Weight', mood:'Mood', temperature:'Temp', oxygen:'SpO2', sleep:'Sleep', steps:'Steps', custom:'Other', symptom:'Symptom', medication:'Medication' })[t] || t; },
    logTypePill(t) { return ({ bp:'pt-pill-warn', glucose:'pt-pill-warn', weight:'pt-pill-info', mood:'pt-pill-info', temperature:'pt-pill-warn' })[t] || 'pt-pill-muted'; },
    formatLogReading(l) {
      if (l.bp_systolic) return `${l.bp_systolic}/${l.bp_diastolic} mmHg` + (l.pulse ? ` · ${l.pulse} bpm` : '');
      if (l.blood_glucose) return `${l.blood_glucose} mmol/L` + (l.glucose_context ? ` (${l.glucose_context})` : '');
      if (l.weight_kg) return `${l.weight_kg} kg` + (l.bmi ? ` · BMI ${l.bmi}` : '');
      if (l.mood_score) return `Mood ${l.mood_score}/10` + (l.sleep_hours ? ` · ${l.sleep_hours}h sleep` : '');
      if (l.temperature_c) return `${l.temperature_c}°C`;
      if (l.oxygen_sat) return `${l.oxygen_sat}% SpO2`;
      if (l.steps) return `${l.steps} steps`;
      if (l.pain_score) return `Pain ${l.pain_score}/10`;
      if (l.symptom_text) return l.symptom_text;
      return '—';
    },
    // ===== SYMPTOM CHECKER =====
    async symLoadHistory() {
      const d = await this._call('patientPortalSymptomHistory');
      if (d) this.sym.history = d.Assessments || [];
    },
    symStart() {
      this.sym.active = true; this.sym.assessmentId = null; this.sym.messages = [];
      this.sym.assessment = null; this.sym.input = ''; this.sym.thinking = false;
      // welcome message
      this.sym.messages.push({ role: 'assistant', content: "Hello! Tell me what you're feeling — when it started, where in the body, how bad on a scale of 1–10, and anything else relevant." });
      this.$nextTick(() => this.symScroll());
    },
    async symOpen(a) {
      this.sym.active = true; this.sym.assessmentId = a.assessment_id;
      const r = await api({ function: 'patientPortalSymptomDetail', assessment_id: a.assessment_id });
      if (r.data && r.data.success) {
        const d = r.data.data.Assessment;
        this.sym.messages = (d.conversation || []).map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: this.stripAssessment(m.content) }));
        if (d.urgency) this.sym.assessment = {
          urgency: d.urgency, chief_complaint: d.chief_complaint,
          likely_causes: d.likely_causes, red_flags: d.red_flags,
          recommended_action: d.recommended_action,
          self_care_advice: d.self_care_advice,
          summary: d.summary
        };
        this.$nextTick(() => this.symScroll());
      }
    },
    stripAssessment(s) { return (s || '').replace(/<assessment>[\s\S]*?<\/assessment>/g, '').trim(); },
    symBackToList() { this.sym.active = false; this.sym.assessmentId = null; this.symLoadHistory(); },
    symScroll() { const el = this.$refs.symMsgList; if (el) el.scrollTop = el.scrollHeight; },
    async symSend() {
      const msg = this.sym.input.trim();
      if (!msg) return;
      this.sym.messages.push({ role: 'user', content: msg });
      this.sym.input = ''; this.sym.thinking = true;
      this.$nextTick(() => this.symScroll());
      try {
        const r = await api({ function: 'patientPortalSymptomCheck', message: msg, assessment_id: this.sym.assessmentId || 0 });
        if (r.data && r.data.success) {
          this.sym.assessmentId = r.data.data.assessment_id;
          if (r.data.data.reply) this.sym.messages.push({ role: 'assistant', content: r.data.data.reply });
          if (r.data.data.assessment) this.sym.assessment = r.data.data.assessment;
        } else {
          this.sym.messages.push({ role: 'assistant', content: '[Error: ' + (r.data?.message || 'failed') + ']' });
        }
      } catch (e) {
        this.sym.messages.push({ role: 'assistant', content: '[Network error — please try again.]' });
      } finally {
        this.sym.thinking = false;
        this.$nextTick(() => this.symScroll());
      }
    },
    async symBookAppt() {
      this.tab = 'appointments'; this.bookModal = true;
      this.newAppt.reason = (this.sym.assessment?.chief_complaint || 'Symptom check follow-up') + ' — ' + (this.sym.assessment?.recommended_action || '');
    },
    async symSendToDoctor() {
      this.tab = 'messages';
      const a = this.sym.assessment;
      this.newMsgForm = {
        target_role: 'doctor',
        priority: (a.urgency === 'emergency' || a.urgency === 'urgent_today') ? 'urgent' : 'normal',
        subject: 'Symptom check: ' + (a.chief_complaint || 'follow-up'),
        body: 'AI symptom assessment summary:\n\n' + a.summary + '\n\nSuggested action: ' + a.recommended_action
      };
      this.newMsgModal = true;
    },
    formatUrgency(u) { return ({
      emergency: '🚨 Call emergency now',
      urgent_today: 'Urgent — see today',
      within_24h: 'Within 24 hours',
      within_week: 'Within 1 week',
      self_care: 'Self-care at home',
      unknown: 'Inconclusive'
    })[u] || u; },
    urgencyPill(u) { return ({ emergency:'pt-pill-danger', urgent_today:'pt-pill-warn', within_24h:'pt-pill-warn', within_week:'pt-pill-info', self_care:'pt-pill-success' })[u] || ''; },
    urgencyClass(u) { return 'pt-sym-' + (u || 'unknown'); },
    urgencyIcon(u) { return ({ emergency:'mdi-ambulance', urgent_today:'mdi-alert', within_24h:'mdi-clock-alert', within_week:'mdi-calendar-clock', self_care:'mdi-home-heart' })[u] || 'mdi-help-circle'; },

    openPay(b) {
      this.payInvoice = b;
      this.payAmount  = parseFloat(b.balance);
      this.payPhone   = (this.patient.phone || '').replace(/\D/g, '');
      if (this.payPhone.startsWith('254')) this.payPhone = '0' + this.payPhone.slice(3);
      this.payStep = 'form'; this.payReceipt = ''; this.payError = ''; this.payCountdown = 60;
      this.payModal = true;
    },
    closePay() {
      if (this.payPoll) { clearInterval(this.payPoll); this.payPoll = null; }
      if (this.payCountdownTimer) { clearInterval(this.payCountdownTimer); this.payCountdownTimer = null; }
      this.payModal = false;
      if (this.payStep === 'success') this.loadBills();
    },
    async initiatePay() {
      this.payStep = 'pushing'; this.sending = true;
      try {
        const r = await api({
          function: 'mpesaInitSTKPush',
          invoice_id: this.payInvoice.invoice_id,
          amount: this.payAmount,
          phone: this.payPhone
        });
        if (r.data && r.data.success) {
          this.payCheckoutId = r.data.data.checkout_request_id;
          this.payStep = 'awaiting';
          this.payCountdown = 60;
          this.payCountdownTimer = setInterval(() => {
            this.payCountdown--;
            if (this.payCountdown <= 0) { this.payStep = 'failed'; this.payError = 'Timed out — no response from M-PESA.'; this.closePollers(); }
          }, 1000);
          this.payPoll = setInterval(() => this.pollPayStatus(), 3000);
        } else {
          this.payStep = 'failed';
          this.payError = r.data?.message || 'Failed to start payment.';
        }
      } catch (e) {
        this.payStep = 'failed'; this.payError = 'Network error.';
      } finally { this.sending = false; }
    },
    closePollers() { if (this.payPoll) { clearInterval(this.payPoll); this.payPoll = null; } if (this.payCountdownTimer) { clearInterval(this.payCountdownTimer); this.payCountdownTimer = null; } },
    async pollPayStatus() {
      const r = await api({ function: 'mpesaCheckStatus', checkout_request_id: this.payCheckoutId });
      if (r.data && r.data.success) {
        const t = r.data.data.Transaction;
        if (t.status === 'success') {
          this.payStep = 'success'; this.payReceipt = t.mpesa_receipt; this.closePollers();
        } else if (t.status === 'failed' || t.status === 'cancelled' || t.status === 'timeout') {
          this.payStep = 'failed'; this.payError = t.result_desc || t.status; this.closePollers();
        }
      }
    },

    async loadThreads() {
      const d = await this._call('patientPortalThreads');
      if (d) this.threads = d.Threads || [];
    },
    async openThread(t) {
      this.activeThread = t;
      const r = await api({ function: 'patientPortalThreadOpen', thread_id: t.thread_id });
      if (r.data && r.data.success) {
        this.activeThread = r.data.data.Thread;
        this.activeMessages = r.data.data.Messages || [];
        this.$nextTick(() => { const el = this.$refs.msgList; if (el) el.scrollTop = el.scrollHeight; });
      }
    },
    async sendReply() {
      if (!this.replyBody.trim()) return;
      this.sending = true;
      try {
        const r = await api({ function: 'patientPortalThreadReply', thread_id: this.activeThread.thread_id, body: this.replyBody });
        if (r.data && r.data.success) { this.replyBody = ''; await this.openThread(this.activeThread); }
        else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.sending = false; }
    },
    async closeThread() {
      if (!confirm('Close this conversation?')) return;
      const r = await api({ function: 'patientPortalThreadClose', thread_id: this.activeThread.thread_id });
      if (r.data && r.data.success) { this.activeThread = null; await this.loadThreads(); }
    },
    async sendNewMsg() {
      this.sending = true;
      try {
        const r = await api({ function: 'patientPortalThreadCreate', ...this.newMsgForm });
        if (r.data && r.data.success) {
          this.newMsgModal = false;
          this.newMsgForm = { target_role: 'general', priority: 'normal', subject: '', body: '' };
          await this.loadThreads();
          alert(r.data.message || 'Sent.');
        } else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.sending = false; }
    },
    formatRole(r) { return ({ doctor:'Doctor', nurse:'Nurse', reception:'Reception', billing:'Billing', pharmacy:'Pharmacy', lab:'Laboratory', mental_health:'Mental Health', art_clinic:'ART Clinic', tb_clinic:'TB Clinic', maternity:'Maternity', general:'Hospital' })[r] || r; },
    roleIcon(r) { return ({ doctor:'mdi-stethoscope', nurse:'mdi-account-heart', reception:'mdi-account-tie', billing:'mdi-cash-multiple', pharmacy:'mdi-pill', lab:'mdi-flask', mental_health:'mdi-brain', art_clinic:'mdi-virus-outline', tb_clinic:'mdi-lungs', maternity:'mdi-baby-carriage', general:'mdi-hospital-building' })[r] || 'mdi-message'; },
    threadStatusPill(s) { return ({ open:'pt-pill-info', in_progress:'pt-pill-warn', answered:'pt-pill-success', resolved:'pt-pill-muted', closed:'pt-pill-muted' })[s] || ''; },
    priorityPill(p) { return ({ low:'pt-pill-muted', normal:'pt-pill-info', high:'pt-pill-warn', urgent:'pt-pill-danger' })[p] || ''; },
    printLab(requestId) {
      const url = this.$router.resolve({ name: 'portal-lab-print', params: { id: requestId }, query: { auto: 1 } }).href;
      window.open(url, '_blank');
    },
    printVisit(visitId) {
      const url = this.$router.resolve({ name: 'portal-visit-print', params: { id: visitId }, query: { auto: 1 } }).href;
      window.open(url, '_blank');
    },

    goTab(t) {
      this.tab = t;
      if (t === 'appointments') this.loadAppts();
      if (t === 'results') this.loadResults();
      if (t === 'prescriptions') this.loadRx();
      if (t === 'immunizations') this.loadImms();
      if (t === 'telehealth') this.loadTH();
      if (t === 'referrals') this.loadRefs();
      if (t === 'bills') this.loadBills();
      if (t === 'visits') this.loadVisits();
      if (t === 'consents') this.loadConsents();
      if (t === 'education') this.loadEducation();
      if (t === 'messages') this.loadThreads();
      if (t === 'symptom') this.symLoadHistory();
      if (t === 'diary') this.loadDiary();
      if (t === 'emergency') this.loadEmergencyQR();
      if (t === 'meds') this.loadMeds();
      if (t === 'wearables') this.loadWear();
      if (t === 'docs') this.loadDocs();
      if (t === 'pregnancy') this.loadPregnancy();
      if (t === 'fp') this.loadFp();
      if (t === 'wellbeing') this.loadEpdsHistory();
      if (t === 'booklet') this.loadBooklet();
    },
    async loadPregnancy() {
      try {
        const r = await api({ function: 'patientPortalPregnancy' });
        if (r.data && r.data.success) this.preg = r.data.data;
        // Maternal extensions (parallel)
        const [pnc, pmtct] = await Promise.all([
          api({ function: 'patientPortalPncSchedule' }),
          api({ function: 'patientPortalPmtctStatus' })
        ]);
        if (pnc.data && pnc.data.success) this.pnc = pnc.data.data;
        if (pmtct.data && pmtct.data.success) this.pmtct = pmtct.data.data;
      } catch (e) { console.error(e); }
    },
    async loadGrowth() {
      try {
        const r = await api({ function: 'patientPortalGrowthChart' });
        if (r.data && r.data.success) this.growth = r.data.data;
        // HEI extension (only meaningful when the current profile is a baby)
        const hei = await api({ function: 'patientPortalHeiStatus' });
        if (hei.data && hei.data.success) this.hei = hei.data.data;
      } catch (e) { console.error(e); }
    },
    async loadFp() {
      try {
        const r = await api({ function: 'patientPortalFpMethods' });
        if (r.data && r.data.success) this.fp = r.data.data;
      } catch (e) { console.error(e); }
    },
    pncRowClass(v) {
      if (v.visit_status === 'completed') return 'pt-pnc-row-good';
      if (v.is_overdue) return 'pt-pnc-row-bad';
      if (v.is_due_today) return 'pt-pnc-row-warn';
      return '';
    },

    // ===== EPDS =====
    startEpds() { this.epdsForm = { active: true, answers: {} }; this.epdsResult = null; this.loadEpdsHistory(); },
    async loadEpdsHistory() {
      try {
        const r = await api({ function: 'patientPortalEpdsHistory' });
        if (r.data && r.data.success) this.epdsHistory = r.data.data.rows || [];
      } catch (e) { console.error(e); }
    },
    async submitEpds() {
      if (!this.epdsComplete) return;
      this.epdsSubmitting = true;
      try {
        const payload = { function: 'patientPortalEpdsSubmit' };
        for (let i = 1; i <= 10; i++) payload['q' + i] = this.epdsForm.answers[i];
        const r = await api(payload);
        if (r.data && r.data.success) {
          this.epdsResult = r.data.data;
          this.epdsForm = { active: false, answers: {} };
          await this.loadEpdsHistory();
        } else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.epdsSubmitting = false; }
    },

    // ===== IPV self-screen =====
    startIpv() { this.ipvForm = { active: true, h1_hurt: 1, h2_insult: 1, h3_threaten: 1, h4_scream: 1, children_at_risk: 0, immediate_danger: 0 }; this.ipvResult = null; },
    async submitIpv() {
      this.ipvSubmitting = true;
      try {
        const r = await api({ function: 'patientPortalIpvSelfReport', ...this.ipvForm });
        if (r.data && r.data.success) {
          this.ipvResult = r.data.data;
          this.ipvForm.active = false;
        } else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.ipvSubmitting = false; }
    },

    // ===== Quick exit (IPV safety) =====
    quickExit() {
      try {
        // Clear our session-storage flag so the app forgets the user
        sessionStorage.removeItem('isLoggedIn');
        localStorage.removeItem('hmsPatientToken');
        // Best-effort: wipe browser history entries we created
        for (let i = 0; i < 30; i++) history.replaceState(null, '', '/');
      } catch (e) { /* sessionStorage may be locked down — ignore */ }
      // Replace current page with a benign decoy
      window.location.replace('https://www.weather.com');
    },

    // ===== MCH Booklet =====
    async loadBooklet() {
      try {
        const r = await api({ function: 'patientPortalMchBooklet' });
        if (r.data && r.data.success) this.booklet = r.data.data;
      } catch (e) { console.error(e); }
    },
    printBooklet() { window.print(); },

    // ===== Adolescent content stream =====
    async loadAdolescent() {
      try {
        const r = await api({ function: 'patientPortalAdolescentContent' });
        if (r.data && r.data.success) this.adolescent = r.data.data;
      } catch (e) { console.error(e); }
    },
    // ===== ASK MY RECORD =====
    async sendAsk() {
      const q = this.askInput.trim();
      if (!q) return;
      this.askMessages.push({ role: 'user', content: q });
      this.askInput = ''; this.askThinking = true;
      this.$nextTick(() => { const el = this.$refs.askMsgList; if (el) el.scrollTop = el.scrollHeight; });
      try {
        const r = await api({ function: 'patientPortalAskRecord', question: q, session_id: this.askSession });
        if (r.data && r.data.success) {
          this.askSession = r.data.data.session_id;
          this.askMessages.push({ role: 'assistant', content: r.data.data.answer, ms: r.data.data.duration_ms });
        } else {
          this.askMessages.push({ role: 'assistant', content: '⚠ ' + (r.data?.message || 'Failed.') });
        }
      } catch (e) {
        this.askMessages.push({ role: 'assistant', content: '⚠ Network error — please try again.' });
      } finally {
        this.askThinking = false;
        this.$nextTick(() => { const el = this.$refs.askMsgList; if (el) el.scrollTop = el.scrollHeight; });
      }
    },
    askNewSession() { this.askSession = ''; this.askMessages = []; this.askInput = ''; },

    async loadCrisis() {
      try {
        const r = await api({ function: 'patientPortalCrisisInfo' });
        if (r.data && r.data.success) this.crisis = r.data.data;
      } catch (e) { console.error(e); }
    },
    openCrisis() { this.crisisModal = true; this.logCrisis('viewed'); },
    closeCrisis() { this.crisisModal = false; this.crisisMessage = ''; },
    async logCrisis(action) { try { await api({ function: 'patientPortalCrisisLog', action }); } catch (e) { /* best-effort log — safe to swallow */ } },
    async sendCrisisMsg() {
      this.crisisSending = true;
      try {
        const r = await api({ function: 'patientPortalCrisisLog', action: 'sent_urgent_msg', message: this.crisisMessage });
        if (r.data && r.data.success) {
          alert(r.data.message || 'Sent.');
          this.crisisModal = false;
          this.crisisMessage = '';
          await this.loadThreads();
        }
      } catch (e) { console.error(e); }
      finally { this.crisisSending = false; }
    },
    growthStatusClass(s) { return ({ low:'pt-growth-low', high:'pt-growth-high', normal:'pt-growth-normal' })[s] || ''; },

    async submitBook() {
      this.booking = true;
      try {
        const r = await api({ function: 'patientPortalBookAppointment', ...this.newAppt });
        if (r.data && r.data.success) { this.bookModal = false; this.newAppt = { appointment_date: '', appointment_time: '', department: '', reason: '' }; await this.loadAppts(); await this.loadHome(); alert('Booked.'); }
        else alert(r.data?.message || 'Failed.');
      } catch (e) { alert('Network error.'); }
      finally { this.booking = false; }
    },
    async cancelAppt(a) {
      const reason = prompt('Reason for cancelling?'); if (reason === null) return;
      const r = await api({ function: 'patientPortalCancelAppointment', appointment_id: a.appointment_id, reason });
      if (r.data && r.data.success) { await this.loadAppts(); await this.loadHome(); }
    },
    async saveProfile() {
      this.profileSaving = true;
      try { await api({ function: 'patientPortalUpdateContact', ...this.profile }); alert('Saved.'); }
      catch (e) { /* surfaced via API toast elsewhere */ } finally { this.profileSaving = false; }
    },
    async optOut(type) {
      if (!confirm('Stop receiving ' + type + ' notifications?')) return;
      await api({ function: 'patientPortalOptOut', contact_type: type });
      alert('You have been opted out.');
    },
    logout() {
      api({ function: 'patientPortalLogout' });
      localStorage.removeItem('patientPortalToken');
      localStorage.removeItem('patientPortalUser');
      this.$router.push('/portal/login');
    },

    formatDate(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ','T')); if (isNaN(dt)) return d; return dt.toLocaleDateString(); },
    formatDt(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ','T')); if (isNaN(dt)) return d; return dt.toLocaleString(); },
    formatMoney(v) { return Number(v||0).toLocaleString(undefined, { maximumFractionDigits: 2 }); },

    apptPill(s) { return ({ scheduled:'pt-pill-info', confirmed:'pt-pill-success', cancelled:'pt-pill-muted', completed:'pt-pill-muted', no_show:'pt-pill-danger' })[s] || ''; },
    rxPill(s)   { return ({ active:'pt-pill-info', dispensed:'pt-pill-success', completed:'pt-pill-muted' })[s] || ''; },
    immPill(s)  { return ({ given:'pt-pill-success', missed:'pt-pill-warn', refused:'pt-pill-danger' })[s] || 'pt-pill-muted'; },
    thPill(s)   { return ({ scheduled:'pt-pill-info', in_progress:'pt-pill-success', completed:'pt-pill-muted', no_show:'pt-pill-danger' })[s] || ''; },
    refPill(s)  { return ({ sent:'pt-pill-warn', accepted:'pt-pill-success', completed:'pt-pill-muted', rejected:'pt-pill-danger' })[s] || ''; },
    payPill(s)  { return ({ unpaid:'pt-pill-danger', partial:'pt-pill-warn', paid:'pt-pill-success' })[s] || ''; },
    flagPill(f) { return ({
      H:'pt-pill-warn', L:'pt-pill-warn', HH:'pt-pill-danger', LL:'pt-pill-danger', N:'pt-pill-success',
      high:'pt-pill-warn', low:'pt-pill-warn', critical_high:'pt-pill-danger', critical_low:'pt-pill-danger',
      abnormal:'pt-pill-warn', normal:'pt-pill-success'
    })[f] || ''; },
    visitPill(s) { return ({ open:'pt-pill-warn', completed:'pt-pill-success', cancelled:'pt-pill-muted' })[s] || 'pt-pill-info'; },
    consentPill(s) { return ({ pending:'pt-pill-warn', signed:'pt-pill-success', declined:'pt-pill-danger', expired:'pt-pill-muted' })[s] || ''; }
  }
};
</script>

<style scoped>
.pt-shell { min-height: 100vh; background: #f7fafc; }
.pt-topbar { background: #fff; box-shadow: 0 2px 4px rgba(0,0,0,.05); position: sticky; top: 0; z-index: 10; }
.pt-topbar-inner { max-width: 1100px; margin: 0 auto; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; }
.pt-brand-sm { font-weight: 700; color: #1a4480; font-size: 16px; }
.pt-user { color: #2d3748; font-size: 14px; display: flex; align-items: center; gap: 6px; }
.pt-icon-btn { background: none; border: none; cursor: pointer; color: #4a5568; font-size: 20px; }

.pt-tabbar { display: flex; gap: 0; border-top: 1px solid #edf2f7; max-width: 1100px; margin: 0 auto; padding: 0 12px; overflow-x: auto; }
.pt-tab { padding: 10px 14px; background: none; border: none; cursor: pointer; color: #718096; font-size: 13px; border-bottom: 2px solid transparent; white-space: nowrap; display: inline-flex; align-items: center; gap: 4px; }
.pt-tab.active { color: #2c5282; border-bottom-color: #2c5282; font-weight: 600; }
.pt-tab i { font-size: 16px; }

.pt-body { padding: 20px 0 60px; }
.pt-container { max-width: 1100px; margin: 0 auto; padding: 0 20px; }

.pt-h2 { font-size: 22px; font-weight: 700; color: #1a4480; margin: 0 0 4px; }
.pt-h3 { font-size: 15px; font-weight: 600; color: #2d3748; margin-bottom: 8px; }
.pt-muted { color: #718096; font-size: 14px; }
.pt-muted-sm { color: #a0aec0; font-size: 12px; }

.pt-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 16px 0; }
@media (max-width: 700px) { .pt-grid { grid-template-columns: 1fr; } }
.pt-card-sm { background: #fff; border-radius: 8px; padding: 16px; cursor: pointer; position: relative; border: 1px solid #e2e8f0; transition: .2s; }
.pt-card-sm:hover { border-color: #2c5282; box-shadow: 0 4px 12px rgba(44,82,130,.1); }
.pt-num { font-size: 28px; font-weight: 700; color: #1a4480; }
.pt-lbl { font-size: 12px; color: #718096; text-transform: uppercase; letter-spacing: .5px; }
.pt-icon { position: absolute; right: 12px; top: 12px; font-size: 24px; color: #cbd5e0; }

.pt-section { margin-top: 24px; }

.pt-row { background: #fff; border-radius: 6px; padding: 12px 14px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; border: 1px solid #edf2f7; }
.pt-row-card { padding: 16px; }

.pt-pill { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.pt-pill-success { background: #c6f6d5; color: #22543d; }
.pt-pill-info    { background: #bee3f8; color: #2a4365; }
.pt-pill-warn    { background: #feebc8; color: #7b341e; }
.pt-pill-danger  { background: #fed7d7; color: #742a2a; }
.pt-pill-muted   { background: #edf2f7; color: #4a5568; }

.pt-table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 6px; overflow: hidden; font-size: 13px; }
.pt-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.pt-table td { padding: 9px; border-bottom: 1px solid #edf2f7; }
.pt-table tr.pt-row-warn { background: #fffaf0; }

.pt-empty { text-align: center; color: #a0aec0; padding: 30px; }

.pt-rx-items { width: 100%; margin-top: 8px; }
.pt-rx-item { padding: 6px 0; border-bottom: 1px dashed #edf2f7; }
.pt-rx-item:last-child { border-bottom: 0; }

.pt-feedback { background: #ebf8ff; padding: 10px; border-radius: 6px; border-left: 3px solid #2b6cb0; font-size: 13px; }

.pt-form-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; max-width: 720px; }
.pt-input { width: 100%; padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 14px; }
.pt-input:disabled { background: #f7fafc; color: #718096; }
.pt-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 4px; text-transform: uppercase; letter-spacing: .4px; }

.pt-btn { padding: 8px 16px; border-radius: 6px; border: none; font-weight: 600; cursor: pointer; font-size: 13px; }
.pt-btn-primary { background: #2c5282; color: #fff; }
.pt-btn-link { background: transparent; color: #2c5282; }
.pt-btn-danger { background: #e53e3e; color: #fff; }
.pt-btn:disabled { opacity: .6; cursor: not-allowed; }

.pt-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; padding: 20px; z-index: 100; }
.pt-modal { background: #fff; border-radius: 10px; max-width: 480px; width: 100%; max-height: 90vh; display: flex; flex-direction: column; }
.pt-modal-head { padding: 16px 20px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.pt-modal-body { padding: 20px; overflow-y: auto; }
.pt-modal-foot { padding: 14px 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }

.pt-bad { color: #c53030; }
.pt-good { color: #276749; }

.pt-consent-text { background: #f7fafc; padding: 12px; border-radius: 6px; margin-top: 10px; font-size: 13px; white-space: pre-wrap; max-height: 240px; overflow-y: auto; width: 100%; }
/* Ask my record */
.pt-ask-suggestions { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 18px 20px; margin-bottom: 14px; }
.pt-ask-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.pt-ask-chip { background: #ebf8ff; color: #2c5282; border: 1px solid #bee3f8; padding: 8px 14px; border-radius: 20px; font-size: 13px; cursor: pointer; transition: .12s; display: inline-flex; align-items: center; gap: 6px; }
.pt-ask-chip:hover { background: #2c5282; color: #fff; border-color: #2c5282; }
.pt-ask-chip i { font-size: 14px; }

.pt-ask-chat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; margin-bottom: 12px; max-height: 60vh; overflow-y: auto; }
.pt-ask-list { display: flex; flex-direction: column; }
.pt-ask-input { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }

/* Mental Health Crisis */
.pt-crisis-fab { position: fixed; bottom: 24px; right: 24px; width: 64px; height: 64px; border-radius: 50%; border: none;
                 background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); color: #fff; box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
                 cursor: pointer; z-index: 90; display: flex; align-items: center; justify-content: center; transition: .2s; }
.pt-crisis-fab i { font-size: 30px; }
.pt-crisis-fab:hover { transform: scale(1.08); box-shadow: 0 8px 24px rgba(220, 38, 38, 0.55); }
.pt-crisis-fab-mh { background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%); box-shadow: 0 6px 20px rgba(124, 58, 237, 0.4); }
.pt-crisis-fab-mh:hover { box-shadow: 0 8px 24px rgba(124, 58, 237, 0.55); }
.pt-crisis-fab-high .pt-crisis-pulse { position: absolute; inset: -6px; border-radius: 50%; border: 3px solid #dc2626; animation: pt-pulse 1.5s infinite; }
@keyframes pt-pulse { 0% { opacity: 1; transform: scale(1); } 100% { opacity: 0; transform: scale(1.35); } }

.pt-crisis-bg { background: rgba(127, 29, 29, 0.55); }
.pt-crisis-modal { width: 620px; max-width: 96vw; max-height: 95vh; }
.pt-crisis-head { background: linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%); color: #fff; padding: 24px 28px; position: relative; }
.pt-crisis-head h2 { margin: 0 0 4px; font-size: 22px; display: flex; align-items: center; gap: 10px; }
.pt-crisis-head h2 i { font-size: 28px; }
.pt-crisis-head p { margin: 0; opacity: .9; font-size: 14px; }
.pt-crisis-close { position: absolute; top: 16px; right: 16px; color: #fff !important; }

.pt-crisis-section { padding: 16px 24px; border-bottom: 1px solid #e2e8f0; }
.pt-crisis-section:last-of-type { border-bottom: 0; }
.pt-crisis-section h3 { font-size: 14px; margin: 0 0 12px; color: #1a202c; font-weight: 700; }
.pt-crisis-danger { background: linear-gradient(135deg, #fff 0%, #fee2e2 100%); }
.pt-crisis-danger h3 { color: #991b1b; font-size: 15px; }

.pt-crisis-btn { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #fff; border: 2px solid #e2e8f0; border-radius: 10px; margin-bottom: 8px; text-decoration: none; color: #1a202c; transition: .15s; cursor: pointer; }
.pt-crisis-btn:hover { transform: translateX(2px); }
.pt-crisis-btn i { font-size: 24px; flex-shrink: 0; }
.pt-crisis-btn div strong { display: block; font-size: 14px; }
.pt-crisis-btn div span { font-size: 11px; color: #64748b; }
.pt-crisis-emergency { background: #fef2f2; border-color: #fca5a5; }
.pt-crisis-emergency:hover { background: #fee2e2; }
.pt-crisis-emergency i { color: #dc2626; }
.pt-crisis-hospital { background: #eff6ff; border-color: #93c5fd; }
.pt-crisis-hospital i { color: #2563eb; }
.pt-crisis-line { background: #f0fdf4; border-color: #86efac; }
.pt-crisis-line i { color: #16a34a; }

.pt-crisis-supporter { background: #fdf4ff; }
.pt-crisis-supporter h3 { color: #7c3aed; }

.pt-crisis-plan { background: #fefce8; }
.pt-crisis-plan h3 { color: #854d0e; }
.pt-crisis-plan-text { background: #fff; padding: 12px; border-radius: 6px; white-space: pre-wrap; font-size: 13px; line-height: 1.55; }

.pt-crisis-tips { padding-left: 20px; line-height: 1.7; font-size: 13px; color: #475569; }
.pt-crisis-tips li { padding: 4px 0; }

.pt-crisis-footer { padding: 20px 24px; background: #f7fafc; text-align: center; font-size: 13px; color: #475569; font-style: italic; }

/* Growth Charts */
.pt-growth-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 14px 0 18px; }
.pt-growth-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; border-left: 4px solid #94a3b8; }
.pt-growth-stat.pt-growth-normal { border-left-color: #16a34a; }
.pt-growth-stat.pt-growth-low    { border-left-color: #2563eb; background: #eff6ff; }
.pt-growth-stat.pt-growth-high   { border-left-color: #dc2626; background: #fef2f2; }

.pt-growth-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; margin-bottom: 14px; }
.pt-growth-card h3 { margin: 0 0 10px; }
.pt-growth-svg { background: #fafafa; border-radius: 6px; }
.pt-growth-axis text { font-size: 10px; fill: #64748b; }
.pt-growth-legend { font-size: 12px; color: #64748b; margin-top: 8px; display: flex; gap: 16px; flex-wrap: wrap; align-items: center; }
.pt-leg-dot { display: inline-block; width: 12px; height: 12px; border-radius: 50%; }
.pt-leg-line { display: inline-block; width: 24px; height: 0; border-bottom: 1.5px solid #94a3b8; }
.pt-leg-band { display: inline-block; width: 24px; height: 10px; background: #fef3c7; border-top: 1px dashed #fbbf24; border-bottom: 1px dashed #fbbf24; }

/* Pregnancy Timeline */
.pt-preg-hero { display: flex; align-items: center; gap: 24px; background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%); border-radius: 12px; padding: 24px; margin-bottom: 16px; }
.pt-preg-circle { width: 140px; height: 140px; flex-shrink: 0; }
.pt-preg-svg { width: 100%; height: 100%; }
.pt-preg-week-num { font-size: 28px; font-weight: 800; fill: #831843; }
.pt-preg-week-lbl { font-size: 12px; fill: #831843; text-transform: uppercase; letter-spacing: 1px; }
.pt-preg-info h2 { color: #831843; margin: 0 0 4px; }
.pt-preg-info p { margin: 4px 0; }
@media (max-width: 600px) { .pt-preg-hero { flex-direction: column; text-align: center; } }

.pt-preg-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 18px; margin-bottom: 12px; }
.pt-preg-card h3 { margin: 0 0 12px; }
.pt-preg-current { background: linear-gradient(135deg, #fff 0%, #fefce8 100%); border-left: 4px solid #ec4899; }
.pt-preg-size { font-size: 16px; padding: 10px 14px; background: #fce7f3; border-radius: 6px; margin-bottom: 12px; }
.pt-preg-grid > div { padding: 8px 0; border-bottom: 1px dashed #e2e8f0; font-size: 14px; line-height: 1.55; }
.pt-preg-grid > div:last-child { border-bottom: 0; }
.pt-preg-do { background: #ecfdf5; padding: 10px 12px !important; border-radius: 6px; border-left: 3px solid #16a34a; }

.pt-preg-risks { border-left: 4px solid #d97706; background: #fffbeb; }
.pt-preg-danger { border-left: 4px solid #dc2626; background: linear-gradient(135deg, #fff 0%, #fef2f2 100%); }
.pt-preg-danger h3 { color: #991b1b; }

.pt-preg-up { padding: 10px; background: #f7fafc; border-radius: 6px; margin-bottom: 8px; }
.pt-list { margin: 0; padding-left: 20px; line-height: 1.7; }
.pt-list li { font-size: 14px; }

/* Document Vault */
.pt-doc-stats { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin: 14px 0; }
@media (max-width: 700px) { .pt-doc-stats { grid-template-columns: repeat(3, 1fr); } }
.pt-doc-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; text-align: center; }
.pt-doc-stat strong { font-size: 22px; color: #1a4480; display: block; }

.pt-doc-filters { display: flex; gap: 6px; margin-bottom: 16px; flex-wrap: wrap; }
.pt-doc-pill { padding: 6px 14px; border-radius: 20px; border: 1px solid #cbd5e0; background: #fff; font-size: 12px; font-weight: 500; cursor: pointer; color: #4a5568; }
.pt-doc-pill:hover { background: #f7fafc; }
.pt-doc-pill.active { background: #2c5282; color: #fff; border-color: #2c5282; }
.pt-doc-pill i { font-size: 14px; }

.pt-doc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
.pt-doc-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; display: flex; gap: 12px; align-items: flex-start; transition: .15s; }
.pt-doc-card:hover { border-color: #2c5282; box-shadow: 0 2px 8px rgba(0,0,0,.06); }
.pt-doc-icon { width: 56px; height: 56px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pt-doc-icon i { font-size: 28px; color: #fff; }
.pt-doc-img-color   { background: #2563eb; }
.pt-doc-pdf-color   { background: #dc2626; }
.pt-doc-doc-color   { background: #1a4480; }
.pt-doc-other-color { background: #64748b; }
.pt-doc-meta { flex: 1; min-width: 0; }
.pt-doc-title { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 14px; }
.pt-doc-actions { display: flex; flex-direction: column; gap: 2px; }

.pt-doc-viewer { width: 95vw; max-width: 1000px; height: 90vh; }
.pt-doc-view-body { padding: 0 !important; flex: 1; display: flex; flex-direction: column; align-items: stretch; overflow: hidden; }
.pt-doc-img { max-width: 100%; max-height: 100%; object-fit: contain; margin: auto; display: block; padding: 12px; }
.pt-doc-iframe { width: 100%; height: 100%; border: 0; flex: 1; }
.pt-doc-notes { padding: 12px 18px; border-top: 1px solid #e2e8f0; background: #f7fafc; font-size: 13px; }

/* Wearables */
.pt-wear-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 14px 0; }
@media (max-width: 700px) { .pt-wear-stats { grid-template-columns: repeat(2, 1fr); } }
.pt-wear-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.pt-stat-num { font-size: 22px; font-weight: 700; color: #1a4480; }
.pt-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .4px; margin-top: 2px; }

.pt-wear-device { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; margin-bottom: 10px; display: flex; align-items: center; gap: 14px; }
.pt-wear-icon { font-size: 36px; color: #2c5282; flex-shrink: 0; }

.pt-wear-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
@media (max-width: 700px) { .pt-wear-grid { grid-template-columns: repeat(2, 1fr); } }
.pt-wear-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; cursor: pointer; transition: .15s; text-align: left; }
.pt-wear-card:hover { border-color: #2c5282; background: #f7fafc; box-shadow: 0 2px 8px rgba(0,0,0,.06); transform: translateY(-1px); }
.pt-wear-card i { font-size: 28px; color: #2c5282; display: block; margin-bottom: 6px; }
.pt-wear-card strong { display: block; color: #1a202c; font-size: 14px; }
.pt-wear-demo { background: linear-gradient(135deg, #f0fdf4 0%, #fff 100%); border-color: #86efac; }
.pt-wear-demo i { color: #16a34a; }

.pt-wear-steps { font-size: 14px; line-height: 1.7; padding-left: 20px; }
.pt-wear-steps li { padding: 2px 0; }
.pt-wear-steps code { background: #f1f5f9; padding: 1px 6px; border-radius: 3px; }

/* My Meds */
.pt-adh-summary { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 20px; }
.pt-adh-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 18px; text-align: center; }
.pt-adh-pct { font-size: 36px; font-weight: 800; color: #1a4480; }
.pt-adh-pct span { font-size: 18px; }
.pt-adh-pct.pt-adh-good { color: #16a34a; }
.pt-adh-pct.pt-adh-fair { color: #d97706; }
.pt-adh-pct.pt-adh-poor { color: #dc2626; }
.pt-adh-streak { font-size: 32px; font-weight: 700; color: #ea580c; }
.pt-adh-streak i { font-size: 28px; }

.pt-med-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 12px; padding: 16px; }
.pt-med-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
.pt-med-adh { text-align: center; }
.pt-med-adh strong { font-size: 22px; }
.pt-med-adh.pt-adh-good strong { color: #16a34a; }
.pt-med-adh.pt-adh-fair strong { color: #d97706; }
.pt-med-adh.pt-adh-poor strong { color: #dc2626; }
.pt-med-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.pt-med-times { margin-top: 10px; font-size: 12px; color: #475569; }
.pt-med-times code { background: #ebf8ff; color: #2c5282; padding: 2px 6px; border-radius: 4px; margin: 0 2px; font-weight: 600; }

.pt-rem-times { display: flex; flex-direction: column; gap: 6px; }
.pt-rem-time-row { display: flex; gap: 6px; align-items: center; }

/* Telehealth request */
.pt-btn-large { padding: 12px 22px; font-size: 14px; }
.pt-th-disclaimer { background: #fffbeb; border: 1px solid #fbbf24; border-radius: 6px; padding: 10px 12px; font-size: 12px; color: #78350f; display: flex; gap: 8px; align-items: flex-start; }
.pt-th-disclaimer i { color: #d97706; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
.pt-th-when { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-top: 4px; }
.pt-th-when label { display: flex; flex-direction: column; padding: 12px; border: 2px solid #e2e8f0; border-radius: 8px; cursor: pointer; transition: .12s; text-align: center; gap: 4px; }
.pt-th-when label:hover { border-color: #2c5282; }
.pt-th-when label.active { border-color: #2c5282; background: #ebf8ff; }
.pt-th-when input[type=radio] { display: none; }
.pt-th-when strong { font-size: 13px; color: #1a202c; }
.pt-th-when span { font-size: 11px; color: #64748b; }

/* Emergency QR */
.pt-em-grid { display: grid; grid-template-columns: 320px 1fr; gap: 24px; align-items: flex-start; }
@media (max-width: 700px) { .pt-em-grid { grid-template-columns: 1fr; } }
.pt-em-card { background: linear-gradient(135deg, #fee2e2 0%, #fff 100%); border: 2px solid #dc2626; border-radius: 12px; padding: 24px; text-align: center; }
.pt-em-qr { width: 100%; max-width: 280px; height: auto; background: #fff; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; }
.pt-em-shortcode { margin-top: 12px; }
.pt-em-shortcode code { background: #fff; padding: 4px 12px; border-radius: 6px; font-size: 16px; letter-spacing: 2px; font-weight: 700; color: #dc2626; border: 1px dashed #dc2626; }
.pt-em-stats { margin-top: 14px; padding-top: 14px; border-top: 1px dashed #fca5a5; font-size: 14px; }
.pt-em-actions { margin-top: 14px; display: flex; flex-direction: column; gap: 6px; }
.pt-em-info ol, .pt-em-info ul { padding-left: 20px; line-height: 1.7; font-size: 14px; }
.pt-em-shows { list-style: none; padding-left: 0; }
.pt-em-shows li { padding: 4px 0; }
.pt-pad { padding: 40px; }

/* Profile picker */
.pt-profile-picker { position: relative; cursor: pointer; }
.pt-profile-current { display: flex; align-items: center; gap: 10px; padding: 6px 12px; border-radius: 24px; background: #f7fafc; border: 1px solid #e2e8f0; transition: .15s; }
.pt-profile-current:hover { background: #ebf8ff; border-color: #2c5282; }
.pt-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 12px; flex-shrink: 0; }
.pt-avatar-blue   { background: #2c5282; }
.pt-avatar-purple { background: #7c3aed; }
.pt-avatar-green  { background: #16a34a; }
.pt-avatar-orange { background: #ea580c; }
.pt-avatar-red    { background: #dc2626; }
.pt-avatar-teal   { background: #0d9488; }
.pt-profile-info { font-size: 12px; line-height: 1.2; }
.pt-profile-name { font-weight: 600; color: #1a202c; }
.pt-profile-rel { color: #64748b; font-size: 11px; }
.pt-profile-menu { position: absolute; top: 100%; right: 0; margin-top: 6px; background: #fff; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.15); min-width: 280px; z-index: 50; overflow: hidden; }
.pt-profile-row { display: flex; align-items: center; gap: 12px; padding: 10px 16px; cursor: pointer; transition: .12s; }
.pt-profile-row:hover { background: #f7fafc; }
.pt-profile-row.pt-profile-active { background: #ebf8ff; }
.pt-profile-check { color: #16a34a; margin-left: auto; font-size: 18px; }
.pt-profile-divider { height: 1px; background: #e2e8f0; }
.pt-profile-add { color: #2c5282; font-weight: 500; }
.pt-profile-add i { font-size: 20px; }

.pt-proxy-banner { background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%); border-bottom: 1px solid #fbbf24; padding: 10px 20px; font-size: 13px; color: #78350f; display: flex; align-items: center; gap: 10px; max-width: 1100px; margin: 0 auto; }
.pt-proxy-banner i { font-size: 18px; color: #d97706; }
.pt-proxy-banner button { margin-left: auto; }

.pt-fam-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; }
.pt-fam-tab { padding: 14px 8px; border: 2px solid #e2e8f0; background: #fff; border-radius: 8px; cursor: pointer; text-align: center; transition: .15s; }
.pt-fam-tab.active { border-color: #2c5282; background: #ebf8ff; color: #2c5282; }
.pt-fam-tab i { display: block; font-size: 24px; margin-bottom: 4px; }
.pt-fam-tab { font-size: 12px; font-weight: 600; }

.pt-perms { display: flex; flex-direction: column; gap: 6px; max-height: 220px; overflow-y: auto; padding-right: 4px; }
.pt-perm-row { display: flex; gap: 8px; align-items: flex-start; padding: 6px 8px; border-radius: 4px; font-size: 12px; cursor: pointer; }
.pt-perm-row:hover { background: #f7fafc; }
.pt-perm-row input[type=checkbox] { margin-top: 2px; }

/* Health Diary */
.pt-diary-quick { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 18px; }
@media (max-width: 700px) { .pt-diary-quick { grid-template-columns: repeat(2, 1fr); } }
.pt-diary-btn { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 18px 10px; cursor: pointer; text-align: center; transition: .15s; }
.pt-diary-btn:hover { border-color: #2c5282; background: #f7fafc; box-shadow: 0 2px 8px rgba(0,0,0,.06); }
.pt-diary-btn i { font-size: 28px; display: block; margin-bottom: 6px; }
.pt-diary-btn div { font-size: 12px; font-weight: 600; color: #334155; }
.pt-diary-bp i      { color: #e53e3e; }
.pt-diary-glucose i { color: #dd6b20; }
.pt-diary-weight i  { color: #2563eb; }
.pt-diary-mood i    { color: #7c3aed; }
.pt-diary-other i   { color: #64748b; }

.pt-trend-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
@media (max-width: 800px) { .pt-trend-grid { grid-template-columns: repeat(2, 1fr); } }
.pt-trend-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #2c5282; border-radius: 8px; padding: 14px; }
.pt-trend-card.pt-trend-good   { border-left-color: #16a34a; }
.pt-trend-card.pt-trend-warn   { border-left-color: #dd6b20; }
.pt-trend-card.pt-trend-danger { border-left-color: #dc2626; }
.pt-trend-head { font-size: 12px; color: #475569; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; display: flex; align-items: center; gap: 4px; }
.pt-trend-head i { font-size: 16px; }
.pt-trend-value { font-size: 24px; color: #1a202c; margin: 6px 0; line-height: 1; }
.pt-trend-value strong { font-size: 28px; }
.pt-trend-unit { font-size: 12px; color: #94a3b8; margin-left: 4px; }
.pt-trend-sub { font-size: 12px; color: #64748b; margin-left: 8px; }
.pt-spark { display: block; margin-top: 8px; }

.pt-range { width: 100%; padding: 0; }
.pt-mood-display { text-align: center; font-size: 24px; padding: 8px; color: #7c3aed; font-weight: 700; }

/* Symptom checker */
.pt-sym-disclaimer { background: #fff5e1; border-left: 4px solid #d69e2e; padding: 14px 18px; border-radius: 6px; display: flex; gap: 12px; align-items: flex-start; }
.pt-sym-disclaimer i { color: #d69e2e; font-size: 24px; flex-shrink: 0; }
.pt-sym-history-row { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 16px; margin-bottom: 8px; cursor: pointer; display: flex; justify-content: space-between; align-items: center; }
.pt-sym-history-row:hover { border-color: #2c5282; background: #f7fafc; }

.pt-sym-chat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; display: flex; flex-direction: column; max-height: 80vh; }
.pt-typing { display: inline-flex; gap: 3px; }
.pt-typing span { width: 6px; height: 6px; background: #94a3b8; border-radius: 50%; animation: pt-bounce 1.4s infinite ease-in-out both; }
.pt-typing span:nth-child(1) { animation-delay: -0.32s; }
.pt-typing span:nth-child(2) { animation-delay: -0.16s; }
@keyframes pt-bounce { 0%, 80%, 100% { transform: scale(0.7); opacity: .5; } 40% { transform: scale(1); opacity: 1; } }

.pt-sym-result { background: #fff; border-radius: 12px; padding: 22px; margin: 20px auto; box-shadow: 0 4px 16px rgba(0,0,0,.08); border-left: 6px solid #2c5282; max-width: 90%; }
.pt-sym-emergency      { border-left-color: #dc2626; background: linear-gradient(135deg, #fff 0%, #fef2f2 100%); }
.pt-sym-urgent_today   { border-left-color: #ea580c; background: linear-gradient(135deg, #fff 0%, #fff7ed 100%); }
.pt-sym-within_24h     { border-left-color: #d97706; background: linear-gradient(135deg, #fff 0%, #fffbeb 100%); }
.pt-sym-within_week    { border-left-color: #2563eb; }
.pt-sym-self_care      { border-left-color: #16a34a; background: linear-gradient(135deg, #fff 0%, #f0fdf4 100%); }
.pt-sym-result-head { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; }
.pt-sym-result-head i { font-size: 36px; color: #2c5282; }
.pt-sym-emergency .pt-sym-result-head i { color: #dc2626; }
.pt-sym-urgent_today .pt-sym-result-head i, .pt-sym-within_24h .pt-sym-result-head i { color: #ea580c; }
.pt-sym-self_care .pt-sym-result-head i { color: #16a34a; }
.pt-sym-result-head h3 { font-size: 18px; }
.pt-sym-summary { font-size: 14px; color: #475569; line-height: 1.55; padding: 12px; background: #f8fafc; border-radius: 6px; margin-bottom: 14px; }
.pt-sym-block { margin-bottom: 14px; }
.pt-sym-block h5 { font-size: 12px; text-transform: uppercase; letter-spacing: .5px; color: #64748b; margin: 0 0 6px; font-weight: 700; }
.pt-sym-block ul { margin: 0; padding-left: 20px; font-size: 14px; }
.pt-sym-block li { padding: 2px 0; }
.pt-sym-redflags { background: #fef2f2; padding: 10px 14px; border-radius: 6px; }
.pt-sym-redflags h5 { color: #991b1b; }
.pt-sym-redflags i { color: #dc2626; }
.pt-sym-actions { display: flex; gap: 8px; flex-wrap: wrap; padding-top: 8px; border-top: 1px dashed #e2e8f0; }

.pt-btn-secondary { background: #fff; color: #2c5282; border: 1px solid #2c5282; }
.pt-btn-danger    { background: #dc2626; color: #fff; }

.pt-btn-sm { padding: 4px 10px; font-size: 11px; }
.pt-btn-mpesa { background: linear-gradient(135deg, #00b04f 0%, #007a3d 100%); color: #fff; }
.pt-btn-mpesa:hover { background: linear-gradient(135deg, #00c057 0%, #008942 100%); }
.pt-btn-mpesa:disabled { opacity: .55; }

.pt-mpesa-modal { width: 480px; max-width: 95vw; }
.pt-pay-summary { background: #f7fafc; padding: 14px; border-radius: 8px; }
.pt-pay-summary > div { padding: 4px 0; font-size: 14px; }
.pt-pay-balance { padding-top: 8px !important; border-top: 1px solid #e2e8f0; margin-top: 4px; font-size: 16px !important; }

.pt-pay-status { text-align: center; padding: 30px 20px; }
.pt-pay-status h4 { font-size: 18px; color: #1a4480; margin: 12px 0 6px; }
.pt-pay-status p { color: #64748b; margin: 4px 0; }
.pt-pay-phone i { font-size: 56px; color: #00b04f; }
.pt-pay-timer { margin-top: 12px; font-size: 24px; font-weight: 700; color: #1a4480; }
.pt-pay-success i { font-size: 64px; color: #00b04f; }
.pt-pay-failed  i { font-size: 64px; color: #e53e3e; }
.pt-pay-receipt { background: #f0fdf4; padding: 10px 14px; border-radius: 6px; margin-top: 12px; font-size: 14px; color: #166534; display: inline-block; border: 1px dashed #86efac; }

.pt-spinner { width: 48px; height: 48px; margin: 0 auto; border: 4px solid #e2e8f0; border-top-color: #00b04f; border-radius: 50%; animation: pt-spin 1s linear infinite; }
@keyframes pt-spin { to { transform: rotate(360deg); } }

.pt-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; background: #e53e3e; color: #fff; border-radius: 9px; font-size: 10px; font-weight: 700; padding: 0 6px; margin-left: 4px; }

.pt-msg-shell { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.pt-thread-row { display: grid; grid-template-columns: 180px 1fr auto; gap: 16px; padding: 14px 18px; border-bottom: 1px solid #edf2f7; cursor: pointer; align-items: center; transition: .15s; }
.pt-thread-row:last-child { border-bottom: 0; }
.pt-thread-row:hover { background: #f7fafc; }
.pt-thread-row.pt-thread-unread { background: #ebf8ff; font-weight: 500; }
.pt-thread-target { display: flex; align-items: center; gap: 6px; color: #2c5282; font-weight: 600; font-size: 13px; }
.pt-thread-target i { font-size: 18px; }
.pt-thread-subj { font-size: 14px; color: #1a202c; }
.pt-thread-end { display: flex; align-items: center; gap: 4px; }

.pt-thread-detail { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; display: flex; flex-direction: column; max-height: 70vh; }
.pt-thread-header { padding: 12px 16px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; gap: 12px; background: #f7fafc; }
.pt-thread-header > div:nth-child(2) { flex: 1; }
.pt-msg-list { flex: 1; overflow-y: auto; padding: 16px; min-height: 300px; max-height: 50vh; }
.pt-msg { max-width: 80%; padding: 10px 14px; border-radius: 14px; margin-bottom: 10px; font-size: 14px; line-height: 1.5; }
.pt-msg-meta { font-size: 11px; color: #a0aec0; margin-bottom: 4px; }
.pt-msg-body { white-space: pre-wrap; }
.pt-msg-patient { background: #2c5282; color: #fff; margin-left: auto; }
.pt-msg-patient .pt-msg-meta { color: rgba(255,255,255,0.7); }
.pt-msg-staff   { background: #f7fafc; border: 1px solid #e2e8f0; }
.pt-msg-system  { background: #fff5e1; border: 1px dashed #d69e2e; font-style: italic; color: #7b341e; max-width: 100%; text-align: center; }
.pt-msg-input { padding: 12px 16px; border-top: 1px solid #e2e8f0; background: #fff; }

.pt-results-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; margin-bottom: 14px; overflow: hidden; }
.pt-results-head { padding: 12px 14px; display: flex; justify-content: space-between; align-items: center; background: #f7fafc; border-bottom: 1px solid #e2e8f0; }
.pt-table.mb-0 { margin-bottom: 0; }
.pt-row-action { background: #fff5f5 !important; }
.pt-edu-body { font-size: 14px; line-height: 1.55; color: #2d3748; white-space: pre-wrap; }

/* ====== PMTCT cascade card (in pregnancy tab) ====== */
.pt-pmtct-card { background: linear-gradient(135deg, #faf5ff 0%, #fff 100%); border-left: 4px solid #7C3AED; }
.pt-pmtct-row { display: flex; flex-wrap: wrap; gap: 14px; padding: 4px 0; font-size: 13px; color: #2d3748; align-items: center; }
.pt-pmtct-status { background: #f7fafc; padding: 8px 12px; border-radius: 6px; margin-top: 8px; color: #4a5568; font-size: 13px; }
.pt-pmtct-status .mdi { color: #7C3AED; margin-right: 6px; }

/* ====== PNC schedule rows ====== */
.pt-pnc-row { display: flex; gap: 12px; padding: 10px 0; border-bottom: 1px solid #edf2f7; }
.pt-pnc-row:last-child { border-bottom: none; }
.pt-pnc-icon { font-size: 22px; width: 30px; flex-shrink: 0; }
.pt-pnc-icon .mdi { font-size: 24px; }
.pt-pnc-body { flex: 1; }
.pt-pnc-row-good { background: linear-gradient(90deg, #f0fff4 0%, transparent 60%); padding-left: 8px; border-radius: 4px; }
.pt-pnc-row-warn { background: linear-gradient(90deg, #fffaf0 0%, transparent 60%); padding-left: 8px; border-radius: 4px; }
.pt-pnc-row-bad  { background: linear-gradient(90deg, #fff5f5 0%, transparent 60%); padding-left: 8px; border-radius: 4px; }
.pt-good { color: #22543d; } .pt-warn { color: #b45309; } .pt-bad { color: #b91c1c; }
.pt-muted { color: #a0aec0; }

/* ====== HEI card (in growth tab) ====== */
.pt-hei-card { background: #fef2f2; border-left: 4px solid #DC2626; padding: 14px 16px; border-radius: 8px; }
.pt-hei-row { display: flex; flex-wrap: wrap; gap: 14px; padding: 4px 0; font-size: 13px; color: #2d3748; }
.pt-h4 { font-size: 13px; font-weight: 700; margin: 8px 0 4px; color: #2d3748; }

/* ====== Family Planning ====== */
.pt-fp-context { background: #fff7ed; border-left: 4px solid #ea580c; padding: 12px 14px; border-radius: 4px; font-size: 13px; color: #7b341e; margin-bottom: 14px; }
.pt-fp-filter { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
.pt-fp-chip { background: #fff; border: 1px solid #e2e8f0; border-radius: 18px; padding: 8px 14px; font-size: 13px; color: #4a5568; cursor: pointer; }
.pt-fp-chip:hover { border-color: #2b6cb0; color: #2b6cb0; }
.pt-fp-chip.active { background: #2b6cb0; color: #fff; border-color: #2b6cb0; }
.pt-fp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 14px; }
.pt-fp-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; transition: transform .12s, box-shadow .12s; }
.pt-fp-card:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,.08); }
.pt-fp-recommended { border: 2px solid #22c55e; background: #f0fff4; }
.pt-fp-blocked { opacity: 0.5; border-color: #fc8181; }
.pt-fp-head { display: flex; gap: 12px; margin-bottom: 12px; align-items: flex-start; }
.pt-fp-head > .mdi { font-size: 32px; color: #2b6cb0; flex-shrink: 0; }
.pt-fp-name { margin: 0 0 4px; font-size: 15px; font-weight: 600; color: #2d3748; }
.pt-fp-stats { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; padding: 10px; background: #f7fafc; border-radius: 6px; margin: 10px 0; font-size: 11px; color: #4a5568; text-align: center; }
.pt-fp-stats strong { color: #2d3748; }
.pt-fp-text { font-size: 12px; color: #4a5568; margin: 4px 0; line-height: 1.5; }
.pt-fp-best { font-size: 12px; color: #2b6cb0; margin-top: 8px; padding-top: 8px; border-top: 1px dashed #e2e8f0; }

.pt-pill-success { background: #c6f6d5; color: #22543d; }
.pt-pill-bad     { background: #fed7d7; color: #742a2a; }
.pt-pill-warn    { background: #feebc8; color: #7b341e; }

/* ====== Wellbeing (EPDS + IPV self-screen) ====== */
.pt-wb-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; margin-bottom: 14px; }
.pt-wb-head { display: flex; gap: 12px; align-items: center; }
.pt-wb-head > .mdi { font-size: 32px; color: #2b6cb0; flex-shrink: 0; }
.pt-wb-head > div:nth-child(2) { flex: 1; }
.pt-wb-q { margin: 16px 0; padding: 12px; background: #f7fafc; border-radius: 8px; }
.pt-wb-options { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
.pt-wb-opt { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 6px; cursor: pointer; font-size: 14px; transition: background .12s; }
.pt-wb-opt:hover { background: #edf2f7; }
.pt-wb-opt.selected { background: #bee3f8; color: #2a4365; font-weight: 600; }
.pt-wb-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 14px; }
.pt-wb-scale { display: flex; align-items: center; gap: 10px; margin: 8px 0 14px; }
.pt-wb-scale input { flex: 1; }
.pt-wb-check { display: flex; align-items: center; gap: 8px; padding: 8px 0; font-size: 14px; }
.pt-wb-result { padding: 16px; border-radius: 8px; margin-top: 12px; }
.pt-wb-result-low { background: #f0fff4; border-left: 4px solid #22c55e; }
.pt-wb-result-possible_depression { background: #fffbeb; border-left: 4px solid #d97706; }
.pt-wb-result-likely_depression { background: #fff7ed; border-left: 4px solid #ea580c; }
.pt-wb-result-severe_or_self_harm { background: #fef2f2; border-left: 4px solid #b91c1c; }
.pt-wb-result-immediate_danger { background: #fef2f2; border-left: 4px solid #b91c1c; }
.pt-wb-result-severe { background: #fef2f2; border-left: 4px solid #b91c1c; }
.pt-wb-result-no_disclosure { background: #f7fafc; border-left: 4px solid #94a3b8; }
.pt-wb-score { font-size: 18px; margin-bottom: 6px; }
.pt-wb-history { margin-top: 14px; padding-top: 14px; border-top: 1px solid #edf2f7; }
.pt-wb-history-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; border-bottom: 1px solid #edf2f7; }
.pt-wb-row-low { color: #22543d; }
.pt-wb-row-severe_or_self_harm { color: #742a2a; }
.pt-wb-resources { margin-top: 12px; }
.pt-wb-resource { display: flex; gap: 14px; padding: 8px 0; border-bottom: 1px solid #edf2f7; align-items: center; }
.pt-wb-resource a { color: #2b6cb0; font-weight: 600; text-decoration: none; min-width: 110px; }
.pt-wb-safety { background: #fef2f2; padding: 10px 14px; border-radius: 6px; margin-top: 14px; font-size: 13px; color: #7f1d1d; }
.pt-wb-ipv { border-left: 4px solid #DC2626; }

/* ====== Quick Exit floating button ====== */
.pt-quickexit {
  position: fixed; bottom: 18px; right: 18px;
  background: #dc2626; color: #fff;
  border: none; border-radius: 28px;
  padding: 12px 18px; font-size: 14px; font-weight: 600;
  display: inline-flex; align-items: center; gap: 6px;
  box-shadow: 0 6px 18px rgba(220, 38, 38, 0.3);
  cursor: pointer; z-index: 999;
}
.pt-quickexit:hover { background: #b91c1c; transform: translateY(-2px); }
.pt-quickexit .mdi { font-size: 20px; }

/* ====== MCH Booklet ====== */
.pt-booklet { background: #fff; }
.pt-booklet-controls { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; flex-wrap: wrap; gap: 12px; }
.pt-booklet-page { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 24px; }
.pt-booklet-header { display: flex; justify-content: space-between; gap: 16px; padding-bottom: 14px; border-bottom: 2px solid #2b6cb0; margin-bottom: 18px; }
.pt-booklet-title { margin: 0; font-size: 20px; color: #2b6cb0; }
.pt-booklet-id { text-align: right; font-size: 13px; color: #2d3748; }
.pt-booklet-h3 { font-size: 14px; font-weight: 700; color: #2d3748; margin: 20px 0 10px; padding-bottom: 4px; border-bottom: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: 0.4px; }
.pt-booklet-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
.pt-booklet-table th { background: #f7fafc; padding: 6px 8px; border-bottom: 1px solid #cbd5e0; text-align: left; font-weight: 600; }
.pt-booklet-table td { padding: 5px 8px; border-bottom: 1px solid #edf2f7; }
.pt-booklet-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px; font-size: 13px; }
.pt-booklet-footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #a0aec0; text-align: center; }

/* ====== Adolescent strip ====== */
.pt-teen-strip { background: linear-gradient(135deg, #fef3c7 0%, #fff 100%); padding: 14px 16px; border-radius: 10px; margin-bottom: 18px; }
.pt-teen-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; margin-top: 10px; }
.pt-teen-card { background: #fff; border-radius: 8px; border-left: 4px solid #2b6cb0; padding: 12px; display: flex; gap: 10px; }
.pt-teen-card > .mdi { font-size: 24px; flex-shrink: 0; }
.pt-teen-card strong { font-size: 13px; color: #2d3748; }
.pt-teen-card p { margin: 4px 0 0; font-size: 12px; line-height: 1.5; }

/* ====== Print mode (for MCH booklet) ====== */
@media print {
  .pt-tab-row, .pt-quickexit, .pt-booklet-controls { display: none !important; }
  .pt-booklet-page { border: none; padding: 0; }
  body, .pt-page, .pt-body, .pt-container { background: #fff !important; }
}
</style>
